import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, ReferenceArea } from 'recharts'
import { Loader, X, ChevronDown } from 'lucide-react'
import { projectsApi } from '../../lib/projectsApi'
import { kbApi } from '../../lib/kbApi'
import { useDashboardContext } from '../../context/DashboardContext'
import { ChartHoverTooltip } from './ChartHoverTooltip'
import useAuthStore from '../../store/auth-store'
import {
  resolveIanaTz, wallClockPartsInTz, parseWallClockInTzToMs,
  monthlyTicksFrom, dayMonthTickLabels, makeTimeAxisFormatter,
} from '../../lib/tz'
import { DataTableModal, StatisticsModal } from './LineChartDataModals'
import DateTimePicker from '../common/DateTimePicker'
import WindRoseChart from './WindRoseChart'
import { EventMonitorPanel } from '../EventMonitorPanel'
import { alarmApi } from '../../lib/alarmApi'
import { GalleryEditorModal, SupportProjectEditorModal } from './GalleryAndSupportEditors'
import { ParamValueCard } from './AllDataView'
import { cacheGet as _wcGet, cacheSet as _wcSet, onPrimed as _wcOnPrimed } from '../../lib/widgetConfigStore'
import { sendMessageToAdmin, MAX_MESSAGE_CHARS } from '../../lib/messageAdminApi'
// Project Map renderer (Leaflet + free tile layers) and its Edit popup —
// powers the GPS widget on the Project Map dashboard.
import ProjectMapView from './map/ProjectMapView'

export const COLORS = ['#358ECD', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

// Wind Rose speed band definitions (label + default colour). Default
// palette matches the XD/PSD swatches from the client: light-blue → indigo
// → green → orange → slate → magenta → teal.
export const WIND_SPEED_BANDS = [
  { label: '< 0.5 m/s',  key: 'band0', defaultColor: '#7DD3F0' },
  { label: '0.5–2 m/s',  key: 'band1', defaultColor: '#5B5DD8' },
  { label: '2–4 m/s',    key: 'band2', defaultColor: '#56B565' },
  { label: '4–6 m/s',    key: 'band3', defaultColor: '#F37D5C' },
  { label: '6–8 m/s',    key: 'band4', defaultColor: '#7C8A9C' },
  { label: '8–10 m/s',   key: 'band5', defaultColor: '#D67BDB' },
  { label: '> 10 m/s',   key: 'band6', defaultColor: '#5EEAD4' },
]

// ─── Wind Rose: known Parameterlib IDs for wind speed / direction ─────────────
// Source: client-supplied list. Paired by index — WIND_PAIRS[i] is the
// (speed, direction) PID tuple for variant i, in client-priority order.
//
//   Wind Speed/Direction:                47/48
//   Avg Wind Sp/Dir:                     49/50
//   Min Wind Sp/Dir:                     51/52
//   Max Wind Sp/Dir:                     53/54
//   Wind Gust/Dir:                      195/196
//   Relative Wind Speed/Dir:           1586/1587
//   Corrected Wind Speed/Dir:          1588/1589
//   WMO Avg Wind Speed/Dir:            1590/1591
//   WMO Avg Corrected Wind Speed/Dir:  1592/1593
//   WMO Relative Wind Gust/Dir:        1594/1616
//   Vct Wind Speed/Dir:                1664/1665
//   Std Wind Speed/Dir:                1667/1668
//
export const WIND_PAIRS = [
  [47, 48], [49, 50], [51, 52], [53, 54], [195, 196],
  [1586, 1587], [1588, 1589], [1590, 1591], [1592, 1593], [1594, 1616],
  [1664, 1665], [1667, 1668],
]
export const WIND_SPEED_PIDS = new Set(WIND_PAIRS.map(([s]) => s))
export const WIND_DIR_PIDS   = new Set(WIND_PAIRS.map(([, d]) => d))

// Flatten every parameter on a device (across all its sensors) into a
// single list. fetchProjectSites returns devices as `{sensors:[{parameters:[]}, ...]}`
// — the earlier Wind Rose dropdowns read `dev.parameters` which is always
// undefined and silently shows an empty list.
function deviceFlatParams(device) {
  if (!device) return []
  if (Array.isArray(device.parameters) && device.parameters.length) return device.parameters
  const out = []
  ;(device.sensors || []).forEach((sensor) => {
    ;(sensor.parameters || []).forEach((p) => {
      out.push({ ...p, sensorName: sensor.name, sensorId: sensor.id })
    })
  })
  return out
}

// Given a project's sites tree, find the first (in site → device order)
// device that has BOTH a wind-speed param and a wind-direction param
// based on the PID lists above. Returns `{ deviceId, speedParamId,
// directionParamId }` or null when no qualifying device exists.
function detectWindDefaults(sites) {
  if (!Array.isArray(sites)) return null
  for (const site of sites) {
    for (const dev of site.devices || []) {
      const params = deviceFlatParams(dev)
      // Walk WIND_PAIRS in their preferred order so "Wind Speed/Direction
      // (47/48)" beats "Avg Wind Sp/Dir (49/50)" when a device exposes
      // both pairs.
      for (const [speedPid, dirPid] of WIND_PAIRS) {
        const speed = params.find((p) => p.parameter_id === speedPid)
        const dir   = params.find((p) => p.parameter_id === dirPid)
        if (speed && dir) {
          return { deviceId: dev.id, speedParamId: speed.id, directionParamId: dir.id }
        }
      }
    }
  }
  return null
}

// ─── localStorage helpers ──────────────────────────────────────────────────────
const lsGet = (key, fallback = null) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback } catch { return fallback }
}
const lsSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); return true } catch { return false }
}

// Build a project-scoped widget-settings key. Configurable widgets (Gallery,
// Support Our Project, Wind Rose, Line Chart, Project Photo) previously
// keyed their localStorage by `widget.id` ONLY — but template widget ids
// are template-wide, so two projects using the same Overview template
// would collide on the same key and one project's settings leaked into
// every other project. Scope by projectId to fix that.
//
// An earlier draft of this fix transparently copied legacy un-scoped
// values into the new scoped key on first read. That re-introduced the
// leak — whichever project loaded first cloned the leaked value into
// its own scope. Drop legacy keys outright instead, see the IIFE below.
// Affected users reconfigure once (typically just one project per widget).
const scopedLsKey = (prefix, projectId, widgetId) =>
  `${prefix}_p${projectId || 'noproject'}_w${widgetId}`

// Reads consult the shared widget-config cache first (populated on
// dashboard mount by primeProject() — see widgetConfigStore.js) so
// every viewer, including anonymous public-portal visitors, sees the
// same picks the owner made. Falls back to localStorage for legacy /
// not-yet-synced values, then to the caller's default.
const lsGetScoped = (prefix, projectId, widgetId, fallback = null) => {
  const shared = _wcGet(prefix, projectId, widgetId)
  if (shared !== undefined) return shared
  return lsGet(scopedLsKey(prefix, projectId, widgetId), fallback)
}
// Writes update: (1) the in-memory shared cache, (2) localStorage
// (so returning viewers on the same browser see the change even
// before the fetch completes), and (3) fire a background PUT to the
// backend for cross-viewer persistence. Anonymous callers' PUTs are
// rejected server-side by design.
const lsSetScoped = (prefix, projectId, widgetId, value) => {
  lsSet(scopedLsKey(prefix, projectId, widgetId), value)
  _wcSet(prefix, projectId, widgetId, value)
  return true
}
// Widgets that call `lsGetScoped(...)` inside a `useState` initializer
// fire before the shared cache is primed. Hook this to force a
// re-read after prime completes so their initial paint matches the
// owner's saved value.
export function useWidgetConfigPrime(projectId) {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const off = _wcOnPrimed((pid) => {
      if (String(pid) === String(projectId)) setTick((t) => t + 1)
    })
    return off
  }, [projectId])
  return tick
}

// One-time module-level cleanup: any legacy un-scoped widget-settings
// key (`wq_gallery_<id>`, `wq_support_<id>`, …) is removed so the cross-
// project leak can't survive across reloads. Legacy keys are pre-bug —
// there's no way to know which project originally wrote them, so we
// drop them rather than migrate. The regex matches `<prefix>_<digits>`
// only; the new project-scoped shape contains `_p<id>_w<id>` and is
// preserved.
;(function purgeLegacyWidgetSettings() {
  try {
    const legacyShape = /^(wq_gallery|wq_support|wq_windrose|wq_graph|wq_photo)_\d+$/
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && legacyShape.test(k)) {
        try { localStorage.removeItem(k) } catch (_) { /* ignore */ }
      }
    }
  } catch (_) { /* localStorage may be disabled */ }
})()

// Shared edit/view icon used in XD headers
const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
  </svg>
)
const ExternalLinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
  </svg>
)

// XD-matching card: full-height, header + content
// ─── ModalPortal ─────────────────────────────────────────────────────────────
// Renders a modal into <body> instead of where it sits in the component tree.
//
// Widget editors live inside react-grid-layout cells, and `.react-grid-item`
// carries `will-change: transform` (plus a real transform while dragging).
// Either one makes that cell the CONTAINING BLOCK for `position: fixed`
// descendants — so `fixed inset-0` stopped meaning "the viewport" and started
// meaning "this widget's box". The editor then rendered inside the tile:
// off-centre, no full-screen backdrop, and clipped by the tile's bounds.
//
// Portalling to <body> puts the modal outside every transformed ancestor, so
// `fixed` refers to the viewport again. This cannot be fixed with CSS from
// inside the grid item — the containing block is decided by the ancestor.
function ModalPortal({ children }) {
  if (typeof document === 'undefined') return children   // SSR / test safety
  return createPortal(children, document.body)
}

function XDCard({ title, subtitle, headerRight, children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden ${className}`}>
      {/* min-h keeps every widget header the same height as the tallest one
          (Project Photo, whose "View All" button makes its header taller) so
          headers line up across the dashboard regardless of header controls. */}
      <div className="mob-lc flex border-b border-gray-100 items-center justify-between px-4 py-2 shrink-0 min-h-[52px]">
        <div>
          <h3 className="common-size18 font-bold text-[#4D4D4D] text-base">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{subtitle}</p>}
        </div>
        {headerRight}
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  )
}

// Legacy card (kept for non-XD widgets)
function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-100 h-full ${className}`}>
      {children}
    </div>
  )
}

/**
 * Summary Card Widget - displays a single metric
 */
export function SummaryCard({ widget, data }) {
  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{widget.title}</h3>
      <div className="text-3xl font-bold text-blue-600">{data?.value || '—'}</div>
      {data?.change && (
        <p className="text-xs text-gray-600 mt-2">
          {data.change > 0 ? '↑' : '↓'} {Math.abs(data.change)}% from last period
        </p>
      )}
    </Card>
  )
}

// ─── Point shape dot renderers ────────────────────────────────────────────────
export const POINT_SHAPES = ['Circle', 'Square', 'Diamond', 'Triangle', 'None']

// Small colour swatch drawn in the selected point shape (Graph Editor).
function ShapeSwatch({ shape, color }) {
  const c = color || '#358ECD'
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      {shape === 'Square' && <rect x="2" y="2" width="12" height="12" fill={c} />}
      {shape === 'Diamond' && <polygon points="8,1 15,8 8,15 1,8" fill={c} />}
      {shape === 'Triangle' && <polygon points="8,1 15,14 1,14" fill={c} />}
      {shape === 'None' && <circle cx="8" cy="8" r="6" fill="none" stroke={c} strokeWidth="2" />}
      {(!shape || shape === 'Circle') && <circle cx="8" cy="8" r="6" fill={c} />}
    </svg>
  )
}

// Named dot components — must be defined at module scope so React.isValidElement()
// returns true when passed as the `dot` prop to Recharts <Line>.
// Recharts calls React.cloneElement(element, { cx, cy, fill, ... }) at each point.
// A null guard handles Recharts passing null cy for missing/filtered data points.
function SquareDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null
  const s = 6
  return <rect x={cx - s / 2} y={cy - s / 2} width={s} height={s} fill={fill} />
}
function DiamondDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null
  const r = 5
  return <polygon points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} fill={fill} />
}
function TriangleDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null
  const r = 5
  return <polygon points={`${cx},${cy - r} ${cx + r},${cy + r} ${cx - r},${cy + r}`} fill={fill} />
}

function makeDot(shape, color) {
  if (shape === 'None')     return false
  if (shape === 'Square')   return <SquareDot   fill={color} />
  if (shape === 'Diamond')  return <DiamondDot  fill={color} />
  if (shape === 'Triangle') return <TriangleDot fill={color} />
  // default Circle — plain object; Recharts renders via its built-in <Dot>
  return { r: 3, fill: color, strokeWidth: 0 }
}

// ─── Graph Editor Modal ────────────────────────────────────────────────────────
export function GraphEditorModal({ open, onClose, onSave, selectedParameters, seriesMetadata, settings, widget }) {
  const [draft, setDraft] = useState(() => ({ ...settings }))
  useEffect(() => { if (open) setDraft({ ...settings }) }, [open, settings])

  if (!open) return null

  const updateSeries = (paramId, field, value) => {
    setDraft(prev => ({
      ...prev,
      series: {
        ...prev.series,
        [paramId]: { ...(prev.series?.[paramId] || {}), [field]: value },
      },
    }))
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">Line Chart Editor</h2>
          
        </div>

        {/* Modal body — one block per parameter: colour, point shape + its own
            vertical axis (Min/Max/Steps). No shared sections / "set as default". */}
        <div className="overflow-y-auto flex-1 px-8 py-5">
          {selectedParameters.length === 0 ? (
            <p className="text-xs text-gray-400">Select parameters from the left panel first.</p>
          ) : selectedParameters.map((param, idx) => {
            const meta = seriesMetadata[param.id] || {}
            const sLabel = [meta.siteName, meta.name || param.name].filter(Boolean).join(' | ')
            const s = draft.series?.[param.id] || {}
            const lineColor = s.color || COLORS[idx % COLORS.length]
            const pointShape = s.pointShape || 'Circle'
            const numOrUndef = (v) => (v === '' ? undefined : Number(v))
            return (
              <div key={param.id} className="pb-5 border-gray-100 last:border-0 border-b mb-4 last:mb-0 last:pb-0">
                <p className="common-size15 text-sm font-bold text-[#4D4D4D] mb-3">{sLabel}</p>
                <div className="flex items-center gap-6 flex-wrap mb-3">
                  {/* Line Color — swatch drawn in the selected point shape + picker */}
                  <div className="flex items-center gap-2">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Line Color</label>
                    <label className="flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50" title={lineColor}>
                      <ShapeSwatch shape={pointShape} color={lineColor} />
                      <ChevronDown className="w-3 h-3 text-gray-400" />
                      <input
                        type="color"
                        value={lineColor}
                        onChange={e => updateSeries(param.id, 'color', e.target.value)}
                        className="sr-only"
                      />
                    </label>
                  </div>
                  {/* Point Shape */}
                  <div className="flex items-center gap-2">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Point Shape</label>
                    <select
                      value={pointShape}
                      onChange={e => updateSeries(param.id, 'pointShape', e.target.value)}
                      className="common-size15 flex items-center gap-1 border bg-white border-gray-200 rounded-lg px-4 py-1.5 cursor-pointer hover:bg-gray-50"
                    >
                      {POINT_SHAPES.map(sh => <option key={sh}>{sh}</option>)}
                    </select>
                  </div>
                </div>
                {/* Per-parameter vertical axis */}
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Y-Axis group (Fix #1): give two parameters the same number to
                      combine them onto ONE shared Y-axis (e.g. compare NTU vs FNU
                      turbidity on a single scale). "Auto" keeps the default. */}
                  <div className="flex items-center gap-1.5">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Y-Axis</label>
                    <select
                      value={s.yAxisGroup || 'Auto'}
                      onChange={e => updateSeries(param.id, 'yAxisGroup', e.target.value)}
                      title="Put parameters on the same Y-Axis number to share one scale"
                      className="common-size15 border bg-white border-gray-200 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-gray-50"
                    >
                      {['Auto', '1', '2', '3', '4'].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Min</label>
                    <input type="number" placeholder="Minimum #" value={s.yMin ?? ''} onChange={e => updateSeries(param.id, 'yMin', numOrUndef(e.target.value))} className="w-24 aap-textfiel common-size15 text-xs border bg-white border-gray-200 rounded-lg px-4 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Max</label>
                    <input type="number" placeholder="Maximum #" value={s.yMax ?? ''} onChange={e => updateSeries(param.id, 'yMax', numOrUndef(e.target.value))} className="w-24 aap-textfiel common-size15 text-xs border bg-white border-gray-200 rounded-lg px-4 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
                    <label className="common-size15 font-semibold text-xs text-[#4D4D4D] ">Steps</label>
                    <input type="number" placeholder="Default" value={s.ySteps ?? ''} onChange={e => updateSeries(param.id, 'ySteps', numOrUndef(e.target.value))} className="w-20 aap-textfiel common-size15 text-xs border bg-white border-gray-200 rounded-lg px-4 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                </div>
              </div>
            )
          })}
          <div className="py-2 bg-white">
            <button
              onClick={() => { onSave(draft); onClose() }}
              className="savebtn common-size18 w-full px-4 py-1.5 bg-[#358ECD] font-bold text-white text-sm rounded transition"
            >Save</button>
            <button
              onClick={onClose}
              className="cancelbtn common-size16 mt-2 w-full text-center text-sm text-[#358ECD] font-semibold hover:underline disabled:opacity-50"
            >Cancel</button>
          </div>

        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

// ─── Wind Rose Editor Modal ────────────────────────────────────────────────────
export function WindRoseEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const defaultColors = Object.fromEntries(WIND_SPEED_BANDS.map(b => [b.key, b.defaultColor]))

  const [widgetName, setWidgetName] = useState(initialSettings.widgetName || '')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(initialSettings.selectedDevice || '')
  const [windDirectionParam, setWindDirectionParam] = useState(initialSettings.windDirectionParam || '')
  const [windSpeedParam, setWindSpeedParam] = useState(initialSettings.windSpeedParam || '')
  const [keyColors, setKeyColors] = useState(initialSettings.keyColors || defaultColors)
  const [loading, setLoading] = useState(false)

  // Re-populate form fields whenever modal opens (uses latest saved settings)
  useEffect(() => {
    if (open) {
      setWidgetName(initialSettings.widgetName || '')
      setSelectedDevice(initialSettings.selectedDevice || '')
      setWindDirectionParam(initialSettings.windDirectionParam || '')
      setWindSpeedParam(initialSettings.windSpeedParam || '')
      setKeyColors(initialSettings.keyColors || defaultColors)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Flatten all devices across sites
  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach(site => {
      ;(site.devices || []).forEach(dev => {
        devs.push({ ...dev, siteName: site.name || site.station_name || '' })
      })
    })
    return devs
  }, [sites])

  // Parameters of selected device — flattened across all sensors so the
  // dropdown actually populates. fetchProjectSites returns parameters
  // under `dev.sensors[].parameters[]`, never `dev.parameters` directly.
  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    const dev = allDevices.find(d => String(d.id) === String(selectedDevice))
    return deviceFlatParams(dev)
  }, [selectedDevice, allDevices])

  // Fetch project sites when modal opens
  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then(data => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  // Auto-default: when the modal opens with no saved selection AND we've
  // just loaded the sites tree, scan for the first device that has both
  // a wind-speed and wind-direction parameter (per WIND_PAIRS) and
  // pre-select it. This matches the customer spec — "Parameter selection
  // defaults to wind speed and wind direction of the first logger which
  // has wind speed and wind direction parameters."
  useEffect(() => {
    if (!open) return
    if (selectedDevice || windDirectionParam || windSpeedParam) return
    if (!sites || sites.length === 0) return
    const defaults = detectWindDefaults(sites)
    if (!defaults) return
    setSelectedDevice(String(defaults.deviceId))
    setWindSpeedParam(String(defaults.speedParamId))
    setWindDirectionParam(String(defaults.directionParamId))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sites])

  if (!open) return null

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">Wind Rose Editor</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Widget Name */}
          <div>
            <label className="common-size18 block text-xs font-bold text-[#4D4D4D] mb-3">
              Widget Name
              <span className="common-size12 ml-4 font-normal text-[#AFAFAF]">(Leave Blank For Default)</span>
            </label>
            <input
              type="text"
              placeholder="Leave Blank For Default"
              value={widgetName}
              onChange={e => setWidgetName(e.target.value)}
              className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>

          {/* Parameter Selection */}
          <div className="border-t pt-4">
            <h3 className="common-size18 text-xs font-bold text-[#4D4D4D] mb-4">Parameter Selection</h3>

            {/* Device */}
            <div className="mb-3">
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Device</label>
              <select
                value={selectedDevice}
                onChange={e => {
                  setSelectedDevice(e.target.value)
                  setWindDirectionParam('')
                  setWindSpeedParam('')
                }}
                className="common-size15 w-full border bg-white border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map(dev => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || dev.serial_number || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Wind Direction */}
            <div className="mb-3">
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Wind Direction</label>
              <select
                value={windDirectionParam}
                onChange={e => setWindDirectionParam(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="common-size15 w-full border bg-white border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Wind Speed */}
            <div>
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Wind Speed</label>
              <select
                value={windSpeedParam}
                onChange={e => setWindSpeedParam(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="common-size15 w-full border bg-white border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Key Colors */}
          <div className="border-t pt-4">
            <h3 className="common-size18 text-xs font-bold text-[#4D4D4D] mb-3">Key Colors</h3>
            <div className="space-y-2.5">
              {WIND_SPEED_BANDS.map(band => (
                <div key={band.key} className="flex items-center justify-between">
                  <span className="common-size15 font-semibold text-sm text-[#4D4D4D] w-24">{band.label}</span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: keyColors[band.key] }}
                    />
                    <input
                      type="color"
                      value={keyColors[band.key]}
                      onChange={e => setKeyColors(prev => ({ ...prev, [band.key]: e.target.value }))}
                      className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5"
                      title={`Pick color for ${band.label}`}
                    />
                    <span className="text-xs text-gray-400 font-mono w-16">{keyColors[band.key].toUpperCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white">
          
          <button
            onClick={() => {
              onSave({ widgetName, selectedDevice, windDirectionParam, windSpeedParam, keyColors })
              onClose()
            }}
            className="savebtn common-size18 w-full px-5 py-2 text-sm text-white bg-[#358ECD] uppercase font-bold rounded-lg transition "
          >Save</button>
          <button
            onClick={onClose}
            className="cancelbtn common-size16 w-full px-5 py-2 text-sm text-[#358ECD] font-semibold hover:underline border-gray-200 rounded-lg transition"
          >Cancel</button>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

// Bake the crop/zoom adjustment into a real image so it persists — the modal
// preview was a live CSS transform that was never applied to the saved file.
// Reproduces the preview: object-contain fit into the crop box, scaled by
// `zoom`, centred, cropped to the box. Falls back to the source URL if the
// image can't be drawn to a canvas (e.g. a cross-origin URL that taints it).
// Crop-box aspect the preview and the baked output BOTH use, so what the user
// frames is exactly what gets saved.
const PHOTO_OUT_W = 800, PHOTO_OUT_H = 352
const PHOTO_ASPECT = PHOTO_OUT_W / PHOTO_OUT_H

// Given the image's natural aspect and a zoom, return the rendered size and the
// pannable overflow — all as fractions of the crop box. `cover` (Math.max) fills
// the box at zoom=1; zoom > 1 grows it further, creating overflow on BOTH axes.
// Shared by the preview and the baker so their geometry can never drift.
function photoFrame(natAspect, zoom) {
  const na = natAspect > 0 ? natAspect : PHOTO_ASPECT
  const z = zoom || 1
  // Cover as a fraction of the box: the axis the image is "shorter" on fills
  // to 1, the other overflows.
  const coverW = na >= PHOTO_ASPECT ? na / PHOTO_ASPECT : 1
  const coverH = na >= PHOTO_ASPECT ? 1 : PHOTO_ASPECT / na
  const w = coverW * z, h = coverH * z
  return { w, h, overflowX: Math.max(0, (w - 1) / 2), overflowY: Math.max(0, (h - 1) / 2) }
}

// offset: {x,y} each in [-1,1], 0 = centred, ±1 = pan fully to that edge.
function bakeProjectPhoto(srcUrl, zoom = 1, offset = { x: 0, y: 0 }) {
  return new Promise((resolve) => {
    if (!srcUrl) { resolve(srcUrl); return }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const OUT_W = PHOTO_OUT_W, OUT_H = PHOTO_OUT_H
        const canvas = document.createElement('canvas')
        canvas.width = OUT_W; canvas.height = OUT_H
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#f3f4f6'; ctx.fillRect(0, 0, OUT_W, OUT_H)
        const ox = Number.isFinite(offset?.x) ? Math.max(-1, Math.min(1, offset.x)) : 0
        const oy = Number.isFinite(offset?.y) ? Math.max(-1, Math.min(1, offset.y)) : 0
        const f = photoFrame(img.width / img.height, zoom)
        const rw = f.w * OUT_W, rh = f.h * OUT_H
        // Centre, then shift by the pan fraction of the available overflow.
        const drawX = (OUT_W - rw) / 2 + ox * f.overflowX * OUT_W
        const drawY = (OUT_H - rh) / 2 + oy * f.overflowY * OUT_H
        ctx.drawImage(img, drawX, drawY, rw, rh)
        resolve(canvas.toDataURL('image/jpeg', 0.9))
      } catch {
        resolve(srcUrl)   // tainted canvas / draw failure — persist as-is
      }
    }
    img.onerror = () => resolve(srcUrl)
    img.src = srcUrl
  })
}

// ─── Change Project Photo Modal ────────────────────────────────────────────────
// `currentUrl` must be the PRISTINE (never-zoomed) source image, not a
// previously-baked/flattened one — otherwise re-opening this modal to adjust
// zoom would zoom into an already-zoomed photo instead of the original.
// `currentZoom` pre-fills the slider so the preview looks the same as what's
// currently saved, without the underlying source ever changing. onSave
// receives `{ bakedUrl, sourceUrl, zoom }` so the caller can persist the
// source alongside the flattened result — see ProjectPhotoWidget.
export function ChangeProjectPhotoModal({ open, onClose, onSave, currentUrl, currentZoom = 1, currentOffset = { x: 0, y: 0 } }) {
  const [dragOver, setDragOver] = useState(false)
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [zoom, setZoom] = useState(1)
  // Pan position, each axis in [-1,1] (0 = centred). Adjusted by dragging the
  // image within the crop box; only axes that actually overflow can move.
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [natAspect, setNatAspect] = useState(0)   // image natural W/H, set on load
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef(null)
  const cropBoxRef = useRef(null)
  const panRef = useRef(null)   // active drag: { sx, sy, ox, oy, w, h, ovX, ovY }

  const sanitizeOffset = (o) => ({
    x: Number.isFinite(o?.x) && Math.abs(o.x) <= 1 ? o.x : 0,
    y: Number.isFinite(o?.y) && Math.abs(o.y) <= 1 ? o.y : 0,
  })

  useEffect(() => {
    if (open) {
      setFile(null)
      setPreviewUrl(currentUrl || '')
      setZoom(currentZoom || 1)
      setOffset(sanitizeOffset(currentOffset))
      setDragOver(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUrl, currentZoom])

  const handleFilePick = (f) => {
    if (!f || !f.type.startsWith('image/')) return
    setFile(f)
    // A freshly picked image starts centred.
    setOffset({ x: 0, y: 0 })
    const reader = new FileReader()
    reader.onload = e => setPreviewUrl(e.target.result)
    reader.readAsDataURL(f)
  }

  // Live frame geometry (rendered size + pannable overflow, as fractions of the
  // box) — shared with the baker so preview and saved output match exactly.
  const frame = photoFrame(natAspect, zoom)

  // Drag-to-pan on BOTH axes. The pointer moves the image 1:1 (drag right → the
  // image moves right), converting the pixel delta into a fraction of the axis'
  // available overflow. An axis with no overflow (e.g. a normal photo at zoom=1
  // has none horizontally) simply stays put.
  const onPanStart = (e) => {
    const rect = cropBoxRef.current?.getBoundingClientRect()
    panRef.current = {
      sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y,
      w: rect?.width || 1, h: rect?.height || 1,
      ovX: frame.overflowX, ovY: frame.overflowY,
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onPanMove = (e) => {
    const d = panRef.current
    if (!d) return
    const dxFrac = (e.clientX - d.sx) / d.w   // fraction of box width
    const dyFrac = (e.clientY - d.sy) / d.h
    const nx = d.ovX > 0 ? Math.max(-1, Math.min(1, d.ox + dxFrac / d.ovX)) : d.ox
    const ny = d.ovY > 0 ? Math.max(-1, Math.min(1, d.oy + dyFrac / d.ovY)) : d.oy
    setOffset({ x: nx, y: ny })
  }
  const onPanEnd = (e) => {
    panRef.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFilePick(e.dataTransfer.files[0])
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Bake the current preview (new upload OR the existing photo) at the
      // chosen zoom, so the adjustment is what actually gets displayed. Also
      // pass along `previewUrl` itself — the un-zoomed source — so the next
      // time this modal opens it starts from the same pristine image instead
      // of re-zooming an already-flattened one.
      const baked = await bakeProjectPhoto(previewUrl, zoom, offset)
      await onSave({ bakedUrl: baked, sourceUrl: previewUrl, zoom, offset })
    } finally {
      setSaving(false)
      onClose()
    }
  }

  if (!open) return null

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#4D4D4D]">Change Project Photo</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Drag-and-drop upload zone */}
          <div
            role="button"
            tabIndex={0}
            className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-10 px-6 cursor-pointer transition select-none ${
              dragOver ? 'border-[#358ECD] bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-gray-400 mb-3" aria-hidden>
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="17,8 12,3 7,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p className="text-sm text-gray-500 text-center leading-relaxed">
              <span className="text-[#358ECD] ">Drag Attachments</span> or{' '}
              <span className="text-[#358ECD] ">Upload Featured Photo</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => handleFilePick(e.target.files[0])}
            />
          </div>

          {/* Image crop preview + zoom slider */}
          {previewUrl && (
            <div className="space-y-2">
              <div
                ref={cropBoxRef}
                className="relative w-full rounded-lg overflow-hidden border border-gray-200 bg-gray-100 touch-none select-none cursor-grab active:cursor-grabbing"
                style={{ aspectRatio: `${PHOTO_OUT_W} / ${PHOTO_OUT_H}` }}
                onPointerDown={onPanStart}
                onPointerMove={onPanMove}
                onPointerUp={onPanEnd}
                onPointerCancel={onPanEnd}
              >
                {/* Explicitly sized + positioned (not object-fit) so the pan can
                    move on BOTH axes — object-position could only shift along
                    whichever axis happened to overflow. bakeProjectPhoto uses
                    the exact same photoFrame() geometry, so the saved crop
                    matches this preview pixel-for-pixel. */}
                <img
                  src={previewUrl}
                  alt="Preview"
                  draggable={false}
                  onLoad={(e) => setNatAspect((e.target.naturalWidth || 1) / (e.target.naturalHeight || 1))}
                  style={{
                    position: 'absolute',
                    width:  `${frame.w * 100}%`,
                    height: `${frame.h * 100}%`,
                    left: `${((1 - frame.w) / 2 + offset.x * frame.overflowX) * 100}%`,
                    top:  `${((1 - frame.h) / 2 + offset.y * frame.overflowY) * 100}%`,
                    maxWidth: 'none',
                    maxHeight: 'none',
                    pointerEvents: 'none',
                    transition: panRef.current ? 'none' : 'width 0.15s ease, height 0.15s ease, left 0.15s ease, top 0.15s ease',
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 text-center">Drag to move the image; use the slider to zoom.</p>

              {/* Zoom slider */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  className="text-gray-500 hover:text-[#4D4D4D] text-xl font-bold leading-none pb-0.5 transition"
                  onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(1)))}
                  aria-label="Zoom out"
                >−</button>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  className="flex-1 accent-[#358ECD]"
                />
                <button
                  className="text-gray-500 hover:text-[#4D4D4D] text-xl font-bold leading-none pb-0.5 transition"
                  onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(1)))}
                  aria-label="Zoom in"
                >+</button>
              </div>

              {/* Reset Zoom — visible only once the image has been zoomed away
                  from its default (1x) scale, mirroring the chart's Reset Zoom
                  convention elsewhere in this file. */}
              {zoom !== 1 && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setZoom(1)}
                    className="text-xs font-semibold text-[#358ECD] hover:underline"
                  >
                    Reset Zoom
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white">
          <button
            onClick={handleSave}
            disabled={saving}
            className="savebtn w-full py-2.5 text-sm font-semibold text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save Project Photo'}
          </button>
          <button
            onClick={onClose}
            className="cancelbtn w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
          >Cancel</button>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * Evenly-spaced "nice" Y-axis ticks for a data range. Recharts' default
 * auto-ticks sometimes land on ugly, unevenly-spaced values (e.g. 70, 85, 112)
 * for ranges whose endpoints aren't round. This rounds the step to a 1/2/2.5/5
 * ×10ⁿ value and snaps the domain out to that step, so ticks are always evenly
 * spaced on round numbers (e.g. 70, 80, 90, 100, 110, 120). Returns undefined
 * for a flat/invalid range so the caller can fall back to auto.
 */
/**
 * Y-axis autoscale window from the data range + a per-unit minimum span
 * (UnitLib.yaxis_span). Widens the axis to at least `spanHint` so small
 * fluctuations don't look like huge swings, and always adds ≥20% headroom
 * (DefaultSpan) so points don't sit on the frame. The data stays centred:
 *
 *   DefaultSpan = range * 1.2
 *   Span        = max(DefaultSpan, spanHint)
 *   Cushion     = range - Span              // ≤ 0
 *   lo = minval + Cushion/2                 // = minval − (Span−range)/2
 *   hi = maxval − Cushion/2                 // = maxval + (Span−range)/2
 *
 * (The lo sign is + here: the spec's paste had `minval − Cushion/2`, which
 * pushes the axis floor ABOVE the data and clips the lowest point — this
 * centres the window instead.) Returns null when it can't form a
 * positive-width window (e.g. flat data with no span hint).
 */
function autoscaleSpan(minval, maxval, spanHint = 0) {
  if (!isFinite(minval) || !isFinite(maxval) || maxval < minval) return null
  const range = maxval - minval
  const span = Math.max(range * 1.2, spanHint > 0 ? spanHint : 0)
  if (!(span > 0)) return null
  const cushion = range - span
  return [minval + cushion / 2, maxval - cushion / 2]
}

function niceTicks(min, max, count = 6) {
  if (!isFinite(min) || !isFinite(max) || max <= min) return undefined
  const rawStep = (max - min) / (count - 1)
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const norm = rawStep / mag
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10
  const step = niceNorm * mag
  const niceMin = Math.floor(min / step) * step
  const niceMax = Math.ceil(max / step) * step
  const ticks = []
  // + step/2 guards against floating-point drift dropping the last tick.
  for (let v = niceMin; v <= niceMax + step / 2; v += step) ticks.push(+v.toFixed(6))
  return ticks.length > 1 ? ticks : undefined
}

/**
 * Line Chart Widget — connected to DashboardContext for real sensor data
 * XD design: D/W/M/Y + Start Date + End Date + three-dot menu + pencil editor
 */
export function LineChartWidget({ widget }) {
  const { projectId } = useParams()
  const {
    selectedParameters = [],
    dateRange = {},
    activePeriod = 'D',
    handlePeriodClick,
    setDateRange,
    isCustomizing = false, canEdit = true,
    // Bumped whenever Parameter Configuration is saved (unit Convert,
    // Sensor min/max, precision) — listed in the fetch effect's deps below
    // so a config change re-fetches immediately instead of the widget
    // continuing to show the pre-save response until a full page reload.
    paramConfigVersion = 0,
    // Bumped on every manual/auto refresh — in the fetch deps so the chart
    // re-pulls in place (no remount), even when the window is unchanged
    // (e.g. a custom date range).
    dataRefreshNonce = 0,
  } = useDashboardContext()

  // Customer's configured timezone (from Account.timezone, populated by the
  // login response).  AccountModal stores display strings like
  // "(UTC-05:00) Eastern Time (US & Canada)" — resolveIanaTz() maps those
  // to IANA names that Intl will accept.  Defensive: never throws, falls
  // back to browser TZ then UTC if all else fails.
  const rawTz = useAuthStore(s => s.user?.timezone)
  const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])

  const [loading, setLoading] = useState(false)
  const [rawData, setRawData] = useState([])
  const [seriesMetadata, setSeriesMetadata] = useState({})
  const [dataWarning, setDataWarning] = useState(null)

  // ── Drag-to-zoom state ───────────────────────────────────────────────────
  // `zoomDragStart` / `zoomDragEnd` track the X coordinates of an in-flight
  // selection (mouse down → move). `zoomDomain` is the locked-in range
  // applied to the XAxis after mouse up. Click "Reset Zoom" → null.
  // Coordinates are in the same units as the chart's x dataKey:
  //   D view : numeric ms epoch
  //   W/M/Y view : categorical string label
  const [zoomDragStart, setZoomDragStart] = useState(null)
  const [zoomDragEnd,   setZoomDragEnd]   = useState(null)
  const [zoomDomain,    setZoomDomain]    = useState(null)

  // Legend hover-to-highlight: the parameter id currently hovered in the
  // legend. When set, that series' line is emphasised (thicker, full opacity)
  // and every other line is dimmed, matching the old WQData behaviour.
  const [hoveredParamId, setHoveredParamId] = useState(null)

  // Legend click-to-toggle: parameter ids the user has hidden. A hidden series
  // is dropped from the plot entirely — its line AND its Y-axis (no phantom
  // gutter) — while its legend entry stays visible (struck-through / greyed) so
  // it can be clicked again to bring it back. Lets the user pick exactly which
  // parameters to view instead of only highlighting one.
  const [hiddenParamIds, setHiddenParamIds] = useState(() => new Set())
  const toggleParamHidden = useCallback((paramId) => {
    const id = String(paramId)
    setHiddenParamIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  // Data Table + Statistics are now popups, not inline view-mode swaps.
  // The chart always renders inline behind the modal backdrop.
  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [statsModalOpen, setStatsModalOpen] = useState(false)

  // Graph editor state — persisted to localStorage, PROJECT-SCOPED so
  // two projects sharing the Overview template don't collide on the
  // same widget.id key. See scopedLsKey doc-block at the top of file.
  const [showEditor, setShowEditor] = useState(false)
  const [graphSettings, setGraphSettings] = useState(() =>
    lsGetScoped('wq_graph', projectId, widget.id,
      { series: {}, yMin: undefined, yMax: undefined, ySteps: undefined })
  )
  // Last-seen unit per parameter, so the fetch effect can detect when a
  // parameter's Convert setting changes (see the fetch effect below).
  const prevUnitsRef = useRef({})

  // Three-dot menu + chart DOM ref (for PNG/PDF export)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const chartContainerRef = useRef(null)
  useEffect(() => {
    if (!showMenu) return
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showMenu])

  // Date input values (YYYY-MM-DD for <input type="date">)
  const fromDate = dateRange.from ? dateRange.from.split(' ')[0] : ''
  const toDate   = dateRange.to   ? dateRange.to.split(' ')[0]   : ''

  // D/W/M/Y: set activePeriod AND auto-set the dateRange window.
  // The window endpoints are computed in the CUSTOMER's timezone so the
  // chart shows a rolling [now − N, now] in their local wall-clock — matching
  // the legacy Symfony getDatesFromRangeV3() behaviour exactly.
  //   D : now − 24h        W : now − 7 days
  //   M : now − 30 days    Y : same calendar day/time, previous year
  const handlePeriodButtonClick = (p) => {
    handlePeriodClick?.(p)
  }

  const MAX_POINTS = 50000
  useEffect(() => {
    if (!selectedParameters.length) {
      setRawData([])
      setSeriesMetadata({})
      setDataWarning(null)
      return
    }
    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      setDataWarning(null)
      try {
        const allData = {}
        const metadata = {}
        for (const param of selectedParameters) {
          if (!param.id) continue
          try {
            const response = await projectsApi.fetchGraphData(Number(param.id), {
              fromDate: dateRange.from,   // wall-clock in customer TZ
              toDate: dateRange.to,       // wall-clock in customer TZ
              timezone: customerTz,       // tells backend how to interpret the strings
            })
            if (!response || response.error || !Array.isArray(response.data)) continue
            metadata[param.id] = {
              name: param.name,
              unit: response.unit || '',
              siteName: param.siteName || param.site_name || '',
              deviceName: param.deviceName || param.device_name || '',
              sensorName: param.sensorName || param.sensor_name || '',
              precision: response.precision ?? 0,
              // Configured sensor range → default y-axis bounds (see YAxis).
              ymin: response.ymin ?? null,
              ymax: response.ymax ?? null,
              // Per-unit autoscale hints (UnitLib): min span + preferred ticks.
              yaxisSpan: response.yaxisSpan ?? null,
              yaxisStep: response.yaxisStep ?? null,
            }
            response.data.forEach(point => {
              if (point.x == null) return
              const v = point.y
              // Hidden-status readings come back from graph-data as y:null, and
              // legacy sentinels (999.9 etc.) aren't real readings — keep BOTH as
              // null (not dropped) so the line BREAKS at the gap with
              // connectNulls={false}, instead of bridging the hidden section.
              const isSentinel = v === 999.9 || v === -999.9 || v === 9999 || v === -9999
              if (!allData[point.x]) allData[point.x] = { x: point.x }
              allData[point.x][`p_${param.id}`] = (v == null || isSentinel) ? null : v
            })
          } catch { /* skip failed param */ }
        }
        if (cancelled) return
        let processed = Object.values(allData)
          // Keep every timestamp that carries a parameter key — even a null gap —
          // so the line breaks at hidden sections; drop only bare {x} rows.
          .filter(d => Object.keys(d).length > 1)
          .sort((a, b) => a.x - b.x)
        // Large-dataset protection: truncate + warn
        if (processed.length > MAX_POINTS) {
          processed = processed.slice(-MAX_POINTS)
          setDataWarning(`Dataset too large — showing the most recent ${MAX_POINTS.toLocaleString()} records only.`)
        }
        setRawData(processed)
        setSeriesMetadata(metadata)

        // If a parameter's unit changed since the last fetch (its Convert
        // setting was just changed in Parameter Configuration), any
        // manually-set per-series Y-Axis Min/Max/Steps on THIS widget were
        // entered under the OLD unit and are now meaningless — e.g. a
        // Celsius range like 20–28 would clip out virtually all of the
        // newly-converted Fahrenheit data (68–82), making the chart look
        // completely blank even though fresh, correct data was fetched.
        // Clear the stale override so the axis falls back to auto-scaling
        // against the new data. Does nothing on first mount (no prior unit
        // recorded yet) or when the unit hasn't actually changed.
        setGraphSettings((prevSettings) => {
          let changed = false
          const nextSeries = { ...(prevSettings.series || {}) }
          for (const [id, meta] of Object.entries(metadata)) {
            const prevUnit = prevUnitsRef.current[id]
            const ss = nextSeries[id]
            if (prevUnit !== undefined && prevUnit !== meta.unit && ss &&
                (ss.yMin !== undefined || ss.yMax !== undefined || ss.ySteps !== undefined)) {
              const { yMin, yMax, ySteps, ...rest } = ss
              nextSeries[id] = rest
              changed = true
            }
            prevUnitsRef.current[id] = meta.unit
          }
          if (!changed) return prevSettings
          const next = { ...prevSettings, series: nextSeries }
          lsSetScoped('wq_graph', projectId, widget.id, next)
          return next
        })
      } catch (err) {
        console.error('[LineChartWidget] fetch error', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [selectedParameters, dateRange, paramConfigVersion, dataRefreshNonce])

  // Drop any drag-to-zoom selection whenever the dataset behind the chart
  // changes — switching to a new period or set of parameters should reset
  // the view, not leave the user staring at the old zoom window applied
  // to brand-new data.
  useEffect(() => {
    setZoomDomain(null)
    setZoomDragStart(null)
    setZoomDragEnd(null)
  }, [selectedParameters, dateRange, activePeriod])

  // Plot raw 10-minute samples for every D/W/M/Y view — no bucketing,
  // no averaging. The chart and the underlying Data table share the exact
  // same points; only the x-axis tick format changes per period.
  const graphData = useMemo(() => {
    return rawData.map(pt => ({
      ...pt,
      x: typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x),
    }))
  }, [rawData])

  // All views use a numeric (time-scale) x-axis now; rotation kept for the
  // longer M-view tick labels.
  const isCategorical = false
  // Nothing rotates any more: Y used to tilt its labels 45° because Recharts
  // packed ~20 day-level dates across the year. With one mark per month there
  // are twelve short labels, which sit flat comfortably.
  const rotateAxis    = false

  const formatXAxisTick = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return ''
    // Drag-zoomed: the visible span no longer matches the selected period, so
    // format by the ZOOMED span instead of the period. Without this a zoom into
    // a few hours of a Month view kept the date-only "Jul 8" format and every
    // auto-tick printed the same date. makeTimeAxisFormatter picks HH:MM /
    // date / month by span, matching the report axis.
    if (Array.isArray(zoomDomain) && zoomDomain.length === 2 &&
        typeof zoomDomain[0] === 'number' && typeof zoomDomain[1] === 'number') {
      const span = Math.abs(zoomDomain[1] - zoomDomain[0])
      return makeTimeAxisFormatter(span, customerTz)(value)
    }
    // W view: 12-hour ticks — midnight shows the date ("Jul 30"), noon shows
    // the time ("12:00"). This is the standard week axis across all graphs.
    if (activePeriod === 'W') {
      const p = wallClockPartsInTz(d, customerTz)
      if (p.hour === 0 && p.minute === 0) {
        return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
      }
      return new Intl.DateTimeFormat('en-GB', { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
    }
    // M view: date labels ("Jul 8") at day boundaries, ~2-day spaced (mTicks).
    if (activePeriod === 'M') {
      return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
    }
    // Y view: month marks carry a prepared label ("Mar", and "Mar 2026" at each
    // year rollover). Falls through to the generic formatter only if the month
    // ticks couldn't be built (e.g. a single data point).
    if (activePeriod === 'Y' && xMonthLabels) {
      return xMonthLabels.get(value) ?? ''
    }
    const opts = (() => {
      switch (activePeriod) {
        case 'Y': return { timeZone: customerTz, month: 'short', day: '2-digit' }
        case 'D':
        default:  return { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false }
      }
    })()
    return new Intl.DateTimeFormat('en-GB', opts).format(d)
  }

  // D view bounds: explicit [now − 24h, now] in UTC ms.  Captured into a memo
  // bound to `dateRange.to` so the window only re-anchors when the user re-
  // selects a period (D button) or shifts the picker — not on every render.
  // This replaces Symfony's null-sentinel trick by giving Recharts an
  // explicit numeric domain.
  const dWindowMs = useMemo(() => {
    if (activePeriod !== 'D') return null
    // Prefer the wall-clock-in-TZ strings already set on dateRange; fall back
    // to "right now in customer TZ" if those are missing.
    const endMs   = parseWallClockInTzToMs(dateRange.to,   customerTz) ?? Date.now()
    const startMs = parseWallClockInTzToMs(dateRange.from, customerTz) ?? (endMs - 24 * 60 * 60 * 1000)
    return { startMs, endMs }
  }, [activePeriod, dateRange.from, dateRange.to, customerTz])

  // D view: 2-hour tick marks anchored to even-hour boundaries IN THE
  // CUSTOMER'S TZ.  e.g. for Asia/Kolkata with now=04:30 IST → 04:00 prev
  // day, 06:00, 08:00 ... 02:00, 04:00 IST.
  const dTicks = useMemo(() => {
    if (activePeriod !== 'D' || !dWindowMs) return undefined
    const { startMs, endMs } = dWindowMs
    // Find the first even-hour boundary at-or-after startMs, evaluated in TZ.
    const startParts = wallClockPartsInTz(new Date(startMs), customerTz)
    // Round up minute/second to next even hour.
    const roundUpHour = startParts.minute === 0 && startParts.second === 0
      ? (startParts.hour % 2 === 0 ? startParts.hour : startParts.hour + 1)
      : startParts.hour + (startParts.hour % 2 === 0 ? 2 : 1)
    // Build the first tick as wall-clock in customer TZ → UTC ms.
    const firstTickStr =
      `${startParts.year}-${String(startParts.month).padStart(2,'0')}-` +
      `${String(startParts.day).padStart(2,'0')} ` +
      `${String(roundUpHour % 24).padStart(2,'0')}:00:00`
    // If roundUpHour wrapped past 24, advance the date by one day.
    const dayAdvance = Math.floor(roundUpHour / 24)
    let firstTickMs = parseWallClockInTzToMs(firstTickStr, customerTz)
    if (firstTickMs == null) return undefined
    firstTickMs += dayAdvance * 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstTickMs; t <= endMs; t += 2 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [activePeriod, dWindowMs, customerTz])

  // W view: 12-hour tick marks anchored to tz-local midnight (00:00) & noon
  // (12:00) across the data window, so the axis reads "Jul 30 · 12:00 · Jul 31
  // · 12:00 · …" (see formatXAxisTick).
  const wTicks = useMemo(() => {
    if (activePeriod !== 'W' || graphData.length === 0) return undefined
    const startMs = graphData[0].x
    const endMs   = graphData[graphData.length - 1].x
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const onBoundary = sp.minute === 0 && sp.second === 0 && (sp.hour === 0 || sp.hour === 12)
    const nextHour = onBoundary ? sp.hour : (sp.hour < 12 ? 12 : 24)
    const dayAdvance = Math.floor(nextHour / 24)
    const firstStr =
      `${sp.year}-${String(sp.month).padStart(2,'0')}-${String(sp.day).padStart(2,'0')} ` +
      `${String(nextHour % 24).padStart(2,'0')}:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    firstMs += dayAdvance * 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstMs; t <= endMs; t += 12 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [activePeriod, graphData, customerTz])

  // M view: day-boundary ticks at a "nice" day-step (~15 labels) so the axis
  // reads "Jul 8 · Jul 10 · Jul 12 · …" across the month regardless of span.
  const mTicks = useMemo(() => {
    if (activePeriod !== 'M' || graphData.length === 0) return undefined
    const startMs = graphData[0].x
    const endMs   = graphData[graphData.length - 1].x
    const totalDays = Math.max(1, Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)))
    const NICE = [1, 2, 3, 5, 7, 10, 14]
    const step = NICE.find((n) => n >= totalDays / 15) || 14
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const atMidnight = sp.hour === 0 && sp.minute === 0 && sp.second === 0
    const firstStr = `${sp.year}-${String(sp.month).padStart(2,'0')}-${String(sp.day).padStart(2,'0')} 00:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    if (!atMidnight) firstMs += 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstMs; t <= endMs; t += step * 24 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [activePeriod, graphData, customerTz])

  // Y view: one mark per month. Left to place its own ticks, Recharts spreads
  // ~20 day-level labels across the year ("31 Mar, 07 Apr, 15 Apr, 22 Apr …"),
  // which has to be rotated 45° to fit and still reads as noise. Month starts
  // are computed in the customer's timezone so the marks sit on local month
  // boundaries.
  // ── Shared Y axes for repeated parameters ────────────────────────────────
  // Selecting the same parameter from two devices (e.g. Air Temperature from
  // two loggers) used to draw a separate Y axis per series — two identical
  // "F 50…80" scales flanking the plot. Parameters that measure the same thing
  // in the same unit share one axis instead, so the duplicate gutters collapse
  // and the traces are read against a single scale.
  //
  // Default grouping key is the DISPLAY-unit NAME string — the unit each series is
  // actually shown in AFTER any Convert setting is applied (response.unit from
  // graph-data, stored in seriesMetadata[id].unit). This matches the legacy Symfony
  // report graph (V2ReportDataProcessor::findDisplayUnitName — a plain string
  // compare on the DISPLAY unit). Every series shown in the same unit shares ONE
  // axis: four differently-named °F temperatures ("Temperature" / "Surface
  // Temperature" / "Sonde Temperature" / "Internal Temperature") collapse onto a
  // single °F axis, AND a sensor whose native °C reading is DISPLAYED in °F lands
  // on the SAME axis as a native-°F sensor — because both DISPLAY "F". °C vs °F
  // stay on separate axes. Param NID / parameter id are deliberately NOT used: NID
  // encodes the ORIGINAL unit_id, so a converted series (native °C shown as °F)
  // would split onto its own axis even though it is displayed in the same unit.
  //
  // OVERRIDE (Fix #1): the chart editor lets a user assign a parameter to a named
  // Y-axis group (series[id].yAxisGroup = "1".."4"). Parameters sharing a group
  // label collapse onto ONE shared axis regardless of unit — so e.g. YSI
  // Turbidity (NTU) and NexSens Turbidity (FNU), which are practically the same,
  // can be compared on a single scale. "Auto"/blank keeps the unit-name default.
  const axisGroups = useMemo(() => {
    const groups = []
    const byKey = new Map()
    selectedParameters.forEach((param, idx) => {
      const meta = seriesMetadata[param.id] || {}
      const override = String(graphSettings.series?.[param.id]?.yAxisGroup ?? '').trim()
      let key
      if (override && override.toLowerCase() !== 'auto') {
        key = `grp:${override.toLowerCase()}`
      } else {
        // Blank-unit series keep their own axis rather than all piling onto one
        // "no unit" axis.
        const unit = String(meta.unit ?? param.unit ?? '').trim().toLowerCase()
        key = unit ? `unit:${unit}` : `p:${param.id}`
      }
      let g = byKey.get(key)
      if (!g) {
        g = { key, axisId: `axis-${key}`, members: [], firstIdx: idx }
        byKey.set(key, g)
        groups.push(g)
      }
      g.members.push({ param, idx })
    })
    return groups
  }, [selectedParameters, seriesMetadata, graphSettings.series])

  // parameter id → the axis it belongs to.
  const axisIdFor = useMemo(() => {
    const m = new Map()
    for (const g of axisGroups) for (const { param } of g.members) m.set(String(param.id), g.axisId)
    return m
  }, [axisGroups])

  // When the user drag-zooms into a portion of the chart, the Y-axis autoscale
  // must recompute against ONLY the points inside that X-window — otherwise the
  // scale stays pinned to the full dataset and the zoomed-in variation looks
  // flat. Falls back to the full data when not zoomed.
  const yScaleData = useMemo(() => {
    if (Array.isArray(zoomDomain) && zoomDomain.length === 2 &&
        typeof zoomDomain[0] === 'number' && typeof zoomDomain[1] === 'number') {
      const lo = Math.min(zoomDomain[0], zoomDomain[1])
      const hi = Math.max(zoomDomain[0], zoomDomain[1])
      return graphData.filter(d => typeof d.x === 'number' && d.x >= lo && d.x <= hi)
    }
    return graphData
  }, [graphData, zoomDomain])

  // Y view bounds: the selected [start, end] window, so the axis spans the whole
  // year even when the device only started reporting partway through it. Taking
  // the bounds from the DATA instead is what left the axis running Apr→Aug for a
  // window that was really Aug→Aug.
  const yWindowMs = useMemo(() => {
    if (activePeriod !== 'Y') return null
    const startMs = parseWallClockInTzToMs(dateRange.from, customerTz)
    const endMs   = parseWallClockInTzToMs(dateRange.to,   customerTz)
    return (startMs != null && endMs != null && endMs > startMs) ? { startMs, endMs } : null
  }, [activePeriod, dateRange.from, dateRange.to, customerTz])

  // One mark per month, anchored to the window's start day: a year from 14 Aug
  // reads 14 Sep, 14 Oct, … 14 Aug.
  const xMonthTicks = useMemo(() => {
    if (activePeriod !== 'Y' || !yWindowMs) return undefined
    const ticks = monthlyTicksFrom(yWindowMs.startMs, yWindowMs.endMs, customerTz)
    return ticks.length > 1 ? ticks : undefined
  }, [activePeriod, yWindowMs, customerTz])

  const xMonthLabels = useMemo(
    () => (xMonthTicks ? dayMonthTickLabels(xMonthTicks, customerTz) : null),
    [xMonthTicks, customerTz],
  )

  const formatTooltipLabel = (value) => {
    if (isCategorical) return String(value)
    if (!value) return ''
    return new Intl.DateTimeFormat('en-US', {
      timeZone: customerTz,
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value))
  }

  // Vertical-axis settings are now per-parameter (set in the Graph Editor).
  // For the single Y-axis chart we use the first selected parameter's axis
  // (exact for one parameter; the first one's scale for combined charts).
  const axisSettings = useMemo(() => {
    const first = selectedParameters[0]
    return (first && graphSettings.series?.[first.id]) || {}
  }, [selectedParameters, graphSettings.series])

  const yDomain = useMemo(() => {
    const min = axisSettings.yMin != null ? axisSettings.yMin : 'auto'
    const max = axisSettings.yMax != null ? axisSettings.yMax : 'auto'
    return [min, max]
  }, [axisSettings])

  const yTicks = useMemo(() => {
    if (axisSettings.yMin != null && axisSettings.yMax != null && axisSettings.ySteps) {
      const step = (axisSettings.yMax - axisSettings.yMin) / axisSettings.ySteps
      return Array.from({ length: axisSettings.ySteps + 1 }, (_, i) =>
        +(axisSettings.yMin + i * step).toFixed(4)
      )
    }
    return undefined
  }, [axisSettings])

  // ── Format timestamp for table rows ─────────────────────────────────────────
  // Format in the customer/profile timezone (customerTz) — same as the chart —
  // NOT the browser's local timezone. Using the bare toLocaleString() rendered
  // rows in whatever timezone the viewer's browser was in (e.g. IST), so the
  // table times disagreed with the chart and looked like they stopped early.
  const formatTimeLabel = (value) => {
    if (isCategorical) return String(value)
    if (!value) return ''
    try {
      return new Date(value).toLocaleString('en-US', {
        timeZone: customerTz,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
      })
    } catch {
      return new Date(value).toLocaleString()
    }
  }

  // ── Statistics (per parameter): count, min, max, avg, median, stddev ────────
  const statistics = useMemo(() => {
    if (!graphData.length || !selectedParameters.length) return []
    return selectedParameters.map((param, idx) => {
      const key = `p_${param.id}`
      const values = graphData.map(d => d[key]).filter(v => v != null && !isNaN(v))
      if (!values.length) return { param, idx, count: 0, name: seriesMetadata[param.id]?.name || param.name, unit: seriesMetadata[param.id]?.unit || '' }
      const count = values.length
      const sum   = values.reduce((a, b) => a + b, 0)
      const avg   = sum / count
      const min   = Math.min(...values)
      const max   = Math.max(...values)
      const sorted = [...values].sort((a, b) => a - b)
      const median = count % 2 === 0 ? (sorted[count/2-1] + sorted[count/2]) / 2 : sorted[Math.floor(count/2)]
      const stddev = Math.sqrt(values.reduce((a, b) => a + (b - avg) ** 2, 0) / count)
      return {
        param, idx, count,
        avg: +avg.toFixed(4), min: +min.toFixed(4), max: +max.toFixed(4),
        median: +median.toFixed(4), stddev: +stddev.toFixed(4),
        name: seriesMetadata[param.id]?.name || param.name,
        unit: seriesMetadata[param.id]?.unit || '',
      }
    })
  }, [graphData, selectedParameters, seriesMetadata])

  // Parameters that have at least one non-null value in the current window.
  // Used to skip Y-axes and legend entries for series with no data so that
  // phantom Y-axis gutters and empty legend labels don't appear.
  const parametersWithData = useMemo(() => {
    if (!graphData.length) return []
    return selectedParameters.filter(param => {
      const key = `p_${param.id}`
      return graphData.some(d => d[key] != null && !isNaN(d[key]))
    })
  }, [graphData, selectedParameters])

  // ── Download helpers ─────────────────────────────────────────────────────────

  // Append anchor to DOM before click — required in modern Chrome/Firefox
  const _triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }

  // Build header + data rows for CSV / XLS
  const _buildRows = () => {
    const hdr = ['"Time"', ...selectedParameters.map(p => {
      const m = seriesMetadata[p.id] || {}
      const name = (m.name || p.name).replace(/"/g, '""')
      const unit = m.unit ? ` (${m.unit})` : ''
      return `"${name}${unit}"`
    })]
    // Only include rows that have at least one real value
    const body = graphData
      .filter(pt => selectedParameters.some(p => pt[`p_${p.id}`] != null))
      .map(pt => {
        const t = `"${formatTimeLabel(pt.x)}"`
        const vals = selectedParameters.map(p => {
          const v = pt[`p_${p.id}`]
          const prec = seriesMetadata[p.id]?.precision ?? 0
          return v != null ? Number(v).toFixed(prec) : ''
        })
        return [t, ...vals]
      })
    return [hdr, ...body]
  }

  const downloadCSV = () => {
    setShowMenu(false)
    try {
      const rows = _buildRows()
      // rows[0] = header; if no data rows, still allow download (header only)
      const csv = rows.map(r => r.join(',')).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
        `${widget.title || 'data'}.csv`
      )
    } catch (e) { console.error('[CSV]', e) }
  }

  const downloadXLS = () => {
    setShowMenu(false)
    try {
      const rows = _buildRows()
      const tsv = rows.map(r =>
        r.map(c => String(c).replace(/^"|"$/g, '').replace(/""/g, '"')).join('\t')
      ).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
        `${widget.title || 'data'}.xls`
      )
    } catch (e) { console.error('[XLS]', e) }
  }

  const downloadPNG = () => {
    setShowMenu(false)
    // Chart always renders inline (data table + statistics are modals now),
    // so the SVG is always present in the DOM — no view-mode juggling needed.
    setTimeout(() => {
      try {
        const svgEl = chartContainerRef.current?.querySelector('svg')
        if (!svgEl) { console.warn('[PNG] No SVG found'); return }

        // Clone + set explicit pixel dimensions (width="100%" breaks canvas draw)
        const rect = svgEl.getBoundingClientRect()
        const W = Math.round(rect.width)  || 800
        const H = Math.round(rect.height) || 400
        const clone = svgEl.cloneNode(true)
        clone.setAttribute('width', W)
        clone.setAttribute('height', H)
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

        // Use Blob URL (avoids encodeURIComponent size limit)
        const svgBlob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' })
        const svgUrl  = URL.createObjectURL(svgBlob)

        const canvas = document.createElement('canvas')
        canvas.width  = W * 2   // 2× for retina
        canvas.height = H * 2
        const ctx = canvas.getContext('2d')
        ctx.scale(2, 2)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, W, H)

        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, 0, 0, W, H)
          URL.revokeObjectURL(svgUrl)
          canvas.toBlob(b => {
            if (b) _triggerDownload(b, `${widget.title || 'chart'}.png`)
          }, 'image/png')
        }
        img.onerror = (err) => {
          URL.revokeObjectURL(svgUrl)
          console.error('[PNG] Image load failed', err)
        }
        img.src = svgUrl
      } catch (e) { console.error('[PNG]', e) }
    }, 80)
  }

  // ─── Print-window helper ───────────────────────────────────────────────────
  // Opens a child window, writes the supplied HTML body, and triggers the
  // browser's print dialog. The same primitive backs Print Chart, Print Data
  // Table, and Print Statistics — the caller supplies the body markup.
  const _openPrintWindowWithBody = (bodyHTML, { closeAfter, titleSuffix = '' } = {}) => {
    setShowMenu(false)
    // Must be synchronous from click — popup blockers allow this
    const win = window.open('', '_blank', 'width=1024,height=768,menubar=no,toolbar=no')
    if (!win) { alert('Pop-up blocked. Please allow pop-ups for this site and try again.'); return }

    const baseTitle = widget.title || 'Chart'
    const title     = titleSuffix ? `${baseTitle} — ${titleSuffix}` : baseTitle
    const subtitle  = `Period: ${fromDate || '—'} → ${toDate || '—'}`

    win.document.open()
    win.document.write(`<!DOCTYPE html>
<html><head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 24px; font-family: Arial, sans-serif; background: #fff; color: #222; }
    h2 { margin: 0 0 4px; font-size: 18px; }
    p.sub { margin: 0 0 16px; font-size: 12px; color: #888; }
    svg { max-width: 100%; height: auto; display: block; }
    table { border-collapse: collapse; width: 100%; font-size: 11px; }
    th, td { border: 1px solid #d0d0d0; padding: 6px 8px; text-align: left; vertical-align: top; }
    thead th { background: #f3f4f6; font-weight: 600; }
    tbody tr:nth-child(even) td { background: #fafafa; }
    .col-head { font-weight: 600; }
    .col-head-sub { color: #888; font-weight: 400; font-size: 10px; }
    @media print { body { margin: 12px; } }
  </style>
</head><body>
  <h2>${title}</h2>
  <p class="sub">${subtitle}</p>
  ${bodyHTML}
</body></html>`)
    win.document.close()

    // Trigger print after content is fully loaded
    win.addEventListener('load', () => {
      win.focus()
      win.print()
      if (closeAfter) setTimeout(() => win.close(), 500)
    })
    // Fallback: some browsers fire load before document.close
    setTimeout(() => { try { win.focus(); win.print() } catch (_) {} }, 800)
  }

  // Build the chart SVG markup for printing (used by Print Chart / Download
  // PDF on the chart-context menu).
  const _buildChartHtml = () => {
    const svgEl = chartContainerRef.current?.querySelector('svg')
    if (!svgEl) {
      return '<p style="color:#888">Chart not available.</p>'
    }
    const clone = svgEl.cloneNode(true)
    const rect  = svgEl.getBoundingClientRect()
    clone.setAttribute('width',  Math.round(rect.width)  || 900)
    clone.setAttribute('height', Math.round(rect.height) || 420)
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    return clone.outerHTML
  }

  // Escape a string for safe inclusion in a print-window HTML body.
  const _esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))

  // Build the data-table HTML for printing — mirrors the structured
  // Site / Device / Sensor / Parameter header the modal renders.
  const _buildDataTableHtml = () => {
    const cols = _buildDataTableColumns()
    const headerCells = cols.map(col =>
      `<th>${col.headerLines.map((l, i) =>
        `<div class="${i === 0 ? 'col-head' : 'col-head-sub'}">${_esc(l)}</div>`
      ).join('')}</th>`
    ).join('')
    const rows = graphData.map(pt => {
      const cells = cols.map(col => {
        const v = col.accessor(pt)
        return `<td>${v == null || v === '' ? '—' : _esc((+v).toFixed(col.precision ?? 0))}</td>`
      }).join('')
      return `<tr><td>${_esc(formatTimeLabel(pt.x))}</td>${cells}</tr>`
    }).join('')
    return `
<table>
  <thead><tr><th>Timestamp</th>${headerCells}</tr></thead>
  <tbody>${rows || `<tr><td colspan="${cols.length + 1}" style="text-align:center;color:#888">No data</td></tr>`}</tbody>
</table>`
  }

  // Build the statistics HTML for printing.
  const _buildStatsHtml = () => {
    const headers = ['Parameter', 'Unit', 'Count', 'Min', 'Max', 'Average', 'Median', 'Std Dev']
    const rows = statistics.map(s => `
      <tr>
        <td>${_esc(s.name)}</td>
        <td>${_esc(s.unit || '—')}</td>
        <td>${_esc(s.count?.toLocaleString() ?? '—')}</td>
        <td>${_esc(s.min ?? '—')}</td>
        <td>${_esc(s.max ?? '—')}</td>
        <td>${_esc(s.avg ?? '—')}</td>
        <td>${_esc(s.median ?? '—')}</td>
        <td>${_esc(s.stddev ?? '—')}</td>
      </tr>`).join('')
    return `
<table>
  <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
  <tbody>${rows || `<tr><td colspan="${headers.length}" style="text-align:center;color:#888">No statistics</td></tr>`}</tbody>
</table>`
  }

  // ─── Chart-context exports (header menu) ──────────────────────────────────
  const downloadPDF = () => _openPrintWindowWithBody(_buildChartHtml(), { closeAfter: true })
  const printChart  = () => _openPrintWindowWithBody(_buildChartHtml(), { closeAfter: false })

  // ─── Data-Table-context exports (modal menu) ─────────────────────────────
  const downloadDataTablePDF = () =>
    _openPrintWindowWithBody(_buildDataTableHtml(), { closeAfter: true,  titleSuffix: 'Data Table' })
  const printDataTable        = () =>
    _openPrintWindowWithBody(_buildDataTableHtml(), { closeAfter: false, titleSuffix: 'Data Table' })

  // ─── Statistics-context exports (modal menu) ─────────────────────────────
  const downloadStatsPDF = () =>
    _openPrintWindowWithBody(_buildStatsHtml(), { closeAfter: true,  titleSuffix: 'Statistics' })
  const printStatistics  = () =>
    _openPrintWindowWithBody(_buildStatsHtml(), { closeAfter: false, titleSuffix: 'Statistics' })

  // Stats-row CSV / XLS — schema differs from time-series rows above.
  const _buildStatsRows = () => {
    const hdr = ['Parameter', 'Unit', 'Count', 'Min', 'Max', 'Average', 'Median', 'Std Dev']
    const body = statistics.map(s => [
      s.name, s.unit || '', s.count ?? '', s.min ?? '', s.max ?? '', s.avg ?? '', s.median ?? '', s.stddev ?? '',
    ])
    return [hdr, ...body]
  }
  const downloadStatsCSV = () => {
    try {
      const csv = _buildStatsRows().map(r =>
        r.map(c => /[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c).join(',')
      ).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
        `${widget.title || 'statistics'}-statistics.csv`
      )
    } catch (e) { console.error('[StatsCSV]', e) }
  }
  const downloadStatsXLS = () => {
    try {
      const tsv = _buildStatsRows().map(r => r.join('\t')).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
        `${widget.title || 'statistics'}-statistics.xls`
      )
    } catch (e) { console.error('[StatsXLS]', e) }
  }

  // ─── Structured column descriptor (Site → Device → Sensor → Param/Unit) ──
  // Used by both the modal table view and the data-table print export.
  const _buildDataTableColumns = () => selectedParameters.map((p) => {
    const m = seriesMetadata[p.id] || {}
    const headerLines = [
      // Line 1 (bold) — site name when known, otherwise parameter name as fallback
      m.siteName || p.siteName || p.site_name || (m.name || p.name),
      // Optional context lines — device UID and sensor name when available
      p.deviceUid || p.device_uid || null,
      p.sensorName || p.sensor_name || null,
      // Parameter (Unit) — always the last line
      `${m.name || p.name}${m.unit ? ` (${m.unit})` : ''}`,
    ].filter(Boolean)
    return {
      paramId: p.id,
      headerLines,
      precision: m.precision ?? 0,
      accessor: (row) => row[`p_${p.id}`],
    }
  })

  return (
    <>
      <XDCard
        title={widget.title}
        subtitle={widget.description}
        headerRight={
          <div className="main-dwmy flex items-center gap-2 flex-wrap">
            {/* D / W / M / Y period buttons */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded px-1 py-0.5 border border-gray-200">
              {['D', 'W', 'M', 'Y'].map(p => (
                <button
                  key={p}
                  onClick={() => handlePeriodButtonClick(p)}
                  className={`common-size15 px-3 py-2 text-xs font-semibold rounded transition ${
                    activePeriod === p ? 'bg-[#888888] text-white' : 'text-[#888888] hover:bg-gray-200'
                  }`}
                >{p}</button>
              ))}
            </div>

            <div className="line-chart-mb">
              {/* Start Date */}
              <div className="flex flex-col" style={{ minWidth: 90 }}>
                <span className="text-[9px] text-gray-400  leading-none mb-0.5">Start Date</span>
                <DateTimePicker
                  value={dateRange.from}
                  defaultTime="00:00:00"
                  ariaLabel="Start Date"
                  onChange={(val) => setDateRange?.(prev => ({ ...prev, from: val }))}
                />
              </div>

              {/* End Date */}
              <div className="flex flex-col" style={{ minWidth: 90 }}>
                <span className="text-[9px] text-gray-400  leading-none mb-0.5">End Date</span>
                <DateTimePicker
                  value={dateRange.to}
                  defaultTime="23:59:59"
                  ariaLabel="End Date"
                  onChange={(val) => setDateRange?.(prev => ({ ...prev, to: val }))}
                />
              </div>

              {/* Three-dot menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(v => !v)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
                  title="More options"
                >
                  <svg width="16" height="16" viewBox="0 0 4 16" fill="#4D4D4D">
                    <circle cx="2" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/>
                  </svg>
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] py-3 text-[13px] text-gray-700">
                    {/* ── Detail popups ── */}
                    <button
                      className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15"
                      onClick={() => { setTableModalOpen(true); setShowMenu(false) }}
                    >
                      View Data Table
                    </button>
                    <button
                      className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15"
                      onClick={() => { setStatsModalOpen(true); setShowMenu(false) }}
                    >
                      View Statistics
                    </button>

                    <div className="my-1.5 border-t border-gray-100"/>

                    {/* ── Downloads (chart context) ── */}
                    <button className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15" onClick={downloadPDF}>Download PDF</button>
                    <button className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15" onClick={downloadPNG}>Download PNG Image</button>
                    <button className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15" onClick={downloadCSV}>Download CSV</button>
                    <button className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15" onClick={downloadXLS}>Download XLS</button>

                    <div className="my-1.5 border-t border-gray-100"/>

                    <button className="w-full text-left px-6 py-1 hover:bg-gray-50 transition text-[#4D4D4D] common-size15" onClick={printChart}>Print Line Chart</button>
                  </div>
                )}
              </div>

              {/* Pencil / edit icon — hidden while in Customize mode */}
              {isCustomizing && canEdit && (
                <button
                  onClick={() => setShowEditor(true)}
                  className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                  title="Edit graph settings"
                >
                  <EditIcon />
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* ── Large-dataset warning banner ── */}
        {dataWarning && (
          <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">⚠️</span>
            <span className="flex-1">{dataWarning}</span>
            <button className="shrink-0 text-amber-400 hover:text-amber-600 font-bold" onClick={() => setDataWarning(null)}>✕</button>
          </div>
        )}

        {/* ── Loading ── */}
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          </div>

        ) : selectedParameters.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400 px-4 text-center">
            Select parameters from the left panel to view sensor data
          </div>

        ) : graphData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data available for this period</div>

        /* ── Chart (always rendered inline; Data Table & Statistics are modals).
           Each parameter gets its own hidden Y axis so different scales
           (heading 0–360 vs temp ~20 vs outliers near -100k) don't collapse
           the smaller series. Only the first parameter's axis is rendered as
           a reference; the others are hidden but still active for scaling. ── */
        ) : (
          <div ref={chartContainerRef} className="relative select-none" style={{ width: '100%', height: 420 }}>
            <ResponsiveContainer width="100%" height={420}>
              <ComposedChart
                data={graphData}
                margin={{ top: 10, right: 20, left: 0, bottom: rotateAxis ? 60 : 40 }}
                // Drag-to-zoom: mouse down → record start, mouse move
                // (with start set) → live-track end so the ReferenceArea
                // grows under the cursor, mouse up → commit the range.
                // We deliberately AVOID setting end on every mouseMove
                // because Recharts also fires mouseMove on hover (no
                // mouse button), which would lay down a tiny selection
                // every time the user just hovered the chart.
                onMouseDown={(e) => {
                  if (e?.activeLabel == null) return
                  setZoomDragStart(e.activeLabel)
                  setZoomDragEnd(e.activeLabel)
                }}
                onMouseMove={(e) => {
                  if (zoomDragStart == null || e?.activeLabel == null) return
                  setZoomDragEnd(e.activeLabel)
                }}
                onMouseUp={() => {
                  if (zoomDragStart == null || zoomDragEnd == null) {
                    setZoomDragStart(null); setZoomDragEnd(null); return
                  }
                  // Treat a click-without-drag (same start + end) as a
                  // no-op so the user doesn't accidentally zoom to a
                  // zero-width slice.
                  if (zoomDragStart === zoomDragEnd) {
                    setZoomDragStart(null); setZoomDragEnd(null); return
                  }
                  // Order the bounds so the smaller value is always left.
                  const cmp = (a, b) => (typeof a === 'number' && typeof b === 'number')
                    ? a - b
                    : String(a).localeCompare(String(b))
                  const lo = cmp(zoomDragStart, zoomDragEnd) <= 0 ? zoomDragStart : zoomDragEnd
                  const hi = cmp(zoomDragStart, zoomDragEnd) <= 0 ? zoomDragEnd   : zoomDragStart
                  setZoomDomain([lo, hi])
                  setZoomDragStart(null); setZoomDragEnd(null)
                }}
              >
                {/* vertical={false}: draw only the horizontal reference lines.
                    The vertical grid lines run the full height of the plot and,
                    on a light-gray dashed stroke, read as faint "ghost" vertical
                    lines in the chart area — the exact artifact users flagged
                    after the hover-cursor line was removed. Horizontal lines are
                    enough for reading values off the axes. */}
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={(activePeriod === 'M' || activePeriod === 'Y') ? '#c7d2da' : '#f0f0f0'} />
                <XAxis
                  dataKey="x"
                  type={isCategorical ? 'category' : 'number'}
                  {...(!isCategorical ? {
                    scale: 'time',
                    // D-view: explicit [now − 24h, now] domain in UTC ms so
                    // the axis ALWAYS spans the full rolling window, even
                    // when the device first reported partway through it.
                    // This is what kills the leading whitespace bug.
                    // A drag-to-zoom selection (`zoomDomain`) wins over the
                    // rolling-window default so the user actually sees the
                    // narrower range they just selected.
                    domain: zoomDomain
                      ? zoomDomain
                      : dWindowMs ? [dWindowMs.startMs, dWindowMs.endMs]
                      : yWindowMs ? [yWindowMs.startMs, yWindowMs.endMs]
                      : ['dataMin', 'dataMax'],
                    allowDataOverflow: !!zoomDomain || !!yWindowMs,
                    // Suppress the fixed D-view 2-hour ticks while zoomed so
                    // Recharts can place sensible ticks inside the chosen
                    // sub-range; otherwise the only label might be the one
                    // 2-hour tick that happens to fall in the window.
                    ...((dTicks || wTicks || mTicks || xMonthTicks) && !zoomDomain
                      ? { ticks: dTicks || wTicks || mTicks || xMonthTicks }
                      : {}),
                  } : {
                    // Categorical (W/M/Y): pass the zoom bounds as an
                    // explicit [lo, hi] tuple too. Recharts category axes
                    // accept this and clip the rendered domain.
                    ...(zoomDomain ? { domain: zoomDomain, allowDataOverflow: true } : {}),
                  })}
                  tickFormatter={isCategorical ? undefined : formatXAxisTick}
                  tick={{ fontSize: 11, fill: '#888' }}
                  angle={rotateAxis ? -45 : 0}
                  textAnchor={rotateAxis ? 'end' : 'middle'}
                  height={rotateAxis ? 70 : 40}
                  // interval 0 draws every explicit tick we supply; only the
                  // periods without their own tick list fall back to Recharts
                  // choosing positions. While drag-zoomed there ARE no explicit
                  // ticks (they're suppressed above), so interval:0 would draw a
                  // tick at EVERY data point — an unreadable band. Let Recharts
                  // thin them (preserveStartEnd + minTickGap) whenever zoomed.
                  interval={zoomDomain ? 'preserveStartEnd' : ((activePeriod === 'D' || activePeriod === 'W' || activePeriod === 'M' || xMonthTicks) ? 0 : 'preserveStartEnd')}
                  minTickGap={zoomDomain ? 50 : ((activePeriod === 'D' || activePeriod === 'W' || activePeriod === 'M' || xMonthTicks) ? 0 : 50)}
                />
                {/* Per-parameter Y axes — one for EVERY selected parameter,
                    whether or not it has data in the current window, so the
                    axis (and its scale) stays visible even when a sensor is
                    briefly offline. Axes alternate left/right and are tinted
                    with the line colour so each axis matches its trace. */}
                {axisGroups.map((group, gIdx) => {
                  // One axis per group. Settings/colour come from the first
                  // member; a group with several members gets a neutral tint
                  // because no single trace colour would be honest for it.
                  const { param, idx } = group.members[0]
                  const shared = group.members.length > 1
                  const ss = graphSettings.series?.[param.id] || {}
                  // Y-axis bounds: explicit chart-editor Min/Max wins; otherwise
                  // AUTO-SCALE to the data in view. We deliberately do NOT pin the
                  // axis to the parameter's Sensor min/max here — that range is
                  // often far wider than the actual readings (e.g. sensor 70–100°F
                  // while the day only moves 72–80°F), which flattens real
                  // variation into a near-straight line and makes the chart look
                  // like it disagrees with the data table. Sensor min/max still
                  // filters out-of-range readings server-side; it just no longer
                  // dictates the visible scale.
                  const yMin = (ss.yMin !== undefined && ss.yMin !== null && ss.yMin !== '') ? Number(ss.yMin) : 'auto'
                  const yMax = (ss.yMax !== undefined && ss.yMax !== null && ss.yMax !== '') ? Number(ss.yMax) : 'auto'
                  // Steps = number of tick marks. Fewer than 2 is degenerate:
                  // a single tick can't span a range, and the exact-tick formula
                  // divides by (steps − 1), so steps=1 → division by zero → a
                  // [NaN] tick array that made the whole Y-axis disappear. Treat
                  // anything < 2 as "no steps" so it falls back to evenly-spaced
                  // ticks instead of vanishing.
                  const yStepsRaw = ss.ySteps ? Number(ss.ySteps) : undefined
                  const ySteps = (yStepsRaw && yStepsRaw >= 2) ? yStepsRaw : undefined
                  const hasMin = yMin !== 'auto'
                  const hasMax = yMax !== 'auto'
                  // When min + max + steps are all set, compute exact tick positions.
                  // ySteps is the number of tick marks (not intervals), so use it directly.
                  const exactTicks = (ySteps && hasMin && hasMax)
                    ? Array.from({ length: ySteps }, (_, i) => +(yMin + i * (yMax - yMin) / (ySteps - 1)).toFixed(4))
                    : undefined
                  // No Steps entered → build evenly-spaced ticks ourselves so the
                  // axis never shows ugly uneven auto-ticks (e.g. 70, 85, 112).
                  // Nice-round from the data range when a bound is auto; evenly
                  // divide the explicit range when the user pinned min AND max.
                  const meta = seriesMetadata[param.id] || {}
                  // Scale must cover every member, not just the first — and only
                  // the points in the current (zoomed) X-window, so the Y-axis
                  // recalculates when the user zooms into a portion of the graph.
                  const seriesVals = group.members.flatMap(({ param: mp }) =>
                    yScaleData.map(d => d[`p_${mp.id}`]).filter(v => v != null && !isNaN(v)))
                  const hasSeriesData = seriesVals.length > 0
                  let evenTicks, evenDomain
                  if (!ySteps && !exactTicks) {
                    // Per-unit hints (UnitLib): minimum span + preferred tick
                    // count. A null/blank hint falls back to plain autoscale /
                    // the default 6 ticks, so nothing changes for units without
                    // hints configured.
                    // While zoomed, ignore the per-unit MINIMUM span so the axis
                    // tightens to the zoomed-in data (span = data range + 20%);
                    // at full view the unit's min span still applies.
                    const spanHint = zoomDomain ? 0 : (Number.isFinite(meta.yaxisSpan) ? meta.yaxisSpan : 0)
                    const stepHint = (Number.isFinite(meta.yaxisStep) && meta.yaxisStep >= 2)
                      ? Math.round(meta.yaxisStep) : 6
                    const dataLo = hasSeriesData ? Math.min(...seriesVals) : undefined
                    const dataHi = hasSeriesData ? Math.max(...seriesVals) : undefined
                    if (hasMin && hasMax) {
                      // Both pinned in the chart editor: evenly divide the range.
                      if (yMax > yMin) {
                        evenTicks = Array.from({ length: stepHint }, (_, i) =>
                          +(yMin + i * (yMax - yMin) / (stepHint - 1)).toFixed(4))
                      }
                    } else if (!hasMin && !hasMax) {
                      // Full autoscale: widen to the unit's minimum span (+20%
                      // headroom), centre the data, then snap to nice ticks.
                      if (dataLo != null && dataHi != null) {
                        const win = autoscaleSpan(dataLo, dataHi, spanHint)
                        if (win) {
                          const t = niceTicks(win[0], win[1], stepHint)
                          if (t) { evenTicks = t; evenDomain = [t[0], t[t.length - 1]] }
                          else { evenDomain = win }
                        }
                      }
                    } else {
                      // One bound pinned: keep the pin, autoscale the other from
                      // the data (no span-centring — it would fight the pin).
                      const lo = hasMin ? yMin : dataLo
                      const hi = hasMax ? yMax : dataHi
                      if (lo != null && hi != null && hi > lo) {
                        const t = niceTicks(lo, hi, stepHint)
                        if (t) { evenTicks = t; evenDomain = [t[0], t[t.length - 1]] }
                      }
                    }
                  }
                  // A parameter with no readings in this window gives Recharts
                  // nothing to derive a scale from, so the axis would reserve its
                  // gutter and draw absolutely nothing — an empty margin beside
                  // the plot, with no unit label either. Fall back to the
                  // parameter's configured sensor range so the axis, its unit and
                  // a readable scale still show. With no sensor range recorded
                  // either, a plain 0–1 keeps the labelled axis visible.
                  if (!hasSeriesData && !exactTicks && !evenTicks) {
                    const lo = Number.isFinite(meta.ymin) ? meta.ymin : 0
                    const hi = Number.isFinite(meta.ymax) && meta.ymax > lo ? meta.ymax : lo + 1
                    const t = niceTicks(lo, hi, 6)
                    if (t) { evenTicks = t; evenDomain = [t[0], t[t.length - 1]] }
                    else { evenDomain = [lo, hi] }
                  }
                  const memberColor = ss.color || COLORS[idx % COLORS.length]
                  // A shared axis serves traces of different colours, so tint it
                  // neutrally rather than pick one of them arbitrarily.
                  const lineColor = shared ? '#6B7280' : memberColor
                  // Keep the axis while ANY of its series is visible; it only
                  // gives up its gutter once every member is hidden.
                  const isHidden = group.members.every(({ param: mp }) => hiddenParamIds.has(String(mp.id)))
                  // Alternate sides by the GROUP's ordinal (0→left, 1→right, …),
                  // NOT the first member's selection index — otherwise multi-member
                  // groups (or any gap in selected indices) can give two groups the
                  // same parity and land both axes on the same side.
                  const axisSide = gIdx % 2 === 0 ? 'left' : 'right'
                  return (
                    <YAxis
                      key={group.axisId}
                      yAxisId={group.axisId}
                      hide={isHidden}
                      orientation={axisSide}
                      tick={{ fontSize: 11, fill: lineColor }}
                      tickLine={{ stroke: lineColor }}
                      axisLine={{ stroke: lineColor }}
                      // 58 (was 55) so a 4-digit scale like 997–1002 and the
                      // rotated unit label both fit without touching.
                      width={58}
                      allowDataOverflow={hasMin || hasMax || !!evenDomain}
                      domain={evenDomain ? evenDomain : [yMin, yMax]}
                      {...(exactTicks ? { ticks: exactTicks } : evenTicks ? { ticks: evenTicks } : ySteps ? { tickCount: ySteps } : {})}
                      // The unit label sat hard against the inner edge of its
                      // band with no offset, which left it only ~13px from the
                      // NEIGHBOURING axis's tick numbers — so with two or more
                      // axes the labels looked jammed between the scales. An
                      // offset moves each label clear of the previous axis's
                      // numbers (~22px), and textAnchor:middle centres the
                      // rotated text along the axis instead of anchoring it at
                      // one end. Matches how ReportView/ChartPopup place theirs.
                      label={meta.unit ? {
                        value: meta.unit,
                        angle: -90,
                        position: axisSide === 'left' ? 'insideLeft' : 'insideRight',
                        offset: 14,
                        style: { fontSize: 10, fill: lineColor, fontWeight: 600, textAnchor: 'middle' },
                      } : undefined}
                    />
                  )
                })}
                <Tooltip
                  // A single vertical reference line follows the cursor to mark
                  // the hovered x. This used to leave "ghost" trails, but the
                  // real culprit was the tooltip's slide animation (fixed just
                  // below with isAnimationActive/transition:none) — with that
                  // off, the line repaints cleanly, so the reference is back.
                  cursor={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '4 4' }}
                  // The Recharts tooltip wrapper defaults to
                  // `transition: transform 0.4s`, so the tooltip SLIDES to each
                  // new position as the mouse moves. That slow slide is what
                  // leaves faint gray ghost trails across the plot when moving
                  // slowly — a GPU compositing artifact that shows on screen
                  // but NOT in screen recordings. Disabling the animation +
                  // forcing transition:none makes the tooltip snap instantly,
                  // which removes the trail.
                  isAnimationActive={false}
                  wrapperStyle={{ transition: 'none', zIndex: 50 }}
                  // Keep the box INSIDE the chart: Recharts flips it to the left
                  // of the cursor near the right edge (and right otherwise) and
                  // clamps it vertically, so it never overflows the card, never
                  // gets clipped, and never forces a horizontal scrollbar. (An
                  // earlier allowEscapeViewBox:{x:true} let it spill off the right
                  // edge — the full label already fits via the box's max-content
                  // width, so escaping the viewBox isn't needed.)
                  allowEscapeViewBox={{ x: false, y: false }}
                  // Shared XD "data hover box" design (see ChartHoverTooltip).
                  content={(p) => (
                    <ChartHoverTooltip
                      {...p}
                      formatLabel={formatTooltipLabel}
                      describe={(entry) => {
                        const id = String(entry?.dataKey || '').replace('p_', '')
                        const m = seriesMetadata[id] || {}
                        const v = typeof entry?.value === 'number' ? entry.value.toFixed(m.precision ?? 0) : (entry?.value ?? '-')
                        // XD hover-box label: "<device> | <sensor> | <param> (<unit>)".
                        // Fall back gracefully when a part is missing.
                        const paramWithUnit = `${m.name || id}${m.unit ? ` (${m.unit})` : ''}`
                        const label = [m.deviceName, m.sensorName, paramWithUnit]
                          .filter(Boolean).join(' | ')
                        return {
                          name: label,
                          valueText: `${v}${m.unit ? ` ${m.unit}` : ''}`,
                          color: entry?.color || entry?.stroke,
                        }
                      }}
                    />
                  )}
                />
                {/* Hovering a legend entry highlights that parameter's line
                    (thicker, full opacity) and dims the rest. The payload item
                    carries the Line's dataKey ("p_<id>"); strip the prefix to
                    get the parameter id. */}
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8, cursor: 'pointer' }}
                  // Click toggles a series' visibility; struck-through + greyed
                  // means hidden. Hover still highlights (unless it's hidden).
                  formatter={(key, entry) => {
                    const id = String(entry?.dataKey || key).replace('p_', '')
                    const name = seriesMetadata[id]?.name || key
                    const hidden = hiddenParamIds.has(id)
                    return (
                      <span style={hidden ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
                        {name}
                      </span>
                    )
                  }}
                  onClick={(o) => {
                    const dk = o?.dataKey || o?.value
                    if (dk) toggleParamHidden(String(dk).replace('p_', ''))
                  }}
                  onMouseEnter={(o) => {
                    const dk = o?.dataKey || o?.value
                    const id = dk ? String(dk).replace('p_', '') : null
                    if (id && !hiddenParamIds.has(id)) setHoveredParamId(id)
                  }}
                  onMouseLeave={() => setHoveredParamId(null)}
                />
                {selectedParameters.map((param, idx) => {
                  const ss = graphSettings.series?.[param.id] || {}
                  const lineColor = ss.color || COLORS[idx % COLORS.length]
                  const isHidden = hiddenParamIds.has(String(param.id))
                  const isHovered = hoveredParamId === String(param.id)
                  const dimmed = hoveredParamId != null && !isHovered
                  return (
                    <Line
                      key={param.id}
                      yAxisId={axisIdFor.get(String(param.id))}
                      hide={isHidden}
                      type="linear"
                      dataKey={`p_${param.id}`}
                      stroke={lineColor}
                      strokeWidth={isHovered ? 4 : 2}
                      strokeOpacity={dimmed ? 0.2 : 1}
                      // D view: draw the point shape at every reading. W/M/Y:
                      // no static dots (too dense) — the shape appears only on
                      // hover via activeDot.
                      //
                      // Dimmed series drop their dots entirely. strokeOpacity
                      // only fades the LINE; the dots are separate SVG shapes
                      // that kept full opacity, so highlighting one parameter
                      // left every other parameter's markers sitting at full
                      // strength over the plot. activeDot goes too, so moving
                      // the cursor across the chart can't pop a marker back on
                      // a series that is meant to be faded out.
                      dot={dimmed ? false
                        : (activePeriod === 'D' ? makeDot(ss.pointShape || 'Circle', lineColor) : false)}
                      activeDot={dimmed ? false : makeDot(ss.pointShape || 'Circle', lineColor)}
                      isAnimationActive={false}
                      // Break the line at null gaps (hidden-status readings come
                      // back from graph-data as y:null) instead of bridging them.
                      connectNulls={false}
                      name={`p_${param.id}`}
                    />
                  )
                })}
                {/* Live drag-to-zoom selection — light-blue tinted band that
                    tracks the cursor between mouse-down and mouse-up. */}
                {zoomDragStart != null && zoomDragEnd != null && zoomDragStart !== zoomDragEnd && (
                  <ReferenceArea
                    yAxisId={axisGroups[0]?.axisId ?? ''}
                    x1={zoomDragStart}
                    x2={zoomDragEnd}
                    strokeOpacity={0.3}
                    fill="#358ECD"
                    fillOpacity={0.15}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>

            {/* Reset Zoom — visible only while a zoom range is active.
                Floats over the top-right corner of the chart so it doesn't
                shift the chart layout in or out of the zoomed state. */}
            {zoomDomain && (
              <button
                type="button"
                onClick={() => { setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null) }}
                className="absolute top-2 right-2 px-2.5 py-1 text-xs font-semibold text-[#358ECD] bg-white border border-[#358ECD] rounded shadow-sm hover:bg-[#E8F1F8] transition"
              >
                Reset Zoom
              </button>
            )}
          </div>
        )}
      </XDCard>

      {/* Graph Editor Modal */}
      <GraphEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={newSettings => { setGraphSettings(newSettings); lsSetScoped('wq_graph', projectId, widget.id, newSettings) }}
        selectedParameters={selectedParameters}
        seriesMetadata={seriesMetadata}
        settings={graphSettings}
        widget={widget}
      />

      {/* Data Table popup — opened from the chart's `⋮` menu */}
      <DataTableModal
        open={tableModalOpen}
        onClose={() => setTableModalOpen(false)}
        title={`${widget.title || 'Data Table'} — Data Table`}
        data={graphData}
        columns={_buildDataTableColumns()}
        formatTimestamp={formatTimeLabel}
        decimals={2}
        downloadPDF={downloadDataTablePDF}
        downloadPNG={downloadPNG}
        downloadCSV={downloadCSV}
        downloadXLS={downloadXLS}
        printDataTable={printDataTable}
      />

      {/* Statistics popup — opened from the chart's `⋮` menu */}
      <StatisticsModal
        open={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        title={`${widget.title || 'Statistics'} — Statistics`}
        statistics={statistics}
        seriesColor={(i) => {
          const param = selectedParameters[i]
          return graphSettings.series?.[param?.id]?.color || COLORS[i % COLORS.length]
        }}
        downloadPDF={downloadStatsPDF}
        downloadPNG={downloadPNG}
        downloadCSV={downloadStatsCSV}
        downloadXLS={downloadStatsXLS}
        printStatistics={printStatistics}
      />
    </>
  )
}

/**
 * Environmental News Widget
 *
 * Fetches the 5 most recent posts from the Fondriest WordPress news site:
 *   GET https://www.fondriest.com/news/wp-json/fondriest/v1/posts/recent?limit=5
 *
 * Response shape (single envelope): { posts: [...], total, totalPages, offset, limit }
 * Each post: { postId, postTitle, postURL, postExcerpt, postDate, postDateRaw,
 *              catId, catName, authorName, authorURL, thumbSmall, thumbFull }
 *
 * Falls back to a fixed placeholder list if the request fails (offline, CORS,
 * upstream down) so the widget never renders blank on the Overview.
 */
// Proxied through our own backend to avoid the cross-origin block
// the browser raises against fondriest.com. Backend caches the
// upstream response for 10 minutes.
const _ENV_NEWS_API_URL = import.meta.env.VITE_API_URL || ''
const _ENV_NEWS_BASE    = import.meta.env.VITE_API_BASE_PATH || '/api'
const ENV_NEWS_API_URL = `${_ENV_NEWS_API_URL}${_ENV_NEWS_BASE}/dashboard/environmental-news/?limit=5`
const ENV_NEWS_VIEW_ALL_URL = 'https://www.fondriest.com/news/'

export function EnvironmentalNewsWidget({ widget, data }) {
  const initial = data?.articles
  const [articles, setArticles] = useState(initial || [])
  const [loading, setLoading]   = useState(!initial)

  useEffect(() => {
    // If the widget was hydrated server-side via `data.articles`, skip the
    // fetch — otherwise pull straight from the WordPress JSON endpoint.
    if (initial) return
    let cancelled = false
    setLoading(true)
    fetch(ENV_NEWS_API_URL)
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then((body) => {
        if (cancelled) return
        const posts = Array.isArray(body?.posts) ? body.posts : []
        setArticles(posts.map((p) => ({
          id:       p.postId,
          title:    p.postTitle,
          url:      p.postURL,
          excerpt:  p.postExcerpt,
          date:     p.postDate,
          dateRaw:  p.postDateRaw,
          category: p.catName,
          author:   p.authorName,
          thumb:    p.thumbSmall || p.thumbFull || null,
        })))
      })
      .catch(() => {
        if (!cancelled) {
          // Keep the placeholder list visible if the API is unreachable so the
          // widget never shows an empty card on the Overview.
          setArticles([
            { id: 'ph-1', title: 'From Source to Sea: Monitoring and Protecting…', url: ENV_NEWS_VIEW_ALL_URL },
            { id: 'ph-2', title: 'From Source to Sea: Monitoring and Protecting…', url: ENV_NEWS_VIEW_ALL_URL },
            { id: 'ph-3', title: 'Using Traditional Ghanaian Fishing Canoes to Monitor…', url: ENV_NEWS_VIEW_ALL_URL },
          ])
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [initial])

  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <a
          href={ENV_NEWS_VIEW_ALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="common-size15 text-[#4D4D4D] flex items-center gap-2 text-sm bg-[#F1F2F4] py-1.5 px-4 rounded-lg hover:bg-gray-300 transition"
        >
          <ExternalLinkIcon /> View All
        </a>
      }
    >
      <div className="divide-y divide-gray-100 px-6">
        {loading && articles.length === 0 && (
          <div className="py-6 flex items-center justify-center gap-2 text-xs text-gray-500">
            <Loader className="w-4 h-4 animate-spin" /> Loading latest news…
          </div>
        )}
        {!loading && articles.length === 0 && (
          <div className="py-6 text-center text-xs text-gray-500">No recent posts.</div>
        )}
        {articles.map((a, i) => (
          <div key={a.id ?? i} className="py-4">
            <p className="common-size15 text-base font-semibold text-[#4D4D4D] line-clamp-2">{a.title}</p>
            {a.date && (
              <p className="common-size15 text-[#AFAFAF] mt-0.5">
                {a.date}{a.category ? ` · ${a.category}` : ''}
              </p>
            )}
            <a
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="common-size15 text-[#888888] text-sm mt-1 hover:underline inline-block"
            >
              Read Article
            </a>
          </div>
        ))}
      </div>
    </XDCard>
  )
}

/**
 * NexSens Website Banner — full-width promo shown at the bottom of every default
 * dashboard (matches the XD). The NEXSENS logo + sensor line-art are decorative;
 * the "VISIT NEXSENS" button links out to nexsens.com.
 */
export function NexSensBanner() {
  return (
    <div className="bg-white nxl rounded-lg shadow-sm mt-4 px-4 md:px-6 py-4 flex flex-col md:flex-row items-center gap-4">
      <img src="/images/Layer_1.png" alt="NexSens Technology" className="shrink-0 logo-nx" />
      {/* Sensor line-art strip — decorative, desktop only. */}
      <img src="/images/nexsensad_v2.png" alt="" aria-hidden="true" className="hidden lg:block flex-1 accs-img object-contain opacity-90" />
      <div className="find-sup flex flex-col sm:flex-row items-center gap-3 md:ml-auto shrink-0 text-center sm:text-left">
        <p className="text-sm text-[#4D4D4D] font-medium sm:max-w-[240px]">Find Supplies, Sensors, and Systems at nexsens.com</p>
        <a
          href="https://www.nexsens.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-[#F1F2F4] hover:bg-gray-300 text-[#4D4D4D] font-semibold text-sm py-2.5 px-5 rounded-lg transition whitespace-nowrap"
        >
          VISIT NEXSENS <ExternalLinkIcon />
        </a>
      </div>
    </div>
  )
}

/**
 * Event Monitor Widget
 */
export function EventMonitorWidget({ widget, data }) {
  // Delegate to the same EventMonitorPanel the System Performance dashboard
  // uses, so the Overview shows real alarms instead of the hard-coded
  // (Low Power / Low Dissolved Oxygen / High Turbidity) fallback. The
  // panel renders its own card, header, Filter dropdown, inline edit mode,
  // and settings modal — we just wire the project-scoped data through.
  const { projectId } = useParams()
  const [alarms, setAlarms] = useState(Array.isArray(data?.events) ? data.events : [])
  const [loading, setLoading] = useState(false)
  const [sites, setSites] = useState([])

  const fetchAlarms = useCallback(() => {
    if (!projectId) return
    setLoading(true)
    alarmApi.getAlarms({ project: projectId })
      .then((res) => setAlarms(
        Array.isArray(res.data) ? res.data : (res.data?.results || [])
      ))
      .catch(() => setAlarms([]))
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(() => {
    fetchAlarms()
  }, [fetchAlarms])

  useEffect(() => {
    if (!projectId) return
    projectsApi.fetchProjectSites(Number(projectId))
      .then((res) => setSites(Array.isArray(res) ? res : []))
      .catch(() => setSites([]))
  }, [projectId])

  return (
    <EventMonitorPanel
      alarms={alarms}
      loading={loading}
      onChanged={fetchAlarms}
      sites={sites}
      projectId={projectId}
    />
  )
}

/**
 * Support / Donation Widget — editable sponsor grid.
 *
 *   - Donate Now link at top (URL configured via editor)
 *   - 2-column grid of sponsor logos, each linking to its domain
 *   - Edit pencil opens SupportProjectEditorModal (add / remove / drag-reorder
 *     sponsors; each sponsor has a logo URL or uploaded image + domain link)
 *   - Persists to localStorage `wq_support_${widget.id}` (matches every
 *     other configurable widget in this file)
 */
export function SupportProjectWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true } = useDashboardContext()

  // Project-scoped key — see scopedLsKey doc-block. Without this every
  // project sharing the Overview template would render the same sponsor
  // list because widget.id collides across projects.
  const lsValue = lsGetScoped('wq_support', projectId, widget.id, null)
  const [donateUrl, setDonateUrl] = useState(lsValue?.donateUrl ?? data?.donateUrl ?? '')
  const [sponsors, setSponsors] = useState(lsValue?.sponsors ?? data?.sponsors ?? [])
  const [showEditor, setShowEditor] = useState(false)
  // Re-read shared widget config once priming completes so every
  // viewer sees the owner's sponsor list without a manual save.
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    const v = lsGetScoped('wq_support', projectId, widget.id, null)
    if (v?.donateUrl != null) setDonateUrl(v.donateUrl)
    if (Array.isArray(v?.sponsors)) setSponsors(v.sponsors)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])

  const handleSave = ({ donateUrl: url, sponsors: list }) => {
    setDonateUrl(url)
    setSponsors(list)
    lsSetScoped('wq_support', projectId, widget.id, { donateUrl: url, sponsors: list })
  }

  return (
    <>
      <XDCard
        title={widget.title || 'Support Our Project'}
        subtitle={widget.description}
        headerRight={isCustomizing && canEdit ? (
          <button
            onClick={() => setShowEditor(true)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit sponsors"
          >
            <EditIcon />
          </button>
        ) : null}
      >
        <div className="p-4 flex flex-col gap-3 h-full">
          {donateUrl && (
            <a
              href={donateUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[#0866AB]  text-sm underline hover:text-[#054b80]"
            >
              Donate Now
            </a>
          )}

          {sponsors.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-6 text-gray-400 gap-2">
              <span className="text-2xl">🤝</span>
              <p className="text-xs">No sponsors yet — click the edit (✎) icon to add some.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 auto-rows-min">
              {sponsors.map((s, idx) => {
                const Inner = (
                  <div className="flex items-center justify-center bg-white border border-gray-100 rounded p-3 hover:border-gray-300 transition" style={{ minHeight: 96 }}>
                    <img
                      src={s.logo}
                      alt={s.name || `Sponsor ${idx + 1}`}
                      className="max-h-20 max-w-full object-contain"
                      onError={(e) => { e.target.style.opacity = 0.3 }}
                    />
                  </div>
                )
                return s.link ? (
                  <a
                    key={s.id || idx}
                    href={s.link}
                    target="_blank"
                    rel="noreferrer"
                    title={s.name}
                  >
                    {Inner}
                  </a>
                ) : (
                  <div key={s.id || idx} title={s.name}>{Inner}</div>
                )
              })}
            </div>
          )}
        </div>
      </XDCard>

      <SupportProjectEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        initialDonateUrl={donateUrl}
        initialSponsors={sponsors}
      />
    </>
  )
}

/**
 * Disclaimer Widget — editable rich-text panel.
 *
 * Mirrors the Project Description pattern: clicking the edit pencil
 * calls the shared `onConfigure` handler which opens WidgetConfigModal
 * with a rich-text editor. Saved content is stored on the widget's
 * `rich_content` field and rendered as HTML below.
 */
const DEFAULT_DISCLAIMER_TEXT = 'Uncertainty and potential for error can be associated with environmental monitoring data. Data users are cautioned to consider carefully the provisional nature of the information before using it for decisions that concern personal or public safety or the conduct of business that involves substantial monetary or operational consequences. No warranty, express or implied, is given as to the accuracy, reliability, utility or completeness of the data hosted on this datacenter.'

export function DisclaimerWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false, canEdit = true } = useDashboardContext()
  const html = widget.custom_rich_content || widget.rich_content || ''
  const fallbackText = data?.text || DEFAULT_DISCLAIMER_TEXT
  return (
    <XDCard
      title={widget.custom_title || widget.title || 'Disclaimer'}
      headerRight={isCustomizing && canEdit && onConfigure ? (
        <button
          onClick={() => onConfigure(widget)}
          className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
          title="Edit disclaimer"
        >
          <EditIcon />
        </button>
      ) : null}
    >
      <div className="p-6 text-gray-600 text-sm leading-relaxed">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p>{fallbackText}</p>
        )}
      </div>
    </XDCard>
  )
}

/**
 * Message the Project Administrator — XD "Message the Project Administrator".
 *
 * Free-text message plus the sender's own address, emailed to the project's
 * administrators. Recipients are resolved server-side from the project's
 * admin list (see app_dashboard/message_admin_views.py), so nothing here
 * chooses or reveals a destination address; the address typed in "Reply To"
 * only becomes the email's Reply-To header.
 *
 * Works for anonymous public-portal visitors too — the backend allows that
 * only when the project's Public Portal is enabled, and rate-limits every
 * caller. `Reply To` is prefilled from the signed-in account when there is
 * one, since that is nearly always the address the sender wants.
 */
/**
 * Config modal for the Message-the-Administrator widget: a display name and
 * the "Forward Message To" address. The address is enforced server-side on
 * send (see message_admin_views._configured_forward_to) — this only edits it.
 */
export function ChangeMessageAdminModal({ open, onClose, onSave, initial = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [forwardTo, setForwardTo]   = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    if (open) {
      setWidgetName(initial?.widgetName || '')
      setForwardTo(initial?.forwardTo || '')
      setErr('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const save = () => {
    const addr = forwardTo.trim()
    // Optional, but if given it must be a plausible single email address.
    if (addr && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      setErr('Please enter a valid email address.')
      return
    }
    onSave({ widgetName: widgetName.trim(), forwardTo: addr })
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#4D4D4D]">Contact Us</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="common-size15 block text-sm font-bold text-[#4D4D4D] mb-1">
              Widget Name <span className="font-normal text-gray-400 text-xs">(Leave Blank For Default)</span>
            </label>
            <input
              type="text"
              value={widgetName}
              onChange={(e) => setWidgetName(e.target.value)}
              placeholder="Message the Project Administrator"
              className="w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm text-[#4D4D4D] placeholder-[#D6D6D6] focus:outline-none focus:border-[#358ECD]"
            />
          </div>
          <div>
            <label className="common-size15 block text-sm font-bold text-[#4D4D4D] mb-1">Forward Message To</label>
            <input
              type="email"
              value={forwardTo}
              onChange={(e) => { setForwardTo(e.target.value); if (err) setErr('') }}
              placeholder="Input Email"
              maxLength={254}
              className="w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm text-[#4D4D4D] placeholder-[#D6D6D6] focus:outline-none focus:border-[#358ECD]"
            />
            {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
          </div>
        </div>
        <div className="px-6 pb-5">
          <button
            type="button"
            onClick={save}
            className="savebtn w-full py-2.5 text-sm font-bold text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition uppercase tracking-wide"
          >
            Save
          </button>
          <div className="text-center mt-2">
            <button type="button" onClick={onClose} className="cancelbtn text-sm text-[#358ECD] hover:underline">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

export function MessageAdminWidget({ widget }) {
  const { isCustomizing = false, canEdit = true, projectId } = useDashboardContext()
  const accountEmail = useAuthStore((s) => s.user?.email) || ''

  // PROJECT-LEVEL config (shared across viewers via ProjectWidgetConfig):
  //   { widgetName, forwardTo }. `forwardTo` is where the message is emailed —
  //   read + enforced server-side on send (see message_admin_views).
  //
  // Keyed under the reserved widget_id=0 slot, NOT widget.id: message_admin is
  // one-per-project, and a freshly-placed widget's id is a non-integer string
  // ("wnew_<ts>") that the integer widget_id column / server lookup can't use.
  const MSG_WIDGET_ID = 0
  const [config, setConfig] = useState(() => lsGetScoped('wq_message', projectId, MSG_WIDGET_ID, {}) || {})
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setConfig(lsGetScoped('wq_message', projectId, MSG_WIDGET_ID, {}) || {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId])
  const [showEditor, setShowEditor] = useState(false)

  const [message, setMessage] = useState('')
  const [replyTo, setReplyTo] = useState(accountEmail)
  const [status, setStatus]   = useState('idle')   // idle | sending | sent | error
  const [error, setError]     = useState(null)

  // The account email arrives after the widget first renders on a cold
  // load, so adopt it then — but never overwrite what the user has typed.
  useEffect(() => {
    if (accountEmail) setReplyTo((prev) => (prev ? prev : accountEmail))
  }, [accountEmail])

  const tooLong  = message.length > MAX_MESSAGE_CHARS
  const canSend  = !!message.trim() && !!replyTo.trim() && !tooLong && status !== 'sending'

  const onSend = async () => {
    if (!canSend) return
    setStatus('sending'); setError(null)
    try {
      await sendMessageToAdmin({ projectId, message: message.trim(), replyTo: replyTo.trim() })
      setMessage('')
      setStatus('sent')
    } catch (e) {
      setError(e?.message || 'Could not send your message right now.')
      setStatus('error')
    }
  }

  return (
    <>
    <XDCard
      title={config.widgetName || widget.custom_title || widget.title || 'Message the Project Administrator'}
      headerRight={isCustomizing && canEdit ? (
        <button
          onClick={() => setShowEditor(true)}
          className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
          title="Edit widget"
        >
          <EditIcon />
        </button>
      ) : null}
    >
      <div className="p-4 h-full flex flex-col gap-3">
        <textarea
          value={message}
          onChange={(e) => { setMessage(e.target.value); if (status !== 'idle') setStatus('idle') }}
          placeholder="Write message here..."
          disabled={status === 'sending'}
          className="flex-1 min-h-[120px] w-full resize-none border border-gray-300 rounded px-3 py-2
                     text-sm text-gray-700 placeholder-gray-400 focus:outline-none
                     focus:border-[#358ECD] disabled:bg-gray-50"
        />

        {status === 'sent' && (
          <p className="text-xs text-green-600">
            Message sent to the project administrator.
          </p>
        )}
        {status === 'error' && error && (
          <p className="text-xs text-red-600">{error}</p>
        )}
        {tooLong && (
          <p className="text-xs text-red-600">
            Message is too long ({message.length.toLocaleString()} of {MAX_MESSAGE_CHARS.toLocaleString()} characters).
          </p>
        )}

        <div className="flex items-center gap-3 shrink-0">
          <input
            type="email"
            value={replyTo}
            onChange={(e) => { setReplyTo(e.target.value); if (status !== 'idle') setStatus('idle') }}
            placeholder="Reply To: Email"
            disabled={status === 'sending'}
            maxLength={254}
            className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm
                       text-gray-700 placeholder-gray-400 focus:outline-none
                       focus:border-[#358ECD] disabled:bg-gray-50"
          />
          <button
            onClick={onSend}
            disabled={!canSend}
            className="shrink-0 px-4 py-1.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm
                       rounded transition disabled:opacity-50 disabled:cursor-not-allowed
                       inline-flex items-center gap-2"
          >
            {status === 'sending' && <Loader className="w-3.5 h-3.5 animate-spin" />}
            {status === 'sending' ? 'Sending…' : 'Send Message'}
          </button>
        </div>
      </div>
    </XDCard>
    <ChangeMessageAdminModal
      open={showEditor}
      onClose={() => setShowEditor(false)}
      initial={config}
      onSave={(next) => { setConfig(next); lsSetScoped('wq_message', projectId, MSG_WIDGET_ID, next); setShowEditor(false) }}
    />
    </>
  )
}

// ─── Knowledge Base — fallback demo list + helper modals ────────────────────
// Fallback shown when the backend is unreachable (e.g. gunicorn restart in
// progress) or hasn't run the KB migration yet. Mirrors the legacy XD demo
// articles so the widget never renders empty / broken on first load.
const KB_FALLBACK_ARTICLES = [
  { id: 'demo-1', title: 'X3 User Guide',            summary: '' },
  { id: 'demo-2', title: 'EWC Integration Guide',    summary: '' },
  { id: 'demo-3', title: 'XB-200 User Guide',        summary: '' },
  { id: 'demo-4', title: 'NX260 Integration Guide',  summary: '' },
  { id: 'demo-5', title: 'XB-200 Getting Started Video', summary: '' },
  { id: 'demo-6', title: 'X3 FAQ',                   summary: '' },
]

/**
 * Full-article detail modal. Loads the body via kbApi.getArticle when a
 * numeric backend id is passed; for fallback (string-id) entries it just
 * shows a stub message explaining the backend isn't connected.
 */
function KnowledgeBaseDetailModal({ open, articleId, fallbackTitle, onClose }) {
  const [loading, setLoading] = useState(false)
  const [article, setArticle] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !articleId) return
    // Demo / fallback ids start with "demo-" — never hit the API for those.
    if (typeof articleId === 'string' && articleId.startsWith('demo-')) {
      setArticle({ title: fallbackTitle || 'Article', body: null })
      return
    }
    setLoading(true); setError(null); setArticle(null)
    kbApi.getArticle(articleId)
      .then((res) => setArticle(res.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'Article not found.' : 'Could not load article.'))
      .finally(() => setLoading(false))
  }, [open, articleId, fallbackTitle])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8 mt-8" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl rounded shadow-lg mx-4 mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-[#4D4D4D] truncate pr-4">
            {article?.title || fallbackTitle || 'Article'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700" aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader className="w-4 h-4 animate-spin" /> Loading article…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 py-8 text-center">{error}</div>
          )}
          {!loading && !error && article && (
            <>
              {article.summary && (
                <p className="text-sm text-gray-500 italic mb-4">{article.summary}</p>
              )}
              {article.body ? (
                // Knowledge Base bodies are admin-authored; trusted source.
                <div
                  className="prose prose-sm max-w-none text-[#4D4D4D]"
                  dangerouslySetInnerHTML={{ __html: article.body }}
                />
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Full article content not available yet. Add it in the
                  Django admin under Knowledge Base Articles.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * View-all modal — search-filtered list of every active article. Each row
 * has a Read Article action that hands off to the detail modal.
 */
function KnowledgeBaseViewAllModal({ open, articles, loading, onOpenArticle, onClose }) {
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return articles
    return articles.filter((a) =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.summary || '').toLowerCase().includes(q)
    )
  }, [articles, query])

  if (!open) return null
  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8 px-4 mt-8" onClick={onClose}>
      <div className="bg-white z-[9999] w-full max-w-3xl rounded shadow-lg mx-8 mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="break-mb flex items-center justify-between px-6 py-4 border-b">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">All Knowledge Base Articles</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Articles"
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded w-64 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:bg-white"
              />
            </div>
            <button type="button" onClick={onClose} className="cross-bt p-1 text-gray-500 hover:text-gray-700" aria-label="Close">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader className="w-4 h-4 animate-spin" /> Loading articles…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              {query ? `No articles match "${query}".` : 'No articles available.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((a) => (
                <div key={a.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="common-size18 text-sm font-semibold text-[#4D4D4D] truncate">{a.title}</div>
                    {a.summary && (
                      <div className="common-size15 text-[#4D4D4D] truncate">{a.summary}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenArticle(a)}
                    className="common-size15 text-xs text-[#358ECD] font-semibold hover:underline  flex-shrink-0"
                  >
                    Read Article
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * Knowledge Base Widget
 *
 * Fetches active articles from /api/dashboard/knowledge-base/ on mount.
 * Falls back to a hardcoded demo list when the API is unreachable so the
 * dashboard never renders an empty widget (e.g. during a gunicorn restart
 * or before the KB migration has been applied on a fresh environment).
 *
 *   • Compact list inline (up to MAX_PREVIEW rows; "View All" link below
 *     when there are more articles than fit).
 *   • Read Article → KnowledgeBaseDetailModal with the full HTML body.
 *   • View All → KnowledgeBaseViewAllModal with search + scrollable list.
 *
 * Article authoring happens in the Django admin under
 * Knowledge Base Articles (admin → app_dashboard → Knowledge Base Articles).
 */
export function KnowledgeBaseWidget({ widget, data }) {
  const MAX_PREVIEW = 6
  const [articles, setArticles] = useState(() => data?.articles || [])
  const [loading, setLoading]   = useState(false)
  const [usingFallback, setUsingFallback] = useState(false)
  const [viewAllOpen, setViewAllOpen]     = useState(false)
  const [detailFor, setDetailFor]         = useState(null) // {id, title} | null

  // Initial load — fetch active articles from the backend. On any failure
  // (network down, 401, 404, 5xx, or the migration not yet applied), fall
  // back to the demo list so the widget still renders something useful.
  useEffect(() => {
    if (data?.articles?.length) return  // server-rendered data wins
    let cancelled = false
    setLoading(true)
    kbApi.listArticles()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || [])
        if (list.length > 0) {
          setArticles(list)
          setUsingFallback(false)
        } else {
          // Empty list → still treat as success but render the fallback so
          // the customer sees something familiar. Admins can clear the
          // fallback by adding their own articles.
          setArticles(KB_FALLBACK_ARTICLES)
          setUsingFallback(true)
        }
      })
      .catch(() => {
        if (cancelled) return
        setArticles(KB_FALLBACK_ARTICLES)
        setUsingFallback(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // Refetch when the widget instance changes (different dashboards); the
  // article set is otherwise static across re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget?.id])

  const preview = articles.slice(0, MAX_PREVIEW)
  const hasMore = articles.length > MAX_PREVIEW

  return (
    <XDCard
      title={widget.title || 'Knowledge Base'}
      subtitle={widget.description}
      headerRight={
        <button
          type="button"
          onClick={() => setViewAllOpen(true)}
          className="text-[#4D4D4D] flex items-center gap-2  text-sm bg-gray-200 hover:bg-gray-300 py-1.5 px-4 rounded-lg transition"
        >
          <ExternalLinkIcon /> View All
        </button>
      }
    >
      <div className="px-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {preview.map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-4">
                <span className="text-sm text-[#4D4D4D] truncate">{a.title}</span>
                <button
                  type="button"
                  onClick={() => setDetailFor({ id: a.id, title: a.title })}
                  className="text-xs text-gray-500 hover:text-[#358ECD]  flex-shrink-0"
                >
                  Read Article
                </button>
              </div>
            ))}
            {hasMore && (
              <button
                type="button"
                onClick={() => setViewAllOpen(true)}
                className="block w-full text-[11px] text-[#358ECD] hover:underline text-center py-2"
              >
                + {articles.length - MAX_PREVIEW} more — view all
              </button>
            )}
          </div>
        )}
      </div>

      <KnowledgeBaseViewAllModal
        open={viewAllOpen}
        articles={articles}
        loading={loading}
        onOpenArticle={(a) => { setViewAllOpen(false); setDetailFor({ id: a.id, title: a.title }) }}
        onClose={() => setViewAllOpen(false)}
      />
      <KnowledgeBaseDetailModal
        open={!!detailFor}
        articleId={detailFor?.id}
        fallbackTitle={detailFor?.title}
        onClose={() => setDetailFor(null)}
      />
    </XDCard>
  )
}

// Generic XD "data hover box" renderer for the secondary chart widgets
// (Bar / Area / Smooth Line / Combo / Column / Stacked Column). Their series
// carry no metadata map, so we read name/value/colour straight off the
// Recharts payload entry. Pie-family widgets keep the default single-value tip.
function renderXdTooltip(p) {
  return (
    <ChartHoverTooltip
      {...p}
      formatLabel={(l) => (l == null ? '' : String(l))}
      describe={(entry) => ({
        name: entry?.name || entry?.dataKey || '',
        valueText: entry?.value == null ? '-' : String(entry.value),
        color: entry?.color || entry?.stroke || entry?.fill || entry?.payload?.fill,
      })}
    />
  )
}

/**
 * Bar Chart Widget
 */
export function BarChartWidget({ widget, data }) {
  const hasData = data?.series?.length > 0
  const mock = [
    { name: 'Jan', v: 12 }, { name: 'Feb', v: 19 }, { name: 'Mar', v: 8 },
    { name: 'Apr', v: 24 }, { name: 'May', v: 16 }, { name: 'Jun', v: 21 },
  ]
  const series = hasData ? data.series : mock
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D','W','M','Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs  rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} />
            <YAxis tick={{ fontSize: 11, fill: '#888' }} />
            <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
            {hasData && data.bars?.length
              ? data.bars.map((bar, i) => <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} radius={[3,3,0,0]} />)
              : <Bar dataKey="v" fill="#358ECD" radius={[3,3,0,0]} />
            }
          </BarChart>
        </ResponsiveContainer>
      </div>
    </XDCard>
  )
}

/**
 * Area Chart Widget
 */
export function AreaChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
          {data.areas && data.areas.map((area, i) => (
            <Area key={i} type="linear" dataKey={area.key} fill={COLORS[i % COLORS.length]} stroke={COLORS[i % COLORS.length]} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Pie Chart Widget
 */
export function PieChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Data Table Widget
 */
export function DataTableWidget({ widget, data }) {
  if (!data || !data.rows) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  const columns = data.columns || Object.keys(data.rows[0] || {})

  return (
    <Card className="p-6 overflow-x-auto">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th key={col} className="text-left py-2 px-4 font-semibold text-gray-700">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, idx) => (
            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
              {columns.map((col) => (
                <td key={`${idx}-${col}`} className="py-2 px-4 text-gray-600">
                  {row[col]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

/**
 * Map View Widget
 */
export function MapViewWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
        <p className="text-gray-500">Map visualization placeholder</p>
      </div>
    </Card>
  )
}

/**
 * Live Data Widget
 */
export function LiveDataWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="space-y-3">
        {data?.values?.map((value, idx) => (
          <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-gray-700">{value.label}</span>
            <span className="font-bold text-lg text-blue-600">{value.reading}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Site Conditions Widget
 */
export function SiteConditionsWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="grid grid-cols-2 gap-4">
        {data?.conditions?.map((condition, idx) => (
          <div key={idx} className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
            <div className="text-xs font-semibold text-gray-600 mb-1">{condition.label}</div>
            <div className="text-2xl font-bold text-green-600">{condition.value}</div>
            <div className="text-xs text-gray-500 mt-1">{condition.unit}</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Video Feed Widget
 */
export function VideoFeedWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="bg-gray-900 rounded-lg h-64 flex items-center justify-center">
        {data?.url ? (
          <video src={data.url} controls className="w-full h-full rounded-lg" />
        ) : (
          <p className="text-gray-400">No video feed available</p>
        )}
      </div>
    </Card>
  )
}

/**
 * Performance Metric Widget
 */
export function PerformanceMetricWidget({ widget, data }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-50'
      case 'warning': return 'text-yellow-600 bg-yellow-50'
      case 'critical': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  return (
    <Card className={`p-6 ${getStatusColor(data?.status)}`}>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{widget.title}</h3>
      <div className="text-3xl font-bold">{data?.value || '—'}</div>
      {data?.description && (
        <p className="text-xs text-gray-600 mt-2">{data.description}</p>
      )}
    </Card>
  )
}

// ============= ADDITIONAL CHART WIDGETS =============

/**
 * Smooth Curve Line Chart Widget
 */
export function SmoothCurveLineChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
          {data.lines && data.lines.map((line, i) => (
            <Line key={i} type="natural" dataKey={line.key} stroke={COLORS[i % COLORS.length]} isAnimationActive={true} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Combo Chart Widget (Line + Bar)
 */
export function ComboChartWidget({ widget, data }) {
  const hasData = data?.series?.length > 0
  const mock = [
    { name: 'Jan', a: 14, b: 9 }, { name: 'Feb', a: 18, b: 12 },
    { name: 'Mar', a: 11, b: 16 }, { name: 'Apr', a: 22, b: 14 },
    { name: 'May', a: 17, b: 20 }, { name: 'Jun', a: 25, b: 18 },
  ]
  const series = hasData ? data.series : mock
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D','W','M','Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs  rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} />
            <YAxis tick={{ fontSize: 11, fill: '#888' }} />
            <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
            {hasData ? (
              <>
                {data.bars?.map((bar, i) => <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} />)}
                {data.lines?.map((line, i) => <Line key={i} type="linear" dataKey={line.key} stroke={COLORS[(i+2) % COLORS.length]} strokeWidth={2} />)}
              </>
            ) : (
              <>
                <Bar dataKey="a" fill="#358ECD" opacity={0.8} />
                <Line type="linear" dataKey="b" stroke="#10B981" strokeWidth={2} dot={false} />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </XDCard>
  )
}

/**
 * Multi Chart Widget — small-multiples variant of the comparison chart.
 *
 * Same toolbar shape as Combo Chart but renders one sub-chart per parameter,
 * each with a single trace, stacked vertically. Useful when the selected
 * parameters have different units / scales where overlaying them on a single
 * chart would be misleading.
 *
 * Data shape (when available):
 *   data.series = [
 *     { title: 'Pond Buoy | NX Temperature (C)', color: '#f59e0b',
 *       points: [{ x: <time>, y: <value> }, ...] },
 *     ...
 *   ]
 * If no data is provided, renders 3 sample sub-charts for preview.
 */
export function MultiChartWidget({ widget, data }) {
  const hasData = Array.isArray(data?.series) && data.series.length > 0

  // Mock preview series — matches the layout shown in the design screenshot.
  const mockBase = [3.1, 2.7, 2.4, 2.1, 1.9, 1.7, 1.5, 1.6, 1.8, 1.4, 1.7, 1.2, 0.9]
  const mockPoints = (offset) =>
    mockBase.map((v, i) => ({ x: `T${i + 1}`, y: Number((v + offset).toFixed(2)) }))
  const mock = [
    { title: 'Pond Buoy | NX Temperature (C)',        color: '#f59e0b', points: mockPoints(3) },
    { title: 'Weather Station | Air Temperature (F)', color: '#10b981', points: mockPoints(3) },
    { title: 'Weather Station | Wind Speed (mph)',    color: '#8b5cf6', points: mockPoints(3) },
  ]
  const charts = hasData ? data.series : mock

  return (
    <XDCard
      title={widget.title || 'Graph'}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D', 'W', 'M', 'Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs  rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4 space-y-5">
        {charts.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">
            Select one or more parameters to view them here.
          </div>
        ) : charts.map((c, i) => (
          <div key={i}>
            <div className="text-sm font-semibold text-gray-700 mb-1">{c.title}</div>
            <ResponsiveContainer width="100%" height={130}>
              <LineChart
                data={c.points || []}
                margin={{ top: 5, right: 12, left: 0, bottom: 5 }}
                // Every stacked chart in this widget shares a sync group, so
                // hovering one shows the same instant on all of them. Synced by
                // x VALUE because the series can differ in sample count.
                syncId={`multichart-${widget?.id ?? 'default'}`}
                syncMethod="value"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} width={40} />
                <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
                <Line
                  // Straight point-to-point — no smoothing (see the note in
                  // SingleParameterChart).
                  type="linear"
                  dataKey="y"
                  stroke={c.color || COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </XDCard>
  )
}

/**
 * Column Chart Widget
 */
export function ColumnChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
          {data.bars && data.bars.map((bar, i) => (
            <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Stacked Column Chart Widget
 */
export function StackedColumnChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
          {data.bars && data.bars.map((bar, i) => (
            <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} stackId="stack" />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Doughnut Chart Widget
 */
export function DoughnutChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={80} outerRadius={120} label>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * 3D Pie Chart Widget (visual representation)
 */
export function Pie3DChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, value }) => `${name}: ${value}`}>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={renderXdTooltip} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

// ============= GAUGE AND DIAL WIDGETS =============

/**
 * Wind Rose Widget — renders a real polar histogram from a wind-direction
 * + wind-speed parameter pair, with the configured colour palette. Falls
 * back to a "please configure" empty state if the user hasn't picked
 * parameters yet, and to an error state if the backend rejects the
 * request.
 *
 * Settings continue to persist to localStorage, matching every other
 * widget in this file. Moving widget config off localStorage is a
 * cross-widget refactor and is out of scope here.
 */
export function WindRoseWidget({ widget }) {
  const { projectId } = useParams()
  const {
    isCustomizing = false, canEdit = true,
    dateRange,
    sites: contextSites,
  } = useDashboardContext()
  // Project-scoped key — see scopedLsKey doc-block at the top of this file.
  const [showEditor, setShowEditor] = useState(false)
  const [windRoseSettings, setWindRoseSettings] = useState(
    () => lsGetScoped('wq_windrose', projectId, widget.id, {})
  )
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setWindRoseSettings(lsGetScoped('wq_windrose', projectId, widget.id, {}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [chartData, setChartData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSaveWindRose = (settings) => {
    setWindRoseSettings(settings)
    lsSetScoped('wq_windrose', projectId, widget.id, settings)
  }

  // Auto-default on first mount when no parameters have been configured.
  // Matches the spec: "Parameter selection defaults to wind speed and
  // wind direction of the first logger which has wind speed and wind
  // direction parameters." We persist the detected pair so the next
  // mount doesn't re-detect (cheap idempotency) and the editor opens
  // pre-filled.
  useEffect(() => {
    if (!projectId) return
    if (windRoseSettings.windDirectionParam && windRoseSettings.windSpeedParam) return
    // Prefer sites already loaded into the DashboardContext (public
    // portal path — anonymous visitors can't hit the authed
    // /project/<id>/sites/ endpoint). Only fall back to the authed
    // fetch when the context has nothing yet.
    const applyDefaults = (siteList) => {
      const defaults = detectWindDefaults(Array.isArray(siteList) ? siteList : [])
      if (!defaults) return
      const next = {
        ...windRoseSettings,
        selectedDevice:     String(defaults.deviceId),
        windSpeedParam:     String(defaults.speedParamId),
        windDirectionParam: String(defaults.directionParamId),
      }
      setWindRoseSettings(next)
      lsSetScoped('wq_windrose', projectId, widget.id, next)
    }
    if (Array.isArray(contextSites) && contextSites.length > 0) {
      applyDefaults(contextSites)
      return
    }
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => { if (!cancelled) applyDefaults(sites) })
      .catch(() => { /* silent — widget will keep showing "select parameters" empty state */ })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, widget.id, contextSites])

  const directionParam = windRoseSettings.windDirectionParam
  const speedParam     = windRoseSettings.windSpeedParam
  const configured     = Boolean(directionParam && speedParam)

  // Fetch the polar histogram whenever the user picks (or changes) the
  // direction/speed parameter pair or the dashboard date range moves.
  useEffect(() => {
    if (!configured) {
      setChartData(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    projectsApi.fetchWindRoseData(directionParam, speedParam, {
      fromDate: dateRange?.from,
      toDate:   dateRange?.to,
    })
      .then((data) => { if (!cancelled) setChartData(data) })
      .catch((err) => {
        if (cancelled) return
        const msg = err?.response?.data?.message || 'Could not load wind rose data.'
        setError(msg)
        setChartData(null)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [configured, directionParam, speedParam, dateRange?.from, dateRange?.to])

  // Map the configured-or-default keyColors map back to the band-ordered
  // array WindRoseChart wants.
  const bandColors = WIND_SPEED_BANDS.map(
    (b) => (windRoseSettings.keyColors || {})[b.key] || b.defaultColor
  )
  const bandLabels = WIND_SPEED_BANDS.map((b) => b.label)

  return (
    <>
      <XDCard
        title={windRoseSettings.widgetName || widget.title}
        subtitle={widget.description}
        headerRight={
          isCustomizing && canEdit ? (
            <button
              onClick={() => setShowEditor(true)}
              className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
              title="Edit wind rose settings"
            >
              <EditIcon />
            </button>
          ) : null
        }
      >
        <div className="flex flex-col items-center justify-center p-4 gap-3">
          {/* Empty / loading / error / chart */}
          {!configured ? (
            <div className="h-64 flex flex-col items-center justify-center text-center text-sm text-gray-500 px-6">
              <div className="text-3xl mb-2">🌹</div>
              <p>Select a device and pick the wind-direction and wind-speed parameters in the editor to render the rose.</p>
            </div>
          ) : loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
            </div>
          ) : error ? (
            <div className="h-64 flex items-center justify-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-4">
              {error}
            </div>
          ) : chartData && chartData.total_paired_samples === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              No paired wind observations in this period.
            </div>
          ) : chartData ? (
            <>
              <WindRoseChart
                sectors={chartData.sectors}
                bandColors={bandColors}
                bandLabels={bandLabels}
                size={300}
                fromDate={chartData.from_date}
                toDate={chartData.to_date}
              />
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600 px-2">
                {WIND_SPEED_BANDS.map((b, i) => (
                  <span key={b.key} className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                      style={{ backgroundColor: bandColors[i] }}
                    />
                    {b.label}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-gray-400">
                {chartData.total_paired_samples.toLocaleString()} paired observations
                {chartData.dominant_sector ? ` · dominant: ${chartData.dominant_sector}` : ''}
              </p>
            </>
          ) : null}
        </div>
      </XDCard>

      <WindRoseEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveWindRose}
        projectId={projectId}
        initialSettings={windRoseSettings}
      />
    </>
  )
}

// ─── Gauge Series Editor Modal ────────────────────────────────────────────────
// XD reference (customer-shared screenshot): header "Gauge Series Editor"
// with Save / Cancel buttons in the top-right; body has Widget Name,
// Parameter Selection (Device + single Parameter dropdowns), and
// Min / Max / Steps numeric fields.
export function GaugeSeriesEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState('')
  const [selectedParameter, setSelectedParameter] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [stepsValue, setStepsValue] = useState('')
  const [loading, setLoading] = useState(false)

  // Reset whenever the modal opens (always re-reads the latest saved settings)
  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    setSelectedDevice(initialSettings.selectedDevice || '')
    setSelectedParameter(initialSettings.selectedParameter || '')
    setMinValue(initialSettings.min ?? '')
    setMaxValue(initialSettings.max ?? '')
    setStepsValue(initialSettings.steps ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Escape closes
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Fetch project sites when modal opens
  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  // Flatten all devices across sites
  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => {
        devs.push({ ...dev, siteName: site.name || site.station_name || '' })
      })
    })
    return devs
  }, [sites])

  // Parameters of the selected device — flattened across sensors
  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    const dev = allDevices.find((d) => String(d.id) === String(selectedDevice))
    return deviceFlatParams(dev)
  }, [selectedDevice, allDevices])

  if (!open) return null

  const handleSave = () => {
    onSave({
      widgetName: widgetName.trim(),
      selectedDevice,
      selectedParameter,
      min:   minValue   === '' ? null : Number(minValue),
      max:   maxValue   === '' ? null : Number(maxValue),
      steps: stepsValue === '' ? null : Number(stepsValue),
    })
    onClose()
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header with Save / Cancel buttons on the right */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[#F1F2F4]">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">Gauge Series Editor</h2>
          
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Widget Name */}
          <div>
            <label className="common-size18 block text-xs font-bold text-[#4D4D4D] mb-3">
              Widget Name
              <span className="common-size12 ml-1 font-normal text-[#AFAFAF]">(Leave Blank For Default)</span>
            </label>
            <input
              type="text" placeholder="Gauge Series"
              value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>

          {/* Parameter Selection */}
          <div>
            <h3 className="common-size18 text-xs font-bold text-[#4D4D4D] mb-4">Parameter Selection</h3>

            <div className="mb-3">
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Device</label>
              <select
                value={selectedDevice}
                onChange={(e) => { setSelectedDevice(e.target.value); setSelectedParameter('') }}
                className="w-full border border-[#D6D6D6] bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || dev.serial_number || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Parameter</label>
              <select
                value={selectedParameter}
                onChange={(e) => setSelectedParameter(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-[#D6D6D6] bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            
          </div>

          {/* Range — Min / Max / Steps */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <label className="common-size15 block text-xs font-semibold text-[#4D4D4D] mb-1">Min</label>
              <input
                type="number" inputMode="decimal" step="any"
                placeholder="Minimum #"
                value={minValue} onChange={(e) => setMinValue(e.target.value)}
                className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="common-size15 block text-xs font-semibold text-[#4D4D4D] mb-1">Max</label>
              <input
                type="number" inputMode="decimal" step="any"
                placeholder="Maximum #"
                value={maxValue} onChange={(e) => setMaxValue(e.target.value)}
                className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="common-size15 block text-xs font-semibold text-[#4D4D4D] mb-1">Steps</label>
              <input
                type="number" inputMode="numeric" step="1" min="1"
                placeholder="Default"
                value={stepsValue} onChange={(e) => setStepsValue(e.target.value)}
                className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
          </div>
          <div className="py-4 bg-white">
              <button onClick={handleSave} className="savebtn common-size18 w-full px-4 py-1.5 text-sm text-white font-bold bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition ">Save</button>
              <button onClick={onClose} className="cancelbtn common-size16 w-full px-4 py-1.5 text-sm text-[#358ECD] font-semibold hover:underline border-gray-200 rounded-lg transition">Cancel</button>
            </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

// ─── Semicircle Gauge SVG ────────────────────────────────────────────────────
// Pure-SVG semicircle with three coloured arc segments (green / yellow / red)
// at fixed percentages of the range, evenly-spaced tick labels, and a needle
// pointing to the current value. Responsive — scales to fill container width
// up to `maxWidth`, never clips inside its card.
function SemicircleGauge({ value, min, max, steps, unit, maxWidth = 320 }) {
  const safeMin = Number.isFinite(min) ? min : 0
  const safeMax = Number.isFinite(max) && max > safeMin ? max : safeMin + 100
  const safeValue = value == null || !Number.isFinite(value)
    ? null
    : Math.max(safeMin, Math.min(safeMax, value))

  // Fixed internal coordinates — viewBox does the scaling.
  const w = 320
  const h = 220
  const cx = w / 2
  const cy = 150
  const r = 120
  const stroke = 18

  // Map a fraction (0..1) → angle on a TOP-OPENING half-arc:
  //   f=0  → 180°  (LEFT middle, the "min" end of the dial)
  //   f=.5 → 270°  (TOP centre of the dial)
  //   f=1  → 360°  (RIGHT middle, the "max" end of the dial)
  //
  // The previous mapping (-90°..+90°) drew the RIGHT half-circle instead of
  // the top half, so the gauge looked rotated 90° clockwise (sideways) and
  // the bottom endpoint at y = cy + r fell outside the viewBox → visibly
  // cut off. With SVG screen-space (y grows downward), sin(270°) = -1 so
  // the apex sits at (cx, cy - r) — the top of the dial.
  const fracToAngle = (f) => 180 + 180 * Math.max(0, Math.min(1, f))
  const deg2rad = (d) => (d * Math.PI) / 180

  const arc = (startFrac, endFrac, color) => {
    const a0 = deg2rad(fracToAngle(startFrac))
    const a1 = deg2rad(fracToAngle(endFrac))
    const x0 = cx + r * Math.cos(a0)
    const y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1)
    const y1 = cy + r * Math.sin(a1)
    const large = endFrac - startFrac > 0.5 ? 1 : 0
    // sweep-flag = 1 → arc drawn in the direction of *increasing* angle.
    // With the 180°→360° mapping above, that sweep passes through 270°
    // (the top of the circle), which is the top semicircle we want.
    // (Earlier 0° → −90°/+90° mapping needed sweep=1 too but the geometry
    // landed on the right-hand half-circle; flipping to 0 here while ALSO
    // flipping the angles double-flipped and produced the broken shape
    // with stray grey "tails" below the dial.)
    return (
      <path
        d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="butt"
      />
    )
  }

  // Coloured zones — fractions of the visible range. Matches the customer's
  // example screenshot: green to ~half, yellow short band, red to the end.
  const GREEN  = '#56B565'
  const YELLOW = '#F4D03F'
  const RED    = '#E36B6B'

  // Tick labels — `steps` is the number of intervals between ticks.
  // If unset, default to 8 (giving 9 labels at 0/25/50/.../200 for 0..200).
  const tickCount = Math.max(2, Math.round(Number(steps) || 8))
  const tickFracs = Array.from({ length: tickCount + 1 }, (_, i) => i / tickCount)

  // Needle
  const valueFrac = safeValue == null ? null : (safeValue - safeMin) / (safeMax - safeMin)
  const needleAngle = valueFrac == null ? null : deg2rad(fracToAngle(valueFrac))
  const needleR = r - stroke / 2 - 6
  const needleX = needleAngle == null ? cx : cx + needleR * Math.cos(needleAngle)
  const needleY = needleAngle == null ? cy : cy + needleR * Math.sin(needleAngle)

  // Format value: 1 decimal max, drop trailing zeros
  const formatVal = (v) => {
    if (v == null || !Number.isFinite(v)) return '—'
    const s = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1)
    return s.replace(/\.0$/, '')
  }

  return (
    // Wrap the SVG in a fixed-aspect-ratio box so the gauge stays a
    // sensible semicircle no matter how the parent grid sizes the widget
    // card. Without `aspectRatio` here, a tall+narrow cell would let the
    // SVG bleed past the card bottom (cut off); a short+wide cell would
    // squash it to a sliver (sideways-looking). The SVG itself then fills
    // the wrapper at width=100%/height=100% with preserveAspectRatio
    // meet, so it scales uniformly to the smaller dimension.
    <div
      style={{
        width: '100%', maxWidth,
        aspectRatio: `${w} / ${h}`,
        margin: '0 auto',
        // maxHeight lets a height-bound parent (e.g. a short widget cell)
        // cap the wrapper; flex parents will then shrink the width to
        // keep the aspect ratio intact instead of letting the SVG spill.
        maxHeight: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', display: 'block' }}
        aria-label="Gauge"
      >
        {/* Light grey baseline arc behind everything */}
        {arc(0, 1, '#E5E7EB')}

        {/* Coloured zones layered on top */}
        {arc(0,    0.50, GREEN)}
        {arc(0.50, 0.625, YELLOW)}
        {arc(0.625, 1.00, RED)}

        {/* Tick labels */}
        {tickFracs.map((f, i) => {
          const a = deg2rad(fracToAngle(f))
          const tr = r - stroke - 14
          const tx = cx + tr * Math.cos(a)
          const ty = cy + tr * Math.sin(a)
          const labelValue = safeMin + f * (safeMax - safeMin)
          return (
            <text key={i} x={tx} y={ty}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize="11" fill="#6B7280" fontWeight="500">
              {formatVal(labelValue)}
            </text>
          )
        })}

        {/* Needle */}
        {needleAngle != null && (
          <>
            <line x1={cx} y1={cy} x2={needleX} y2={needleY}
                  stroke="#374151" strokeWidth="3" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="6" fill="#374151" />
          </>
        )}

        {/* Value + unit label below the gauge */}
        <text x={cx} y={cy + 36}
              textAnchor="middle" fontSize="15" fontWeight="700" fill="#1F2937">
          {formatVal(safeValue)}{unit ? ` ${unit}` : ''}
        </text>
      </svg>
    </div>
  )
}

/**
 * Gauge Series Widget — renders a semicircle dial for the latest value of
 * one configured parameter, with min/max/steps controlling the scale and
 * tick labels. Editor lives in `GaugeSeriesEditorModal`. Settings persist
 * in localStorage (project-scoped).
 */
export function GaugeSeriesWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, paramConfigVersion = 0 } = useDashboardContext()

  const [settings, setSettings] = useState(
    () => lsGetScoped('wq_gauge', projectId, widget.id, {})
  )
  // Re-read the shared config once priming finishes so the owner's
  // saved picks land on the first paint for any viewer.
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setSettings(lsGetScoped('wq_gauge', projectId, widget.id, {}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [showEditor, setShowEditor] = useState(false)
  const [latestValue, setLatestValue] = useState(null)
  const [unit, setUnit] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_gauge', projectId, widget.id, next)
  }

  const paramId = settings.selectedParameter
  const configured = Boolean(paramId)

  // Fetch latest value whenever the chosen parameter changes
  useEffect(() => {
    if (!configured) { setLatestValue(null); setUnit(''); return }
    let cancelled = false
    setLoading(true); setError(null)
    projectsApi.fetchGraphData(Number(paramId), {})
      .then((res) => {
        if (cancelled) return
        if (res && typeof res === 'object' && !res.error) {
          setLatestValue(res.latestValue ?? null)
          setUnit(res.unit || '')
        } else {
          setLatestValue(null)
          setUnit('')
        }
      })
      .catch((e) => { if (!cancelled) { console.error('[Gauge]', e); setError('Could not load value.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, paramId, paramConfigVersion])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Gauge Series'}
        subtitle={widget.description}
        headerRight={
          isCustomizing && canEdit ? (
            <button
              onClick={() => setShowEditor(true)}
              className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
              title="Edit gauge"
            >
              <EditIcon />
            </button>
          ) : null
        }
      >
        {/* min-h-[180px] keeps the gauge from collapsing into a
            sliver when the widget cell is short; the SVG inside still
            scales down via its aspectRatio wrapper if there's no room. */}
        <div className="flex flex-col items-center justify-center p-4 h-full min-h-[180px] w-full overflow-hidden">
          {!configured ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">📍</span>
              <p className="common-size15 text-sm text-[#888888] text-center">Pick a device and parameter in the editor.</p>
            </div>
          ) : loading ? (
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          ) : error ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">{error}</div>
          ) : (
            <SemicircleGauge
              value={latestValue}
              min={settings.min ?? 0}
              max={settings.max ?? 100}
              steps={settings.steps}
              unit={unit}
            />
          )}
        </div>
      </XDCard>

      <GaugeSeriesEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialSettings={settings}
      />
    </>
  )
}

// ─── Bullet Graph Editor Modal ───────────────────────────────────────────────
// Multi-parameter editor — each row has Device + Parameter + Min/Max/Steps.
// "+ Add Parameter" appends another blank row. Delete (✕) per row.
export function BulletGraphEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    const initial = Array.isArray(initialSettings.parameters) && initialSettings.parameters.length
      ? initialSettings.parameters
      : [{ id: 'p' + Date.now(), selectedDevice: '', selectedParameter: '', min: '', max: '', steps: '' }]
    setRows(initial.map((r, i) => ({ ...r, id: r.id || `p${i}_${Date.now()}` })))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => devs.push({ ...dev, siteName: site.name || '' }))
    })
    return devs
  }, [sites])

  const paramsForDevice = (deviceId) => {
    if (!deviceId) return []
    return deviceFlatParams(allDevices.find((d) => String(d.id) === String(deviceId)))
  }

  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
  }
  const addRow = () => {
    setRows((prev) => [...prev, { id: 'p' + Date.now(), selectedDevice: '', selectedParameter: '', min: '', max: '', steps: '' }])
  }
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id))

  if (!open) return null

  const save = () => {
    const cleaned = rows
      .filter((r) => r.selectedDevice && r.selectedParameter)
      .map((r) => ({
        id: r.id,
        selectedDevice: r.selectedDevice,
        selectedParameter: r.selectedParameter,
        min:   r.min   === '' ? null : Number(r.min),
        max:   r.max   === '' ? null : Number(r.max),
        steps: r.steps === '' ? null : Number(r.steps),
      }))
    onSave({ widgetName: widgetName.trim(), parameters: cleaned })
    onClose()
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D] text-base">Bullet Graph Editor</h2>
          
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div>
            <label className="common-size18 block text-xs font-bold text-[#4D4D4D] mb-1">
              Widget Name <span className="common-size12 ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input type="text" placeholder="Bullet Graph" value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
          </div>

          <div>
            <h3 className="common-size18 text-xs font-bold text-[#4D4D4D] mb-3">Parameter Selection</h3>
            <div className="space-y-4">
              {rows.map((row, idx) => (
                <div key={row.id} className="space-y-2 border border-gray-100 rounded-lg p-3 bg-gray-50/40">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-500">Parameter {idx + 1}</span>
                    {rows.length > 1 && (
                      <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700" title="Remove">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="common-size15 block text-xs text-[#4D4D4D] mb-1">Device</label>
                    <select value={row.selectedDevice} onChange={(e) => { updateRow(row.id, 'selectedDevice', e.target.value); updateRow(row.id, 'selectedParameter', '') }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]">
                      <option value="">{loading ? 'Loading devices…' : 'Select Device'}</option>
                      {allDevices.map((dev) => (
                        <option key={dev.id} value={dev.id}>
                          {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || `Device ${dev.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="common-size15 block text-xs text-[#4D4D4D] mb-1">Parameter</label>
                    <select value={row.selectedParameter} onChange={(e) => updateRow(row.id, 'selectedParameter', e.target.value)}
                      disabled={!row.selectedDevice}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400">
                      <option value="">Select Parameter</option>
                      {paramsForDevice(row.selectedDevice).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" inputMode="decimal" step="any" placeholder="Minimum #" value={row.min} onChange={(e) => updateRow(row.id, 'min', e.target.value)}
                      className="common-size15 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input type="number" inputMode="decimal" step="any" placeholder="Maximum #" value={row.max} onChange={(e) => updateRow(row.id, 'max', e.target.value)}
                      className="common-size15 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input type="number" inputMode="numeric" step="1" min="1" placeholder="Steps" value={row.steps} onChange={(e) => updateRow(row.id, 'steps', e.target.value)}
                      className="common-size15 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addRow}
              className="mt-3 text-sm  text-[#358ECD] hover:underline flex items-center gap-1">
              <span className="common-size15 font-semibold text-base leading-none">+</span> Add Parameter
            </button>

            <div className="py-4 bg-white">
              <button onClick={save} className="savebtn common-size18 w-full px-4 py-1.5 text-sm text-white font-bold bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition ">Save</button>
              <button onClick={onClose} className="cancelbtn common-size16 w-full px-4 py-1.5 text-sm text-[#358ECD] font-semibold hover:underline border-gray-200 rounded-lg transition">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

// Single horizontal bullet bar
function BulletBar({ label, sublabel, value, min, max, steps, color = '#F37D5C' }) {
  const lo = Number.isFinite(min) ? min : 0
  const hi = Number.isFinite(max) && max > lo ? max : lo + 100
  const v = value == null || !Number.isFinite(value) ? null : Math.max(lo, Math.min(hi, value))
  const pct = v == null ? 0 : ((v - lo) / (hi - lo)) * 100
  const tickCount = Math.max(2, Math.round(Number(steps) || 6))
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => lo + (i / tickCount) * (hi - lo))
  const fmt = (n) => Math.abs(n) >= 100 ? Math.round(n).toString() : (Math.round(n * 10) / 10).toString()

  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-3">
      <div className="text-right">
        <div className="text-[11px] font-semibold text-[#4D4D4D] leading-tight">{label}</div>
        {sublabel && <div className="text-[10px] text-gray-500">{sublabel}</div>}
      </div>
      <div>
        <div className="relative w-full h-5 bg-gray-100 rounded">
          {v != null && (
            <div className="absolute inset-y-0 left-0 rounded-l" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.85 }} />
          )}
          {/* "target" marker — value position drawn as a small dark notch */}
          {v != null && (
            <div className="absolute top-0 bottom-0 w-0.5 bg-gray-800" style={{ left: `calc(${pct}% - 1px)` }} />
          )}
        </div>
        <div className="relative w-full h-3 mt-0.5">
          {ticks.map((t, i) => {
            const left = (i / tickCount) * 100
            return (
              <span key={i}
                    className="absolute -translate-x-1/2 text-[9px] text-gray-500"
                    style={{ left: `${left}%` }}>
                {fmt(t)}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Bullet Graph Widget — stacks one horizontal bullet bar per configured
 * parameter. Latest value drives the fill width and the dark notch.
 */
export function BulletGraphWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, paramConfigVersion = 0 } = useDashboardContext()
  const [settings, setSettings] = useState(() => lsGetScoped('wq_bullet', projectId, widget.id, {}))
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setSettings(lsGetScoped('wq_bullet', projectId, widget.id, {}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [showEditor, setShowEditor] = useState(false)
  const [paramMeta, setParamMeta] = useState({})    // id → {name, unit_name}
  const [values, setValues] = useState({})          // id → {value, unit}

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_bullet', projectId, widget.id, next)
  }

  const params = Array.isArray(settings.parameters) ? settings.parameters : []

  // Resolve parameter names from project sites (so labels show the human name)
  useEffect(() => {
    if (!projectId || params.length === 0) { setParamMeta({}); return }
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => {
        if (cancelled) return
        const out = {}
        for (const s of (sites || [])) {
          for (const d of (s.devices || [])) {
            for (const p of deviceFlatParams(d)) {
              out[String(p.id)] = { name: p.name || '', unit_name: p.unit || '' }
            }
          }
        }
        setParamMeta(out)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [projectId, params.length])

  // Fetch latest values
  useEffect(() => {
    if (params.length === 0) { setValues({}); return }
    let cancelled = false
    Promise.allSettled(
      params.map((p) =>
        projectsApi.fetchGraphData(Number(p.selectedParameter), {})
          .then((res) => ({
            id: String(p.selectedParameter),
            value: (res && typeof res === 'object' && !res.error) ? (res.latestValue ?? null) : null,
            unit:  (res && res.unit) || '',
          }))
      )
    ).then((results) => {
      if (cancelled) return
      const map = {}
      results.forEach((r) => { if (r.status === 'fulfilled' && r.value?.id) map[r.value.id] = r.value })
      setValues(map)
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params.map((p) => p.selectedParameter)), paramConfigVersion])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Bullet Graph'}
        subtitle={widget.description}
        headerRight={isCustomizing && canEdit ? (
          <button onClick={() => setShowEditor(true)} className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition" title="Edit bullet graph">
            <EditIcon />
          </button>
        ) : null}
      >
        <div className="p-4">
          {params.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">🎯</span>
              <p className="common-size15 text-sm text-center">Add a parameter in the editor to render a bar.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {params.map((p) => {
                const meta = paramMeta[String(p.selectedParameter)] || {}
                const v    = values[String(p.selectedParameter)]
                return (
                  <BulletBar
                    key={p.id}
                    label={meta.name || `Param ${p.selectedParameter}`}
                    sublabel={meta.unit_name || v?.unit || ''}
                    value={v?.value ?? null}
                    min={p.min ?? 0}
                    max={p.max ?? 100}
                    steps={p.steps}
                  />
                )
              })}
            </div>
          )}
        </div>
      </XDCard>
      <BulletGraphEditorModal open={showEditor} onClose={() => setShowEditor(false)} onSave={handleSave} projectId={projectId} initialSettings={settings} />
    </>
  )
}

// ─── Solid Gauge Editor Modal ────────────────────────────────────────────────
export function SolidGaugeEditorModal({ open, onClose, onSave, projectId, initialSettings = {}, title = 'Solid Gauge Editor' }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState('')
  const [selectedParameter, setSelectedParameter] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    setSelectedDevice(initialSettings.selectedDevice || '')
    setSelectedParameter(initialSettings.selectedParameter || '')
    setMinValue(initialSettings.min ?? '')
    setMaxValue(initialSettings.max ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => devs.push({ ...dev, siteName: site.name || '' }))
    })
    return devs
  }, [sites])

  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    return deviceFlatParams(allDevices.find((d) => String(d.id) === String(selectedDevice)))
  }, [selectedDevice, allDevices])

  if (!open) return null

  const save = () => {
    onSave({
      widgetName: widgetName.trim(),
      selectedDevice,
      selectedParameter,
      min: minValue === '' ? null : Number(minValue),
      max: maxValue === '' ? null : Number(maxValue),
    })
    onClose()
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">{title}</h2>
          
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div>
            <label className="common-size18 font-bold block text-xs text-[#4D4D4D] mb-3">
              Widget Name <span className="common-size12 ml-1 font-normal text-[#AFAFAF]">(Leave Blank For Default)</span>
            </label>
            <input type="text" placeholder="Solid Gauge" value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
          </div>
          <div>
            <h3 className="common-size18 text-xs font-bold text-[#4D4D4D] mb-4">Parameter Selection</h3>
            <div className="mb-3">
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Device</label>
              <select value={selectedDevice} onChange={(e) => { setSelectedDevice(e.target.value); setSelectedParameter('') }}
                className="w-full border border-gray-200 rounded-lg bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]">
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="common-size15 block font-bold text-xs text-[#4D4D4D] mb-3">Parameter</label>
              <select value={selectedParameter} onChange={(e) => setSelectedParameter(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-gray-200 rounded-lg px-3 bg-white py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400">
                <option value="">Select Parameter…</option>
                {deviceParams.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <label className="common-size15 block text-xs font-semibold text-[#4D4D4D]">Min</label>
              <input type="number" inputMode="decimal" step="any" placeholder="Minimum #" value={minValue} onChange={(e) => setMinValue(e.target.value)}
                className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            </div>
            <div className="flex items-center gap-2">
              <label className="common-size15 block text-xs font-semibold text-[#4D4D4D]">Max</label>
              <input type="number" inputMode="decimal" step="any" placeholder="Maximum #" value={maxValue} onChange={(e) => setMaxValue(e.target.value)}
                className="common-size15 w-full border border-[#D6D6D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            </div>
          </div>
          <div className="py-4 bg-white">
            <button onClick={save} className="savebtn common-size18 w-full px-4 py-1.5 text-sm text-white font-bold bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition">Save</button>
            <button onClick={onClose} className="cancelbtn common-size16 w-full px-4 py-1.5 text-sm text-[#358ECD] font-semibold hover:underline border-gray-200 rounded-lg transition">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

// Solid-fill arc gauge — semicircle, single colour fill (no green/yellow/red zones),
// title above the arc, value+unit centred below. Renders RESPONSIVELY — the SVG
// scales to fill its container width up to a max, so it never clips inside its card.
function SolidGaugeArc({ value, min, max, unit, label, maxWidth = 280 }) {
  const lo = Number.isFinite(min) ? min : 0
  const hi = Number.isFinite(max) && max > lo ? max : lo + 100
  const v  = value == null || !Number.isFinite(value) ? null : Math.max(lo, Math.min(hi, value))

  // Fixed internal coordinates — the viewBox does the scaling. Square-ish
  // canvas (not a wide/short rectangle) since the ring now sweeps most of a
  // full circle rather than just the top half.
  const W  = 280
  const H  = 260
  const cx = W / 2
  const cy = 148
  const r  = 92
  const stroke = 20
  const FILL = '#C9D643'  // muted lime green from the XD

  // 270° "3/4 donut" sweep with a 90° gap centred on the bottom — matches
  // the reference design (Min/Max sit at the two open ends, bottom-left and
  // bottom-right) instead of the old plain top half-circle. Angle
  // convention: 0°=right, 90°=bottom, 180°=left, 270°=top (y grows
  // downward). f=0 → 135° (bottom-left), f=1 → 405°=45° (bottom-right),
  // passing through 180 (left), 270 (top) and 360/0 (right) along the way.
  const START_DEG = 135
  const SWEEP_DEG = 270
  const fracToAngle = (f) => START_DEG + SWEEP_DEG * Math.max(0, Math.min(1, f))
  const d2r = (d) => (d * Math.PI) / 180
  const arc = (f0, f1, color) => {
    const a0 = d2r(fracToAngle(f0))
    const a1 = d2r(fracToAngle(f1))
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1)
    // Large-arc-flag must reflect the ACTUAL degree span, not just the 0..1
    // fraction: with a 270° total sweep, a fraction as small as ~0.67
    // already exceeds the 180° threshold — unlike the old plain 180°
    // semicircle, where the max possible span could never cross it.
    const large = (f1 - f0) * SWEEP_DEG > 180 ? 1 : 0
    return <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" />
  }

  const frac = v == null ? 0 : (v - lo) / (hi - lo)
  const fmt = (n) => {
    if (n == null || !Number.isFinite(n)) return '—'
    const s = Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1)
    return s.replace(/\.0$/, '')
  }
  // Temperature units read better with the ° glyph ("°F" instead of bare
  // "F"); other units (mph, %, mm, ...) are shown exactly as the API sends them.
  const displayUnit = unit && ['F', 'C', 'K'].includes(unit) ? `°${unit}` : unit

  // Endpoint coordinates for the Min/Max labels, placed just below the
  // ring's two open ends (bottom-left / bottom-right).
  const loEnd = { x: cx + r * Math.cos(d2r(START_DEG)), y: cy + r * Math.sin(d2r(START_DEG)) }
  const hiEnd = { x: cx + r * Math.cos(d2r(START_DEG + SWEEP_DEG)), y: cy + r * Math.sin(d2r(START_DEG + SWEEP_DEG)) }

  return (
    // aspect-ratio wrapper so the SVG keeps its natural proportions regardless
    // of the parent's exact width/height — same fix as the Gauge Series widget.
    <div
      style={{
        width: '100%', maxWidth,
        aspectRatio: `${W} / ${H}`,
        maxHeight: '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', display: 'block' }} aria-label="Solid gauge">
        {/* Ring's top edge sits at cy - r - stroke/2 (≈36) — plenty of
            clearance above it here, unlike the old cramped half-circle. */}
        {label && (
          <text x={cx} y={26} textAnchor="middle" fontSize="15" fill="#6B7280">{label}</text>
        )}
        {arc(0, 1, '#E5E7EB')}
        {v != null && frac > 0 && arc(0, frac, FILL)}
        {/* Value + unit centred inside the ring. With no reading, show a
            muted "No data" instead of a big bold em-dash that reads as a stray
            line/glitch. */}
        {v == null ? (
          <text x={cx} y={cy} textAnchor="middle" fontSize="14" fill="#9CA3AF">No data</text>
        ) : (
          <>
            <text x={cx} y={cy + 8} textAnchor="middle" fontSize="40" fontWeight="700" fill="#1F2937">{fmt(v)}</text>
            {displayUnit && <text x={cx} y={cy + 34} textAnchor="middle" fontSize="15" fill="#6B7280">{displayUnit}</text>}
          </>
        )}
        {/* Min/Max labels just below the ring's two open ends */}
        <text x={loEnd.x} y={loEnd.y + 20} textAnchor="middle" fontSize="12" fill="#9CA3AF">{fmt(lo)}</text>
        <text x={hiEnd.x} y={hiEnd.y + 20} textAnchor="middle" fontSize="12" fill="#9CA3AF">{fmt(hi)}</text>
      </svg>
    </div>
  )
}

/**
 * Solid Gauge Widget — single-arc fill gauge for one parameter's latest value.
 */
export function SolidGaugeWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, paramConfigVersion = 0 } = useDashboardContext()
  const [settings, setSettings] = useState(() => lsGetScoped('wq_solid', projectId, widget.id, {}))
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setSettings(lsGetScoped('wq_solid', projectId, widget.id, {}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [showEditor, setShowEditor] = useState(false)
  const [latestValue, setLatestValue] = useState(null)
  const [unit, setUnit] = useState('')
  const [paramName, setParamName] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_solid', projectId, widget.id, next)
  }

  const paramId = settings.selectedParameter
  const configured = Boolean(paramId)

  // Resolve parameter name from cached sites for the "Speed" label above the gauge
  useEffect(() => {
    if (!configured || !projectId) { setParamName(''); return }
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => {
        if (cancelled) return
        for (const s of (sites || [])) {
          for (const d of (s.devices || [])) {
            const p = deviceFlatParams(d).find((x) => String(x.id) === String(paramId))
            if (p) { setParamName(p.name || ''); return }
          }
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [configured, paramId, projectId])

  useEffect(() => {
    if (!configured) { setLatestValue(null); setUnit(''); return }
    let cancelled = false
    setLoading(true)
    projectsApi.fetchGraphData(Number(paramId), {})
      .then((res) => {
        if (cancelled) return
        if (res && typeof res === 'object' && !res.error) {
          setLatestValue(res.latestValue ?? null)
          setUnit(res.unit || '')
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, paramId, paramConfigVersion])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Solid Gauge'}
        subtitle={widget.description}
        headerRight={isCustomizing && canEdit ? (
          <button onClick={() => setShowEditor(true)} className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition" title="Edit gauge">
            <EditIcon />
          </button>
        ) : null}
      >
        {/* min-h-[200px] mirrors the Gauge Series widget — keeps the
            SVG from collapsing into a sliver when the card is short.
            w-full + the SVG's aspect-ratio wrapper inside handle the
            scaling so the gauge stays a proper top-half semicircle. */}
        <div className="flex flex-col items-center justify-center p-4 h-full w-full min-h-[200px] overflow-hidden">
          {!configured ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">📍</span>
              <p className="common-size15 text-sm text-center">Pick a device and parameter in the editor.</p>
            </div>
          ) : loading ? (
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          ) : (
            <SolidGaugeArc value={latestValue} min={settings.min ?? 0} max={settings.max ?? 100} unit={unit} label={paramName} />
          )}
        </div>
      </XDCard>
      <SolidGaugeEditorModal open={showEditor} onClose={() => setShowEditor(false)} onSave={handleSave} projectId={projectId} initialSettings={settings} />
    </>
  )
}

/**
 * DataBoxWidget — a compact "current value" stat tile (XD Overview data boxes).
 *
 * Configurable like the Solid Gauge: the user picks a device + parameter in the
 * editor, and the box fetches that parameter's latest value + unit. Reuses the
 * shared SolidGaugeEditorModal (device/parameter picker) and the same
 * scoped-settings + fetchGraphData plumbing so behaviour stays consistent.
 */
// ─── DataBoxWidget ───────────────────────────────────────────────────────────
// ONE latest-reading tile, showing the Nth parameter ticked in the sidebar.
// N comes from the trailing number in the widget's name ("Data Box 1" -> the
// 1st ticked parameter, "Data Box 2" -> the 2nd, ...).
//
// Overview therefore stays a fixed FOUR tiles: tick six parameters and you
// still see four, because only boxes 1-4 exist. The All Data screen is the one
// that grows with the selection.
//
// It used to be configured by hand — each tile needed a device + parameter
// picked in its own editor, which is why they sat empty showing "Pick a device
// and parameter in the editor". There is nothing to configure now, so the
// widget has no editor and no pencil.
//
// The tile body is AllDataView's ParamValueCard, so both screens render an
// identical card with no second copy of that markup to drift.
export function DataBoxWidget({ widget }) {
  // paramConfigVersion bumps when Parameter Configuration is saved (e.g. a unit
  // Convert); include it in the fetch deps so this tile re-fetches the converted
  // value + unit immediately, instead of staying stale until Edit Mode ends or a
  // reload (the Line Chart already re-fetched on it — this makes the tile match).
  const { selectedParameters = [], paramConfigVersion = 0, dataRefreshNonce = 0 } = useDashboardContext()
  const [reading, setReading] = useState(null)   // { value, unit } | null
  const [loading, setLoading] = useState(false)

  // Which slot is this? Trailing digits of the name ("Data Box 3" -> index 2).
  // Falls back to the grid column (0/3/6/9 in the 12-col Overview) so a
  // renamed tile still lands somewhere sensible, then to the first slot.
  const boxIndex = (() => {
    const label = widget?.custom_title || widget?.name || widget?.title || ''
    const m = String(label).match(/(\d+)\s*$/)
    if (m) return Math.max(0, parseInt(m[1], 10) - 1)
    const col = Number(widget?.col)
    if (Number.isFinite(col) && col > 0) return Math.floor(col / 3)
    return 0
  })()

  const param = selectedParameters[boxIndex] || null
  const paramId = param?.id ?? null

  useEffect(() => {
    if (!paramId) { setReading(null); return }
    let cancelled = false
    setLoading(true)
    projectsApi.fetchGraphData(Number(paramId), {})
      .then((res) => {
        if (cancelled) return
        setReading({
          value: (res && typeof res === 'object' && !res.error) ? (res.latestValue ?? null) : null,
          unit:  (res && res.unit) || param?.unit || '',
          precision: (res && typeof res === 'object' && Number.isFinite(res.precision)) ? res.precision : undefined,
        })
      })
      .catch(() => { if (!cancelled) setReading(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // dataRefreshNonce bumps on every manual/auto refresh so the tile re-pulls
  // its latest value in place (no grid remount needed).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramId, paramConfigVersion, dataRefreshNonce])

  if (!param) {
    return (
      <Card className="p-4 h-full flex items-center justify-center">
        <p className="text-xs text-gray-400 text-center">
          Tick {boxIndex === 0 ? 'a parameter' : `${boxIndex + 1} or more parameters`} in the sidebar.
        </p>
      </Card>
    )
  }

  return (
    <Card className="p-4 h-full">
      {loading && (
        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mb-1">
          <Loader className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
      <ParamValueCard
        siteName={param.siteName || param.site_name || ''}
        paramName={param.name || ''}
        value={reading?.value ?? null}
        unit={reading?.unit ?? (param.unit || '')}
        precision={reading?.precision}
      />
    </Card>
  )
}

// ============= WEATHER PANEL WIDGETS =============

/**
 * Generic Weather/Water/Soil Panel Widget
 */
function PanelWidget({ widget, data, bgColor = 'from-blue-50 to-blue-100', borderColor = 'border-blue-200', valueColor = 'text-blue-600', icon = '📊' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden border-l-4 ${borderColor.replace('border-', 'border-l-').split(' ')[0]}`}>
      <div className="flex border-b border-gray-100 items-center justify-between px-4 py-2 shrink-0">
        <div>
          <h3 className="common-size18 font-bold text-[#4D4D4D] text-base">{widget.title}</h3>
          {widget.description && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{widget.description}</p>}
        </div>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className={`flex-1 flex items-center px-6 py-4 bg-gradient-to-br ${bgColor}`}>
        <div>
          <div className={`text-5xl font-bold ${valueColor}`}>{data?.value ?? '—'}</div>
          {data?.unit && <p className="text-sm text-gray-600 mt-1">{data.unit}</p>}
          {data?.change != null && (
            <p className="text-sm text-gray-600 mt-1">{data.change > 0 ? '↑' : '↓'} {Math.abs(data.change)}</p>
          )}
          {!data?.value && (
            <p className="text-sm text-gray-400 mt-1">No live data</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function AirTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🌡️" />
}

export function BarometricPressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="🌪️" />
}

export function DailyRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function HeadingWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-indigo-50 to-indigo-100" borderColor="border-indigo-200" valueColor="text-indigo-600" icon="🧭" />
}

export function HumidityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function IntervalRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function LightningWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function PrecipitationWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🌧️" />
}

export function PressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="🌪️" />
}

export function RainIntensityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🌧️" />
}

export function SolarRadiationWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="☀️" />
}

export function TotalRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function WindWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-gray-50 to-gray-100" borderColor="border-gray-200" valueColor="text-gray-600" icon="💨" />
}

// ============= WATER PANEL WIDGETS =============

export function AmmoniumWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function BGAWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="🌿" />
}

export function ChlorophyllWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="🌿" />
}

export function ConductivityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function DissolvedOxygenWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function HydrocarbonsWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🛢️" />
}

export function LevelWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="📏" />
}

export function OilDetectionWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-red-50 to-red-100" borderColor="border-red-200" valueColor="text-red-600" icon="🛢️" />
}

export function ORPWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="⚡" />
}

export function PHWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="⚗️" />
}

export function WaterPressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="📊" />
}

export function SalinityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🧂" />
}

export function SnowWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="❄️" />
}

export function SpConductanceWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function WaterTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-red-50 to-red-100" borderColor="border-red-200" valueColor="text-red-600" icon="🌡️" />
}

export function TurbidityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-gray-50 to-gray-100" borderColor="border-gray-200" valueColor="text-gray-600" icon="🌫️" />
}

export function WaterCurrentWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="〰️" />
}

export function WaveWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="〰️" />
}

// ============= SOIL PANEL WIDGETS =============

export function SoilConductivityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function SoilMoistureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="💧" />
}

export function SoilSalinityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="🧂" />
}

export function SoilTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🌡️" />
}

// ============= OTHER WIDGETS =============

/**
 * GPS Widget — Project Map renderer.
 *
 * On the Project Map dashboard the backend template assigns this single
 * widget; the widget renders the full interactive Leaflet map plus the
 * top-right Edit button that opens the Map Editor popup (pin colours per
 * site type, base tile layer, default centre + zoom).
 *
 * The map needs the full project sites list and the active project id to
 * persist its per-project config — both come from DashboardContext so we
 * don't have to thread them through the widget registry.
 *
 * (Earlier this widget was a tiny "Location Coordinates" stub; the user
 * asked for the Project Map dashboard to render its assigned GPS widget
 * as the actual interactive map, with all map configuration living
 * inside the widget rather than at the page level.)
 */
export function GPSWidget({ widget }) {
  const { sites = [], projectId } = useDashboardContext()
  return (
    <Card className="p-0 overflow-hidden">
      {widget?.title && (
        <h3 className="text-lg font-semibold text-[#4D4D4D] px-4 pt-3 pb-2">
          {widget.title}
        </h3>
      )}
      <div className="p-3 pt-0">
        <ProjectMapView sites={sites} projectId={projectId} />
      </div>
    </Card>
  )
}

// ============= ALARM WIDGETS =============

/**
 * Alarm Summary Widget
 */
export function AlarmSummaryWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="space-y-3">
        <div className="flex justify-between p-3 bg-red-50 rounded-lg border border-red-200">
          <span className="font-semibold text-red-700">Critical</span>
          <span className="font-bold text-lg text-red-600">{data?.critical || 0}</span>
        </div>
        <div className="flex justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <span className="font-semibold text-yellow-700">Warning</span>
          <span className="font-bold text-lg text-yellow-600">{data?.warning || 0}</span>
        </div>
        <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <span className="font-semibold text-blue-700">Info</span>
          <span className="font-bold text-lg text-blue-600">{data?.info || 0}</span>
        </div>
      </div>
    </Card>
  )
}

/**
 * Individual Alarm Widget
 */
export function IndividualAlarmWidget({ widget, data }) {
  const getSeverityStyles = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-700'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700'
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-700'
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700'
    }
  }

  return (
    <Card className={`p-6 border ${getSeverityStyles(data?.severity)}`}>
      <h3 className="text-lg font-semibold mb-2">{data?.name || widget.title}</h3>
      <p className="text-sm mb-3">{data?.message || 'No message'}</p>
      <div className="text-xs space-y-1">
        <p><span className="font-semibold">Status:</span> {data?.status || 'Active'}</p>
        <p><span className="font-semibold">Time:</span> {data?.timestamp || '—'}</p>
      </div>
    </Card>
  )
}

// ============= MISCELLANEOUS WIDGETS =============

/**
 * Project Description Widget
 */
export function ProjectDescriptionWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false, canEdit = true } = useDashboardContext()
  const html = widget.custom_rich_content || widget.rich_content || ''
  const fallbackText = data?.description || ''
  return (
    <XDCard
      title={widget.custom_title || widget.title}
      subtitle={widget.custom_description || widget.description}
      headerRight={
        isCustomizing && canEdit && onConfigure ? (
          <button
            onClick={() => onConfigure(widget)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit project description"
          >
            <EditIcon />
          </button>
        ) : null
      }
    >
      <div className="p-6 text-gray-600 text-base leading-relaxed overflow-auto">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-gray-400 italic text-center py-8">
            {fallbackText || 'No description yet · click the ✎ icon to add project description'}
          </p>
        )}
      </div>
    </XDCard>
  )
}

/**
 * Project Photo Widget — with edit icon, photo-change modal, and localStorage persistence
 */
// Normalise a saved `wq_photo` config to `{ sourceUrl, zoom, bakedUrl }`.
// Legacy saves (before zoom-aware storage) stored a plain baked-URL string
// with no separate source — for those there's no better option than
// treating the baked value as its own source too, so re-editing an old
// photo starts from what's currently shown (matches the old behavior);
// only photos saved AFTER this fix retain a true pristine original.
function normalisePhotoConfig(raw) {
  const okOffset = (o) => (o && Number.isFinite(o.x) && Number.isFinite(o.y) && Math.abs(o.x) <= 1 && Math.abs(o.y) <= 1)
  if (raw && typeof raw === 'object' && raw.bakedUrl) {
    return {
      sourceUrl: raw.sourceUrl || raw.bakedUrl,
      zoom: raw.zoom ?? 1,
      offset: okOffset(raw.offset) ? raw.offset : { x: 0, y: 0 },
      bakedUrl: raw.bakedUrl,
    }
  }
  if (typeof raw === 'string' && raw) {
    return { sourceUrl: raw, zoom: 1, offset: { x: 0, y: 0 }, bakedUrl: raw }
  }
  return null
}

export function ProjectPhotoWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, goToGallery } = useDashboardContext()
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  // Project-scoped key — see scopedLsKey doc-block. Without scoping, two
  // projects on the same Overview template would render the same custom
  // project photo because widget.id collides across projects.
  const [photoConfig, setPhotoConfig] = useState(
    () => normalisePhotoConfig(lsGetScoped('wq_photo', projectId, widget.id, null))
  )
  const photoUrl = photoConfig?.bakedUrl || data?.url || '/images/projectphoto.jpg'
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    const v = normalisePhotoConfig(lsGetScoped('wq_photo', projectId, widget.id, null))
    if (v) setPhotoConfig(v)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])

  // If the API provides a URL and we have no locally saved photo, use it
  useEffect(() => {
    if (data?.url && !lsGetScoped('wq_photo', projectId, widget.id, null)) {
      setPhotoConfig({ sourceUrl: data.url, zoom: 1, bakedUrl: data.url })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.url, projectId])

  // Receives `{ bakedUrl, sourceUrl, zoom }` from the modal — `sourceUrl` is
  // the un-zoomed original, kept alongside the flattened `bakedUrl` so a
  // later edit can restore back to the un-zoomed image instead of zooming
  // into an already-flattened one.
  const handleSavePhoto = async ({ bakedUrl, sourceUrl, zoom, offset }) => {
    if (!bakedUrl) return
    const next = {
      sourceUrl: sourceUrl || bakedUrl,
      zoom: zoom ?? 1,
      offset: (offset && Number.isFinite(offset.x) && Number.isFinite(offset.y)) ? offset : { x: 0, y: 0 },
      bakedUrl,
    }
    setPhotoConfig(next)
    // Persist to localStorage; silently ignore quota errors.
    lsSetScoped('wq_photo', projectId, widget.id, next)
  }

  return (
    <>
      <XDCard
        title={widget.title}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            {/* View All first, then the edit pencil to its right — matches the
                Gallery and other widgets' header button order. */}
            <button
              type="button"
              onClick={() => goToGallery?.()}
              disabled={isCustomizing}
              className="common-size15 text-[#4D4D4D] flex items-center gap-2 text-sm bg-[#F1F2F4] py-1.5 px-4 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Open Gallery"
            >
              <ExternalLinkIcon /> View All
            </button>
            {/* Edit icon hidden in Customize mode */}
            {isCustomizing && canEdit && (
              <button
                onClick={() => setShowPhotoModal(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change project photo"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        <div className="flex items-center justify-center overflow-hidden p-6">
          <img src={photoUrl} alt={widget.title} className="max-w-full max-h-72 object-contain rounded" />
        </div>
      </XDCard>

      <ChangeProjectPhotoModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onSave={handleSavePhoto}
        currentUrl={photoConfig?.sourceUrl || photoUrl}
        currentZoom={photoConfig?.zoom ?? 1}
        currentOffset={photoConfig?.offset ?? { x: 0, y: 0 }}
      />
    </>
  )
}

/**
 * Gallery Widget — single-image carousel matching the XD design.
 *
 *   - One image shown at a time (full width of the widget body)
 *   - Caption (when set) sits under the image
 *   - "View All" button (top right) opens the Gallery tab via DashboardContext
 *   - Edit pencil opens GalleryEditorModal (add by URL or upload, caption,
 *     drag-reorder, remove)
 *   - `<` `>` arrows in the bottom-right corner cycle through images
 *   - Persists to localStorage `wq_gallery_${widget.id}`. Backward compatible
 *     with `widget.rich_content` JSON (legacy) and `data.images` (API).
 */
function parseGalleryImages(raw) {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch { return null }
}

// Normalise different image-source shapes to `{ id, src, caption }`.
function normaliseImage(img, idx) {
  const src = img?.src || img?.url || img
  return {
    id: img?.id || `g${idx}_${typeof src === 'string' ? src.slice(0, 20) : idx}`,
    src,
    caption: img?.caption || img?.alt || '',
  }
}

export function GalleryWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, goToGallery } = useDashboardContext()

  // Persisted config: { albumId, albumName }. Picked via the "Change Gallery"
  // editor — the user selects one of the project's albums and the widget
  // shows its photos as a single-image carousel.
  //
  // KEY MUST BE PROJECT-SCOPED — every project using the Overview template
  // shares the same widget.id (1..15 from the seed), so an un-scoped key
  // would leak one project's selected album to all other projects.
  const lsConfig = lsGetScoped('wq_gallery', projectId, widget.id, null)
  const [albumConfig, setAlbumConfig] = useState(
    lsConfig && lsConfig.albumId
      ? { albumId: lsConfig.albumId, albumName: lsConfig.albumName || '' }
      : null
  )
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    const v = lsGetScoped('wq_gallery', projectId, widget.id, null)
    if (v?.albumId) setAlbumConfig({ albumId: v.albumId, albumName: v.albumName || '' })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [index, setIndex] = useState(0)
  const [showEditor, setShowEditor] = useState(false)

  // Fetch photos whenever the selected album changes
  useEffect(() => {
    if (!albumConfig?.albumId) { setPhotos([]); return }
    let cancelled = false
    setLoading(true); setError(null)
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    // 'all' is the sentinel for the "All Photos" selection — fetch every photo
    // in the project rather than a single album.
    const photoUrl = albumConfig.albumId === 'all'
      ? `${API_URL}/api/photo/photo/?project=${projectId}`
      : `${API_URL}/api/photo/photo/?album=${albumConfig.albumId}`
    fetch(photoUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res) ? res : (res.results || [])
        setPhotos(list)
        setIndex(0)
      })
      .catch((e) => { if (!cancelled) { console.error('[GalleryWidget]', e); setError('Could not load album photos.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [albumConfig?.albumId, projectId])

  // Legacy fallback: if no album selected but we have inline images from
  // either widget.rich_content or `data`, display those. Keeps existing
  // dashboards working until the user re-picks an album.
  const legacyImages = useMemo(() => {
    if (albumConfig?.albumId) return []
    const rich = parseGalleryImages(widget.rich_content) || []
    if (rich.length > 0) return rich.map(normaliseImage)
    return (data?.images || []).map(normaliseImage)
  }, [albumConfig?.albumId, widget.rich_content, data?.images])

  const carouselItems = albumConfig?.albumId
    ? photos.map((p, i) => ({
        id: p.id,
        src: p.image || p.photo_url || p.url || p.file_url || p.thumbnail || '',
        caption: p.name || p.title || `Photo ${i + 1}`,
      })).filter((p) => p.src)
    : legacyImages

  const handleSave = (config) => {
    setAlbumConfig(config)
    setIndex(0)
    lsSetScoped('wq_gallery', projectId, widget.id, config)
  }

  const prev = () => setIndex((i) => (carouselItems.length ? (i - 1 + carouselItems.length) % carouselItems.length : 0))
  const next = () => setIndex((i) => (carouselItems.length ? (i + 1) % carouselItems.length : 0))

  const current = carouselItems[index]

  return (
    <>
      <XDCard
        title={widget.title || 'Gallery'}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToGallery?.(albumConfig?.albumName || undefined)}
              disabled={isCustomizing}
              className="common-size15 text-[#4D4D4D] flex items-center gap-2 text-sm bg-[#F1F2F4] py-1.5 px-4 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title={albumConfig?.albumName ? `Open album: ${albumConfig.albumName}` : 'Open Gallery'}
            >
              <ExternalLinkIcon /> View All
            </button>
            {isCustomizing && canEdit && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change gallery album"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        <div className="relative h-full p-4 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center flex-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {error}
            </div>
          ) : carouselItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-2">
              <span className="text-3xl">🖼️</span>
              <p className="text-sm">
                {albumConfig?.albumId === 'all' ? 'No photos yet.' : albumConfig?.albumId ? 'This album is empty.' : 'No album selected'}
              </p>
              <p className="text-xs text-gray-300">Click the edit (✎) icon to pick an album</p>
            </div>
          ) : (
            <>
              <div className="flex-1 flex items-center justify-center bg-white rounded overflow-hidden">
                <img
                  src={current.src}
                  alt={current.caption || `Image ${index + 1}`}
                  className="max-h-72 max-w-full object-contain"
                  onError={(e) => { e.target.style.opacity = 0.3 }}
                />
              </div>
              {current.caption && (
                <p className="text-xs text-center text-gray-600 mt-2 px-2 truncate">{current.caption}</p>
              )}

              {/* Prev / next arrows (bottom-right corner per XD) */}
              {carouselItems.length > 1 && (
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={prev}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded shadow-sm text-gray-700 hover:bg-gray-50"
                    title="Previous image"
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded shadow-sm text-gray-700 hover:bg-gray-50"
                    title="Next image"
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </XDCard>

      <GalleryEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialAlbumId={albumConfig?.albumId || null}
      />
    </>
  )
}

// ─── "Change Webcam" modal — same flow as the Gallery's album picker ─────────
export function ChangeWebcamModal({ open, onClose, onSave, projectId, initialWebcamId = null }) {
  const [webcams, setWebcams] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(initialWebcamId)

  useEffect(() => {
    if (!open) return
    setSelectedId(initialWebcamId)
    if (!projectId) { setWebcams([]); return }
    setLoading(true); setError(null)
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    fetch(`${API_URL}/api/data/camera/?site__project=${projectId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results || [])
        setWebcams(list)
      })
      .catch((e) => { console.error('[Webcam] list:', e); setError('Could not load webcams.') })
      .finally(() => setLoading(false))
  }, [open, projectId, initialWebcamId])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const camLabel = (cam) =>
    cam.name || (cam.ftp_folder ? `EWC ${cam.ftp_folder}` : `Camera ${cam.id}`)

  const handleSelect = () => {
    const cam = webcams.find((c) => String(c.id) === String(selectedId))
    if (!cam) return
    onSave({ webcamId: cam.id, webcamLabel: camLabel(cam), ftpFolder: cam.ftp_folder || '' })
    onClose()
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Change Webcam</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</p>}
          <select
            value={selectedId || ''}
            onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
            disabled={loading || webcams.length === 0}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">
              {loading ? 'Loading webcams…' : (webcams.length === 0 ? 'No webcams available' : 'Select Webcam')}
            </option>
            {webcams.map((c) => (
              <option key={c.id} value={c.id}>{camLabel(c)}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSelect}
            disabled={!selectedId || loading}
            className="w-full bg-[#358ECD] text-white font-semibold uppercase text-sm tracking-wide py-2.5 rounded-lg hover:bg-[#2d7ab8] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Select Webcam
          </button>
          <div className="text-center">
            <button type="button" onClick={onClose} className="cancelbtn text-sm text-[#358ECD] hover:underline">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * Webcam Widget — picks one camera from the project's sites and displays
 * its feed. Configuration uses the "Change Webcam" modal pattern matching
 * the Gallery widget's "Change Gallery" picker.
 *
 * Note: the Camera model carries site + ftp_folder but no streaming URL —
 * actual feed rendering is best-effort (uses any `data.url` provided by
 * the surrounding widget data, otherwise shows a "Feed unavailable"
 * placeholder labelled with the chosen camera's ftp_folder).
 */
export function WebcamWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, canEdit = true, goToGallery, selectedParameters = [] } = useDashboardContext()
  const lsConfig = lsGetScoped('wq_webcam', projectId, widget.id, null)
  const [config, setConfig] = useState(
    lsConfig && lsConfig.webcamId
      ? { webcamId: lsConfig.webcamId, webcamLabel: lsConfig.webcamLabel || '', ftpFolder: lsConfig.ftpFolder || '' }
      : null
  )
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    const v = lsGetScoped('wq_webcam', projectId, widget.id, null)
    if (v?.webcamId) {
      setConfig({
        webcamId:    v.webcamId,
        webcamLabel: v.webcamLabel || '',
        ftpFolder:   v.ftpFolder   || '',
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [showEditor, setShowEditor] = useState(false)

  // EWC is the camera "sensor": when the user checks any EWC parameter in the
  // sidebar, we can resolve that site's webcam automatically — no manual pick
  // required. Grab the site id of the first selected EWC parameter.
  const ewcSite = useMemo(() => {
    const p = selectedParameters.find((sp) => /ewc/i.test(sp?.sensorName || ''))
    return p?.siteId ?? null
  }, [selectedParameters])

  // Camera id auto-resolved from the selected EWC site (used only when the user
  // hasn't manually picked a webcam via the ✎ editor — a manual pick wins).
  const [autoWebcam, setAutoWebcam] = useState(null)
  useEffect(() => {
    if (config?.webcamId || !ewcSite) { setAutoWebcam(null); return }
    let cancelled = false
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    fetch(`${API_URL}/api/data/camera/?site=${ewcSite}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        const list = Array.isArray(d) ? d : (d?.results || [])
        const cam = list.find((c) => c.enabled) || list[0] || null
        if (!cancelled) setAutoWebcam(cam ? { webcamId: cam.id, webcamLabel: cam.name || cam.ftp_folder || 'Webcam' } : null)
      })
      .catch(() => { if (!cancelled) setAutoWebcam(null) })
    return () => { cancelled = true }
  }, [config?.webcamId, ewcSite])

  // Effective webcam: a manual pick always wins; otherwise fall back to the
  // EWC-resolved camera.
  const activeWebcam = config?.webcamId ? config : autoWebcam
  const activeWebcamId = activeWebcam?.webcamId || null

  // Latest camera snapshot for the active webcam. The camera uploads new
  // images to /camera/upload on its own schedule; we re-fetch every 60s so the
  // widget behaves like a live feed. { url, takenAt, name } or null.
  const [feed, setFeed] = useState(null)
  useEffect(() => {
    if (!activeWebcamId) { setFeed(null); return }
    let cancelled = false
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const load = () => {
      const token = localStorage.getItem(JWT_KEY)
      fetch(`${API_URL}/api/data/camera/${activeWebcamId}/latest/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => { if (!cancelled) setFeed(d?.url ? { url: d.url, type: d.type || 'image', takenAt: d.taken_at, name: d.name } : null) })
        .catch(() => { if (!cancelled) setFeed(null) })
    }
    load()
    const t = setInterval(load, 60000)
    return () => { cancelled = true; clearInterval(t) }
  }, [activeWebcamId])

  const handleSave = (next) => {
    setConfig(next)
    lsSetScoped('wq_webcam', projectId, widget.id, next)
  }

  return (
    <>
      <XDCard
        title={widget.title || 'Webcam'}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToGallery?.()}
              disabled={isCustomizing}
              className="text-[#4D4D4D] flex items-center gap-2  text-xs bg-gray-200 py-1.5 px-3 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Open Gallery"
            >
              <ExternalLinkIcon /> View All
            </button>
            {isCustomizing && canEdit && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change webcam"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        <div className="p-4">
          <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center relative" style={{ minHeight: 200 }}>
            {(feed?.url || data?.url) ? (
              <>
                {feed?.type === 'video' ? (
                  <video
                    src={feed.url}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                    muted
                    preload="metadata"
                  />
                ) : (
                  <img src={feed?.url || data?.url} alt={activeWebcam?.webcamLabel || 'Webcam feed'} className="w-full h-full object-cover" />
                )}
                {feed?.takenAt && (
                  <span className="absolute bottom-1 right-2 text-[10px] text-white/90 bg-black/50 px-1.5 py-0.5 rounded">
                    {new Date(feed.takenAt).toLocaleString()}
                  </span>
                )}
              </>
            ) : activeWebcamId ? (
              <div className="text-center px-4 py-10">
                <p className="text-gray-300 text-sm">📹 {`${activeWebcam?.webcamLabel || 'Webcam'} — no media yet`}</p>
                <p className="text-gray-500 text-xs mt-2">Waiting for the camera to upload.</p>
              </div>
            ) : (
              <div className="text-center px-4 py-10">
                <p className="text-gray-300 text-sm">📹 Webcam feed unavailable</p>
                <p className="text-gray-500 text-xs mt-2">Select an EWC parameter, or click ✎ to pick a webcam.</p>
              </div>
            )}
          </div>
        </div>
      </XDCard>

      <ChangeWebcamModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialWebcamId={config?.webcamId || null}
      />
    </>
  )
}

// ─── Field Notes — helpers + popups ────────────────────────────────────────
// Note shape: { id, author, timestamp, body, attachmentDataUrl?, attachmentName? }
// Persisted per-project (and per-widget instance) via lsGetScoped/lsSetScoped.
// Attachments are stored as base64 data URLs in localStorage — fine for the
// handful of small site photos this widget targets; a real backend store
// will replace this when the persistence layer is built.

function formatFieldNoteTs(ts) {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  // "8:00am EST" style — matches the XD which uses lowercase am/pm + zone.
  const tParts = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  // toLocaleTimeString gives "8:00 AM"; collapse the space and lowercase the meridiem.
  const time = tParts.replace(/\s/g, '').replace('AM', 'am').replace('PM', 'pm')
  // Pull the timezone abbreviation off a separate formatToParts call (not all
  // browsers give it back from toLocaleTimeString reliably).
  let tz = ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d)
    tz = parts.find((p) => p.type === 'timeZoneName')?.value || ''
  } catch { /* tz lookup is best-effort */ }
  return `${date} ${time}${tz ? ' ' + tz : ''}`
}

// Tiny paperclip icon — Tailwind sized via parent className.
const PaperclipIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
)

/**
 * "New Field Note" popup — title, Save/Cancel header buttons, large
 * textarea with the current timestamp prefilled at the top, and a
 * "Drag Attachments or Upload Image" drop zone at the bottom.
 *
 * Returns the new note via onSave; parent persists.
 */
function FieldNotesNewModal({ open, onSave, onCancel, defaultAuthor }) {
  const [body, setBody] = useState('')
  const [attachment, setAttachment] = useState(null) // { dataUrl, name } | null
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)
  const ts = useMemo(() => new Date().toISOString(), [open])

  useEffect(() => {
    if (open) { setBody(''); setAttachment(null); setDragOver(false) }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const ingestFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) return
    // 5 MB ceiling so a single attachment can't blow out the localStorage
    // quota — anything larger gets rejected with a toast-style inline note.
    if (file.size > 5 * 1024 * 1024) {
      alert('Attachment too large (max 5 MB).')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setAttachment({ dataUrl: String(reader.result), name: file.name })
    reader.readAsDataURL(file)
  }

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) ingestFile(f)
  }

  const submit = () => {
    const trimmed = body.trim()
    if (!trimmed) return
    onSave({
      id: 'n' + Date.now(),
      author: defaultAuthor || 'You',
      timestamp: ts,
      body: trimmed,
      attachmentDataUrl: attachment?.dataUrl || null,
      attachmentName: attachment?.name || null,
    })
  }

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8 mt-8"
      onClick={onCancel}
    >
      <div className="main-popup-design bg-white w-full max-w-2xl rounded shadow-lg mx-4 mt-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">New Field Note</h2>
          
        </div>
        {/* Body */}
        <div className="px-5 py-4">
          <div className="border border-gray-200 rounded">
            <div className="px-3 pt-2 pb-1 text-[15px] text-gray-400 border-b border-gray-100">
              {formatFieldNoteTs(ts)}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter note by typing…"
              rows={10}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
            />
          </div>

          {/* Attachment drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mt-3 border-2 border-dashed rounded p-6 text-center text-sm transition-colors ${
              dragOver ? 'border-[#358ECD] bg-[#358ECD]/5' : 'border-gray-200 bg-gray-50'
            }`}
          >
            {attachment ? (
              <div className="flex items-center justify-center gap-3">
                <img src={attachment.dataUrl} alt={attachment.name} className="h-16 w-16 object-cover rounded" />
                <span className="text-xs text-gray-600">{attachment.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <span className="text-gray-400">
                Drag Attachments or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-[#358ECD] hover:underline"
                >
                  Upload Image
                </button>
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) ingestFile(f); e.target.value = '' }}
            />
          </div>
          <div className="px-6 py-4 bg-white">
            <button
              type="button"
              onClick={submit}
              disabled={!body.trim()}
              className="savebtn px-3 w-full py-2 text-sm bg-[#358ECD] text-white rounded hover:bg-[#2c7ab3] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Note
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="cancelbtn px-3 w-full py-1 text-sm border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * "All Field Notes" popup — search bar + New Note button in the header,
 * scrollable list of all notes (with attached images inline on the right
 * when present). Matches the XD viewAll layout.
 */
function FieldNotesViewAllModal({ open, notes, onClose, onNewNote, onDeleteNote }) {
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return notes
    return notes.filter((n) =>
      (n.body || '').toLowerCase().includes(q) ||
      (n.author || '').toLowerCase().includes(q)
    )
  }, [notes, query])

  if (!open) return null

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8 mt-8"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-6xl rounded shadow-lg mx-4 mt-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="break-mb flex items-center justify-between px-6 py-4 border-b">
          <h2 className="common-head25 text-base font-bold text-[#4D4D4D]">All Field Notes</h2>
          <div className="flex items-center gap-3 new-note">
            <div className="relative">
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Notes"
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded w-64 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:bg-white"
              />
            </div>
            <button
              type="button"
              onClick={onNewNote}
              className="common-size15 px-3 py-1.5 text-sm bg-[#358ECD] text-white rounded hover:bg-[#2c7ab3]"
            >
              New Note
            </button>
            <button
              type="button"
              onClick={onClose}
              className="cross-bt p-1 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12">
              {query ? `No notes match "${query}".` : 'No field notes yet.'}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((n) => (
                <div key={n.id} className="all-f-n bg-gray-50 rounded p-4 flex gap-4 group">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#4D4D4D]">{n.author}</div>
                    <div className="text-[11px] text-gray-500 mb-1.5">{formatFieldNoteTs(n.timestamp)}</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{n.body}</p>
                  </div>
                  {n.attachmentDataUrl && (
                    <img
                      src={n.attachmentDataUrl}
                      alt={n.attachmentName || 'attachment'}
                      className="w-32 h-24 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onDeleteNote(n.id)}
                    className="self-start text-[11px] text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete note"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  )
}

/**
 * Field Notes Widget — matches the XD spec:
 *
 *   • Header: "Field Notes" title, "New Note" + "View All" buttons on the right.
 *   • Body: compact list of recent notes — author (bold) and timestamp on
 *     the same line, body underneath, a paperclip icon for notes with
 *     attachments. Up to MAX_PREVIEW visible inline.
 *   • "New Note" opens the FieldNotesNewModal (textarea + image drop zone).
 *   • "View All" opens the FieldNotesViewAllModal (search + scrollable list
 *     with attachment thumbnails rendered inline).
 *
 * Notes persist to localStorage scoped per (project, widget instance) so
 * each Field Notes widget keeps its own list and projects don't bleed
 * into one another (same convention as the other configurable widgets in
 * this file). Backend persistence is a follow-up task.
 */
// ─── Field Notes pre-add modal ──────────────────────────────────────────────
// Field Notes has no device/parameter to configure — notes are added after
// placement via "New Note" — but like every other configurable widget it
// still supports a custom widget name and description, so the "configure
// before placing" flow just captures those two fields upfront. Settings are
// stored under their own scoped-config prefix (`wq_fieldnotes_settings`),
// separate from `wq_fieldnotes` which holds the notes array itself.
export function FieldNotesPreAddModal({ open, onClose, onSave, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (open) {
      setWidgetName(initialSettings.widgetName || '')
      setDescription(initialSettings.description || '')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const save = () => {
    onSave({ widgetName: widgetName.trim(), description: description.trim() })
    onClose()
  }

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="main-popup-design bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Field Notes Editor</h2>
          
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Widget Name <span className="ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input
              type="text" placeholder="Field Notes"
              value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Description <span className="ml-1 font-normal text-gray-400">(optional subtitle)</span>
            </label>
            <input
              type="text" placeholder="Short description shown below the title"
              value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>
        </div>
        <div className="py-4 bg-white">
            <button onClick={save} className="savebtn px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition ">Save</button>
            <button onClick={onClose} className="cancelbtn px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          </div>
      </div>
    </div>
    </ModalPortal>
  )
}

export function FieldNotesWidget({ widget }) {
  const { projectId } = useParams()
  const {
    isCustomizing = false, canEdit = true,
    user,                               // not in context today, but referenced safely
  } = useDashboardContext()
  const lsKey = ['wq_fieldnotes', projectId, widget.id]

  const MAX_PREVIEW = 3

  const [notes, setNotes] = useState(() => {
    const v = lsGetScoped(lsKey[0], lsKey[1], lsKey[2], null)
    return Array.isArray(v) ? v : []
  })
  // Widget name / description, configured before placement (see
  // FieldNotesPreAddModal above) — separate scoped-config slot from the
  // notes array itself.
  const [nameSettings, setNameSettings] = useState(
    () => lsGetScoped('wq_fieldnotes_settings', projectId, widget.id, {})
  )
  const primeTick = useWidgetConfigPrime(projectId)
  useEffect(() => {
    setNameSettings(lsGetScoped('wq_fieldnotes_settings', projectId, widget.id, {}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeTick, projectId, widget.id])
  const [showNewModal, setShowNewModal] = useState(false)
  const [showViewAllModal, setShowViewAllModal] = useState(false)

  const persist = (next) => {
    setNotes(next)
    lsSetScoped(lsKey[0], lsKey[1], lsKey[2], next)
  }

  const addNote = (note) => {
    persist([note, ...notes])
    setShowNewModal(false)
  }
  const deleteNote = (id) => persist(notes.filter((n) => n.id !== id))

  const defaultAuthor = user?.name || user?.email || 'You'
  const previewNotes = notes.slice(0, MAX_PREVIEW)

  return (
    <XDCard
      title={nameSettings.widgetName || widget.title || 'Field Notes'}
      subtitle={nameSettings.description || widget.description}
      headerRight={
        <div className="flex items-center gap-2">
          {isCustomizing && canEdit && (
            <button
              type="button"
              onClick={() => setShowNewModal(true)}
              className="text-white  text-xs bg-[#358ECD] hover:bg-[#2d7ab8] py-1.5 px-3 rounded-lg transition"
            >
              New Note
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowViewAllModal(true)}
            disabled={isCustomizing}
            className="text-[#4D4D4D] flex items-center gap-2  text-xs bg-gray-200 hover:bg-gray-300 py-1.5 px-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Open all field notes"
          >
            <ExternalLinkIcon /> View All
          </button>
        </div>
      }
    >
      <div className="px-5 py-4">
        {previewNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
            <p className="text-gray-500 text-sm">No notes yet — click "New Note" to add one.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {previewNotes.map((n) => (
              <div key={n.id} className="border-b border-gray-100 pb-3 last:border-b-0">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-semibold text-[#4D4D4D]">{n.author}</span>
                  <span className="text-[11px] text-gray-500">{formatFieldNoteTs(n.timestamp)}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed flex items-start gap-1.5">
                  {n.attachmentDataUrl && (
                    <span className="text-gray-400 mt-0.5 flex-shrink-0" title={n.attachmentName || 'attachment'}>
                      <PaperclipIcon />
                    </span>
                  )}
                  <span className="line-clamp-2">{n.body}</span>
                </p>
              </div>
            ))}
            {notes.length > MAX_PREVIEW && (
              <button
                type="button"
                onClick={() => setShowViewAllModal(true)}
                className="block w-full text-[11px] text-[#358ECD] hover:underline text-center pt-1"
              >
                + {notes.length - MAX_PREVIEW} more — view all
              </button>
            )}
          </div>
        )}
      </div>

      <FieldNotesNewModal
        open={showNewModal}
        onSave={addNote}
        onCancel={() => setShowNewModal(false)}
        defaultAuthor={defaultAuthor}
      />
      <FieldNotesViewAllModal
        open={showViewAllModal}
        notes={notes}
        onClose={() => setShowViewAllModal(false)}
        onNewNote={() => { setShowViewAllModal(false); setShowNewModal(true) }}
        onDeleteNote={deleteNote}
      />
    </XDCard>
  )
}

/**
 * Custom HTML Widget
 */
export function CustomHTMLWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false, canEdit = true } = useDashboardContext()
  const html = data?.html || widget.custom_rich_content || widget.rich_content || widget.data_config?.html || ''
  return (
    <XDCard
      title={widget.custom_title || widget.title || 'Custom HTML'}
      subtitle={widget.custom_description || widget.description}
      headerRight={
        isCustomizing && canEdit && onConfigure ? (
          <button
            onClick={() => onConfigure(widget)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit HTML content"
          >
            <EditIcon />
          </button>
        ) : null
      }
    >
      <div className="p-6 overflow-auto">
        {html ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-sm text-gray-400 italic text-center py-8">
            No content · click the ✎ icon to add HTML
          </p>
        )}
      </div>
    </XDCard>
  )
}

/**
 * Rich Content Widget — renders WYSIWYG HTML created in Django admin or frontend editor.
 * Content is stored in widget.rich_content (from MasterWidget.rich_content).
 * SECURITY: content originates from authenticated admin/staff users — treat as trusted.
 */
export function RichContentWidget({ widget }) {
  const html = widget.rich_content || widget.data_config?.rich_content || ''
  return (
    <div className="bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div>
          <h3 className="common-size18 font-bold text-[#4D4D4D] text-base">{widget.title}</h3>
          {widget.description && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{widget.description}</p>}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-sm text-gray-400 italic">No content yet. Click "Edit Content" in customize mode to add rich content.</p>
        )}
      </div>
    </div>
  )
}

/**
 * Help/Information Widget
 */
export function HelpWidget({ widget, data }) {
  return (
    <Card className="p-6 bg-blue-50 border border-blue-200">
      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
        ❓ {widget.title}
      </h3>
      <div className="text-sm text-blue-800">
        <p className="mb-3">{data?.helpText || 'Help information not available'}</p>
        {data?.links && (
          <div className="space-y-2">
            {data.links.map((link, idx) => (
              <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                • {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Custom Widget - extensible for user-created widgets
 */
export function CustomWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[#4D4D4D] mb-4">{widget.title}</h3>
      <div className="bg-gray-50 rounded-lg p-4 text-gray-600 text-center">
        <p>Custom widget configuration</p>
        {data && <pre className="text-xs mt-2 overflow-auto">{JSON.stringify(data, null, 2)}</pre>}
      </div>
    </Card>
  )
}

/**
 * Widget renderer - dynamically loads the appropriate widget component
 */
export function WidgetRenderer({ widget, data, isEditing = false, onEdit, onDelete, noWrap = false, onConfigure }) {
  const widgetComponents = {
    // Basic
    summary_card: SummaryCard,
    live_data: LiveDataWidget,
    site_conditions: SiteConditionsWidget,
    data_table: DataTableWidget,
    
    // Charts and Graphs
    line_chart: LineChartWidget,
    // Legacy alias — some seeded templates (Parameter Comparison,
    // System Performance) use `chart_line`/`chart_bar` instead of
    // the canonical `line_chart`/`bar_chart`. Both should render.
    chart_line: LineChartWidget,
    chart_bar:  BarChartWidget,
    smooth_line_chart: SmoothCurveLineChartWidget,
    combo_chart: ComboChartWidget,
    multi_chart: MultiChartWidget,
    area_chart: AreaChartWidget,
    column_chart: ColumnChartWidget,
    stacked_column_chart: StackedColumnChartWidget,
    bar_chart: BarChartWidget,
    pie_chart: PieChartWidget,
    doughnut_chart: DoughnutChartWidget,
    pie_chart_3d: Pie3DChartWidget,
    
    // Gauges and Dials
    wind_rose: WindRoseWidget,
    gauge_series: GaugeSeriesWidget,
    bullet_graph: BulletGraphWidget,
    solid_gauge: SolidGaugeWidget,
    data_box: DataBoxWidget,

    // Weather Panels
    air_temperature: AirTemperatureWidget,
    barometric_pressure: BarometricPressureWidget,
    daily_rain: DailyRainWidget,
    heading: HeadingWidget,
    humidity: HumidityWidget,
    interval_rain: IntervalRainWidget,
    lightning: LightningWidget,
    precipitation: PrecipitationWidget,
    pressure: PressureWidget,
    rain_intensity: RainIntensityWidget,
    solar_radiation: SolarRadiationWidget,
    total_rain: TotalRainWidget,
    wind: WindWidget,
    
    // Water Panels
    ammonium: AmmoniumWidget,
    bga: BGAWidget,
    chlorophyll: ChlorophyllWidget,
    conductivity: ConductivityWidget,
    dissolved_oxygen: DissolvedOxygenWidget,
    hydrocarbons: HydrocarbonsWidget,
    level: LevelWidget,
    oil_detection: OilDetectionWidget,
    orp: ORPWidget,
    ph: PHWidget,
    pressure_water: WaterPressureWidget,
    salinity: SalinityWidget,
    snow: SnowWidget,
    sp_conductance: SpConductanceWidget,
    temperature: WaterTemperatureWidget,
    turbidity: TurbidityWidget,
    water_current: WaterCurrentWidget,
    wave: WaveWidget,
    
    // Soil Panels
    soil_conductivity: SoilConductivityWidget,
    soil_moisture: SoilMoistureWidget,
    soil_salinity: SoilSalinityWidget,
    soil_temperature: SoilTemperatureWidget,
    
    // Other
    gps: GPSWidget,
    
    // Alarms
    alarm_summary: AlarmSummaryWidget,
    individual_alarm: IndividualAlarmWidget,
    
    // Miscellaneous
    project_description: ProjectDescriptionWidget,
    project_photo: ProjectPhotoWidget,
    gallery: GalleryWidget,
    webcam: WebcamWidget,
    field_notes: FieldNotesWidget,
    custom_html: CustomHTMLWidget,
    rich_content: RichContentWidget,
    help: HelpWidget,
    
    // Video and Map
    video_feed: VideoFeedWidget,
    map_view: MapViewWidget,
    performance_metric: PerformanceMetricWidget,
    custom: CustomWidget,

    // New XD-design widgets
    environmental_news: EnvironmentalNewsWidget,
    event_monitor: EventMonitorWidget,
    support_project: SupportProjectWidget,
    donation: SupportProjectWidget,
    disclaimer: DisclaimerWidget,
    message_admin: MessageAdminWidget,
    knowledge_base: KnowledgeBaseWidget,
  }

  const Component = widgetComponents[widget.widget_type] || CustomWidget

  if (noWrap) {
    return <Component widget={widget} data={data} onConfigure={onConfigure} />
  }

  return (
    <div
      style={{
        gridRow: `span ${widget.size_rows || 1}`,
        gridColumn: `span ${widget.size_cols || 1}`,
      }}
      className="relative group h-full"
    >
      {isEditing && (
        <div className="absolute top-2 right-2 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button
              onClick={() => onEdit(widget)}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              ✎
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(widget.id)}
              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              ✕
            </button>
          )}
        </div>
      )}
      <Component widget={widget} data={data} />
    </div>
  )
}
